// import { logger, config, security, globals, utils, remoteDL, localDL } from '@/services';
import config from './config';
import globals from './globals';
import CustomMessageBox from './CustomMessageBox';
import utils from './utils';
import logger from './logger';
import security from './security';
import sessionRecovery from './sessionRecovery';
import remoteDL from './remoteDatalayer';
import localDL from './localDatalayer';
import { equipmentDetailAvailable, savedEquipmentBaseline, settleEquipmentDetailFailure } from './equipmentSnapshot';
import { selectionResponseError, savedSelectionOfferAvailable } from './selectionResponse';
import { syncJobBlockReason, syncJobDescription } from './equipmentQueue';
import { isAgentSandbox, isLocalServiceUnavailable } from './appRuntimeMode';
import { createLibraryTransition } from './libraryTransition';
import { normalizeDxfResponse } from './dxfResponse';
import { shallowRef, computed, watch } from 'vue';
//import $ from '../assets/scripts/jquery-3.6.0.min';
import moment from '../assets/scripts/moment.min';
import bus from '@/main';
import { NullLogger } from '@microsoft/signalr';

const $ = window.$;
const pausedSyncJobs = shallowRef([]);
const retainedDrafts = shallowRef([]);
const equipmentRevisionStarts = new Map();
let releasePausedSyncJobs = null;
// Existing local qualification seam: observe actual entries and optionally
// seed only version metadata AFTER ordinary adoption has selected its key.
const ltvQualification = isAgentSandbox() ? { contextEntries: 0, bootstrapEntries: 0 } : null;
let ltvCachedVersions = null;
let libraryTransitionActive = false, libraryRecoveryRequired = false, librarySnapshot = null;
let libraryInitialization = null;
let dataRefreshRequest = null;
let dataRefreshPreparing = false;
// Construct lazily: the user and IndexedDB identity are not ready at import time.
let libraryTransition = null;
function getLibraryTransition() {
    if (!libraryTransition) libraryTransition = createLibraryTransition({
        deferred: () => $.Deferred(), resolved: () => $.Deferred().resolve().promise(),
        current: () => globals.user.getCdg(),
        fence: () => {
            librarySnapshot = { user: globals.user.captureLibraryState(), selection: selectionListResult };
            localDL.clearBatchJobQueue();
            return security.fenceLibraryWork();
        },
        isCurrent: owner => security.incarnationMatches(owner),
        request: (target, previous) => sessionRecovery.run(operation => operation.write(() => security.requestLibrary(target, previous)), { interactive: false }),
        abandon: () => { libraryTransitionActive = false; libraryRecoveryRequired = true; librarySnapshot = null; },
        context: () => sessionRecovery.run(operation => operation.read(() => security.initialiseContext()), { interactive: false }),
        load: target => sessionRecovery.run(operation => operation.read(() => loadLibraryContext(target)), { interactive: false }),
        busy: (active, usable) => {
            libraryTransitionActive = active;
            libraryRecoveryRequired = !active && !usable;
            globals.dataModelReady.value = !active && usable;
            if (!active && !usable && librarySnapshot) {
                globals.user.restoreLibraryState(librarySnapshot.user);
                selectionListResult = librarySnapshot.selection;
                setLocalDLIdentity();
            }
            if (!active) librarySnapshot = null;
        }
    });
    return libraryTransition;
}
function switchLibrary(target) {
    if (dataRefreshRequest) return $.Deferred().reject({ retryable: true, refreshBusy: true }).promise();
    if (libraryRecoveryRequired) return $.Deferred().reject(libraryWriteError()).promise();
    return getLibraryTransition().change(target);
}
function libraryWriteError(localSettingsSave) {
    const finishingLocalSave = localSettingsSave && dataRefreshPreparing && userSettingsLocalSaves.has(localSettingsSave) && settingsContextMatches(localSettingsSave.context);
    if (dataRefreshRequest && !finishingLocalSave) return { recoverable: true, retryable: true, refreshBusy: true, errorMessage: config.getTranslationText('refresh.failed') };
    if (!libraryTransitionActive && !libraryRecoveryRequired) return null;
    return { recoverable: true, retryable: true, libraryRecoveryRequired: true, libraryUnconfirmed: true };
}
function queueLibraryBatch(operation, rows, isCurrentContext) {
    return localDL.queueBatchJob(operation, rows, sessionCacheOwner(security.getIncarnation(), isCurrentContext));
}
function resetLibraryMemory() {
    selectionListResult = sharedOfferStubsResult = bodiesResult = accessoriesResult = trailersResult = payloadsResult = null;
    for (const flag of [doingBackgroundOfferRetrieval, doingBackgroundOverviewRetrieval, doingBackgroundSelectionRetrieval,
        doingBackgroundSharedOfferStubsRetrieval, doingBackgroundBodiesRetrieval, doingBackgroundAccessoriesRetrieval,
        doingBackgroundTrailersRetrieval, doingBackgroundPayloadsRetrieval, doingBackgroundOfferCaching,
        doingBackgroundSelectionCaching, doingBackgroundSharedOfferStubsCaching, doingBackgroundBodyStubsCaching,
        doingBackgroundAccessoriesStubsCaching, doingBackgroundTrailerStubsCaching, doingBackgroundPayloadStubsCaching]) flag.value = false;
    networkRequestsUnderway = 0;
    networkRequestGeneration++;
}
function loadLibraryContext(expected, refreshContext) {
    const dfd = $.Deferred();
    const owner = security.getIncarnation();
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    dfd.always(release);
    const context = refreshContext ? $.Deferred().resolve(refreshContext.loginData).promise() : security.initialiseContext();
    context.then(loginData => {
        if (!security.incarnationMatches(owner)) return dfd.reject({ cancelled: true });
        if (!loginData?.contextEstablished || loginData.result.ReturnCode <= 0 || globals.user.getCdg() !== expected)
            return dfd.reject({ libraryUnconfirmed: true });
        if (refreshContext && !refreshContext.current()) return dfd.reject({ cancelled: true });
        setLocalDLIdentity();
        resetLibraryMemory();
        globals.userSettingsDfd?.reject?.({ cancelled: true });
        globals.selectionListDfd?.reject?.({ cancelled: true });
        globals.userSettingsDfd = null;
        globals.selectionListDfd = null;
        loginData.librarySwitch = true;
        const ready = $.Deferred();
        doDataActionsAfterLoginOrReAuth(loginData, false, ready, undefined, refreshContext?.current);
        ready.then(() => {
            if (!security.incarnationMatches(owner) || (refreshContext && !refreshContext.current()))
                return $.Deferred().reject({ cancelled: true }).promise();
            return handleDataRequest(null,
                'offers/newoffers?application=&vehicleType=&vehicleTypeCode=&axleLayout=&make=&range=&pageNumber=1&pageSize=30', OPERATION.GetSelectionList, true);
        })
            .then(data => {
                if (!security.incarnationMatches(owner) || (refreshContext && !refreshContext.current())) return dfd.reject({ cancelled: true });
                selectionListCallback(data); dfd.resolve(loginData);
            }).fail(error => dfd.reject(error));
    }).fail(error => dfd.reject(error));
    return dfd.promise();
}

function refreshApplicationData() {
    if (dataRefreshRequest) return dataRefreshRequest.promise();
    if (libraryTransitionActive || libraryRecoveryRequired || getLibraryTransition().pending())
        return $.Deferred().reject({ retryable: true, libraryUnconfirmed: true }).promise();
    const dfd = $.Deferred(), waitingOwner = security.getIncarnation();
    const promise = dfd.promise();
    let operationOwner = waitingOwner;
    promise.getRefreshOwner = () => operationOwner;
    const expected = { model: globals.user, user: globals.user.getUserName(), instance: globals.user.getUserLicenceInstanceId(),
        cdg: globals.user.getCdg(), storage: globals.getStorageKey(), revision: security.getUserContextRevision() };
    const sameIdentity = () => expected.model === globals.user && expected.user === globals.user.getUserName() && expected.instance === globals.user.getUserLicenceInstanceId() &&
        expected.cdg === globals.user.getCdg() && expected.storage === globals.getStorageKey();
    dataRefreshRequest = dfd;
    dataRefreshPreparing = true;
    const waitingRelease = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    const waitingTimer = setTimeout(() => dfd.reject({ timeout: true, retryable: true }), 20000);
    dfd.always(() => { clearTimeout(waitingTimer); waitingRelease(); dataRefreshPreparing = false; if (dataRefreshRequest === dfd) dataRefreshRequest = null; });
    function waitForLocalSaves() {
        if (dfd.state() !== 'pending') return;
        if (!security.incarnationMatches(waitingOwner) || !sameIdentity() || expected.revision !== security.getUserContextRevision())
            return dfd.reject({ cancelled: true });
        const pending = Array.from(userSettingsLocalSaves).filter(save => save.context.owner === waitingOwner &&
            save.context.user === expected.model && save.context.username === expected.user && save.context.instance === expected.instance &&
            save.context.cdg === expected.cdg && save.context.storage === expected.storage && save.dfd.state() === 'pending');
        if (pending.length) return $.when(...pending.map(save => save.dfd)).then(waitForLocalSaves).fail(error => dfd.reject(error));
        clearTimeout(waitingTimer); waitingRelease(); dataRefreshPreparing = false;
        beginRefresh();
    }
    function beginRefresh() {
        const snapshot = { user: globals.user.captureLibraryState(), selection: selectionListResult };
        // Keep the current session, provider proof, storage key and edited model.
        // Fence earlier requests/transactions before refreshing any cache entries.
        const owner = security.fenceLibraryWork();
        operationOwner = owner;
        expected.revision = undefined;
        let allowed = true, expiring = false;
        const current = () => security.incarnationMatches(owner) && sameIdentity() &&
            (expected.revision === undefined || expected.revision === security.getUserContextRevision());
        const bootstrapCurrent = () => allowed && current();
        const guard = { canProceed: () => dfd.state() === 'pending' && current() };
        const release = security.registerCancellation(() => { guard.abort?.(); dfd.reject({ cancelled: true }); });
        // Existing request deadlines remain unchanged. Bound the whole read/cache
        // operation as well, so an unfinished bootstrap cannot own the UI forever.
        const timer = setTimeout(() => {
            if (!security.incarnationMatches(owner) || dfd.state() !== 'pending') return;
            expiring = true;
            guard.abort?.();
            const error = current() ? { timeout: true, retryable: true } : { authenticationFailed: true, contextChanged: true };
            if (current()) { globals.user.restoreLibraryState(snapshot.user); selectionListResult = snapshot.selection; }
            allowed = false;
            release();
            error.refreshOwner = operationOwner = security.fenceLibraryWork();
            dfd.reject(error);
        }, 60000);
        dfd.fail(() => {
            allowed = false;
            guard.abort?.();
            if (current()) { globals.user.restoreLibraryState(snapshot.user); selectionListResult = snapshot.selection; }
        });
        dfd.always(() => { clearTimeout(timer); release(); if (dataRefreshRequest === dfd) dataRefreshRequest = null; });
        localDL.clearBatchJobQueue();
        security.initialiseContext(true).then(loginData => {
            if (!current()) return dfd.reject(security.incarnationMatches(owner) ? { authenticationFailed: true, contextChanged: true } : { cancelled: true });
            if (!loginData?.contextEstablished || !(loginData.result?.ReturnCode > 0))
                return dfd.reject({ businessFailure: true });
            expected.revision = security.getUserContextRevision();
            setLocalDLIdentity();
            localDL.clearCurrentLibraryCache(guard).then(() => {
                if (!guard.canProceed()) return dfd.reject({ cancelled: true });
                loadLibraryContext(expected.cdg, { loginData, current: bootstrapCurrent }).then(data => dfd.resolve(data)).fail(error => { if (!expiring) dfd.reject(error); });
            }).fail(error => { if (!expiring) dfd.reject(error); });
        }).fail(error => { if (!expiring) dfd.reject(error); });
    }
    waitForLocalSaves();
    return promise;
}
const dataLayer = window.dataLayer;
var TScMessenger = window.TScMessenger;
var OPERATION = config.OPERATION;
//constants
var
    operationTypeGet = 'GET',
    operationTypePost = 'POST',
    // operationTypeDelete = 'DELETE',
    dataTypeJson = 'json';
    // dataTypePdf = 'pdf';

//variables
var
    // userNameProvided,
    // passwordProvided,
    securityTokenProvided,
    cultureCodeProvided;




var STORE = localDL.STORE;
var PROPERTY = localDL.PROPERTY;
var isOnlineLastCheckTimeStamp = 0;
var onlineCheckDfd;
var onlineCheckUnderway = false;
var immediateDebugLogPostRequired = false;
var lastDebugLogUploadTimeStamp = 0;
var lastUsageLogUploadTimeStamp = 0;

var recentlyCached = shallowRef({Type: '', Id: 0, Value: 0});

//var tempCounter = 0;

var PRIORITY = {
    LOW: (config.isOnlineLowPriorityValidityPeriod*1000),
    MEDIUM: (config.isOnlineMediumPriorityValidityPeriod*1000),
    HIGH: (config.isOnlineHighPriorityValidityPeriod*1000)
};

function updatePriorityFromConfigValues() {
    PRIORITY = {
        LOW: (config.isOnlineLowPriorityValidityPeriod * 1000),
        MEDIUM: (config.isOnlineMediumPriorityValidityPeriod * 1000),
        HIGH: (config.isOnlineHighPriorityValidityPeriod * 1000)
    };
    dataManager.PRIORITY = PRIORITY;
}

function getOnlineStatePriority() {
    return globals.isOnline.value ? PRIORITY.HIGH : PRIORITY.LOW;
}

var onlineStatePriorityComputed = computed(function () {
    return globals.isOnline.value ? PRIORITY.HIGH : PRIORITY.LOW;
});

var doingBackgroundOfferRetrieval = shallowRef(false);
var doingBackgroundOverviewRetrieval = shallowRef(false);
var doingBackgroundSelectionRetrieval = shallowRef(false);
var doingBackgroundSharedOfferStubsRetrieval = shallowRef(false);
var doingBackgroundBodiesRetrieval = shallowRef(false);
var doingBackgroundAccessoriesRetrieval = shallowRef(false);
var doingBackgroundTrailersRetrieval = shallowRef(false);
var doingBackgroundPayloadsRetrieval = shallowRef(false);

var backgroundOfferRetrievalFailed = shallowRef(false);
var standardHandleVersionsTasksComplete = shallowRef(false);
var slowHandleVersionsTasksComplete = shallowRef(false);

var preventCaching = false;

var backgroudOfferRetrievalRetryComputedSubscriptionRef = null;
var backgroudOfferRetrievalRetryComputed = computed(function () {
    return backgroundOfferRetrievalFailed.value === true && standardHandleVersionsTasksComplete.value === true && slowHandleVersionsTasksComplete.value === true;
});



var doingBackgroundOfferCaching = shallowRef(false);

var doingBackgroundSelectionCaching = shallowRef(false);
watch(doingBackgroundSelectionCaching, function (newValue) {
    if (newValue === false) {
        selectionListResult = null;
    }
});

var doingBackgroundSharedOfferStubsCaching = shallowRef(false);
watch(doingBackgroundSharedOfferStubsCaching, function (newValue) {
    if (newValue === false) {
        sharedOfferStubsResult = null;
    }
});

var doingBackgroundBodyStubsCaching = shallowRef(false);
watch(doingBackgroundBodyStubsCaching, function (newValue) {
    if (newValue === false) {
        bodiesResult = null;
    }
});
var doingBackgroundAccessoriesStubsCaching = shallowRef(false);
watch(doingBackgroundAccessoriesStubsCaching, function (newValue) {
    if (newValue === false) {
        accessoriesResult = null;
    }
});

var doingBackgroundTrailerStubsCaching = shallowRef(false);
watch(doingBackgroundTrailerStubsCaching, function (newValue) {
    if (newValue === false) {
        trailersResult = null;
    }
});

var doingBackgroundPayloadStubsCaching = shallowRef(false);
watch(doingBackgroundPayloadStubsCaching, function (newValue) {
    if (newValue === false) {
        payloadsResult = null;
    }
});

var allOriginalAccessoriesRetrievedObv = shallowRef(false);
//var allOriginalEquipmentCached = ko.computed(function () {
//    var numNotCached = 0;
//    ko.utils.arrayForEach(originalEquipmentRetrievedObvArr(), function (originalEquipmentObv) {
//        if (originalEquipmentObv() === false) {
//            numNotCached++;
//        }
//    });
//    if (numNotCached === 0) {
//        return true;
//    } else {
//        return false;
//    }
//});
//var allOriginalEquipmentCachedSubscriptionRef;

var allNewOfferImagesRetrieved = shallowRef(false);
var allNewSpecImagesRetrieved = shallowRef(false);
var newOfferDataCached = shallowRef(false);
var newSpecDataCached = shallowRef(false);
var allNewOfferAndSpecImagesAndDataCached = computed(function () {
    
    return (allNewOfferImagesRetrieved.value === true && allNewSpecImagesRetrieved.value === true && newOfferDataCached.value === true && newSpecDataCached.value === true);
});
var newOfferDataAndImagesCachedSubscriptionRef;
var selectedVehicleId = 0;

var allExistingOfferImagesRetrieved = shallowRef(false);
var allExistingSpecImagesRetrieved = shallowRef(false);
var existingOfferDataCached = shallowRef(false);
var existingSpecDataCached = shallowRef(false);
var allExistingOfferAndSpecImagesAndDataCached = computed(function () {
    return (allExistingOfferImagesRetrieved.value === true && allExistingSpecImagesRetrieved.value === true && existingOfferDataCached.value === true && existingSpecDataCached.value === true && allOriginalAccessoriesRetrievedObv.value === true);
});
var existingOfferDataAndImagesCachedSubscriptionRef;
var selectedOfferId = 0;

// var curExistingOfferIdKey;
var alreadyLoadedImages = [];
var requestedImages = [];
var latestReportGraphicsFromLogin;
var selectionListResult;
var sharedOfferStubsResult;
var bodiesResult;
var accessoriesResult;
var trailersResult;
var payloadsResult;

var dataOpListenerCallback;
// var curOpTracker;
//var numRecordsToCache = 0;
// var numRecordsCached = 0;
var tempLoginDataRef = null;

var networkRequestsUnderway = 0;
let networkRequestGeneration = 0;

//usage 1 use logging control booleans
var performanceUseLogged = false,
    costingUseLogged = false,
    specificationUseLogged = false,
    summaryUseLogged = false,
    companyOverviewUseLogged = false,
    offersUseLogged = false,
    brochureUseLogged = false,
    trainingUseLogged = false,
    configurationUseLogged = false;

var syncOpSuccessResponseHandlerCallback = null, syncOpFailureResponseHandlerCallback = null, sharedOfferSyncHandlerCallback = null;

var teamSyncHandlerCallback = null;

// Setting up callback on globals for connectivity update for Intercom
globals.setSendEventInfoToIntercomCallback(sendInfoToIntercom);
security.registerDataManagerLogCallback(log);

var dataManager = {
    getSelectionList: getSelectionList,
    getNewOfferDetails: getNewOfferDetails,
    getOfferSpecificationDetails: getOfferSpecificationDetails,
    getVehicleSpecificationAdvantages: getVehicleSpecificationAdvantages,
    getLoginPageInformation: getLoginPageInformation,
    postSimulationResults: postSimulationResults,
    deleteOffer: deleteOffer,
    saveOffer: saveOffer,
    getExistingOfferDetails: getExistingOfferDetails,
    loadLocallyOrRetrieveAndStoreAppLogos: loadLocallyOrRetrieveAndStoreAppLogos,
    persist: persist,
    retrieve: retrieve,
    remove: remove,
    replaceReportImageUrls: replaceReportImageUrls,
    checkLicenceDays: checkLicenceDays,
    handleVersions: handleVersions,
    STORE: STORE,
    PROPERTY: PROPERTY,
    monitorOnlineState: monitorOnlineState,
    PRIORITY: PRIORITY,
    checkOnlineState: checkOnlineState,
    onlineStatePriorityComputed: onlineStatePriorityComputed,
    getOnlineStatePriority: getOnlineStatePriority,
    getTranslations: getTranslations,
    getCommonPackDetails: getCommonPackDetails,
    getLegislationDetails: getLegislationDetails,
    getUserSettings: getUserSettings,
    ensureCalculationContext,
    isCalculationContextCurrent,
    saveUserSettings: saveUserSettings,
    login: login,
    logout: logout,
    clearCachedData: clearCachedData,
    securityTokenIsValid: securityTokenIsValid,
    securityTokenIsValidAsTextFlag: securityTokenIsValidAsTextFlag,
    doSyncIfNeeded: doSyncIfNeeded,
    setupIndexedDB: setupIndexedDB,
    setOfflineValidUntilIfNeeded: setOfflineValidUntilIfNeeded,
    loadProperties: loadProperties,
    log: log,
    doDebugLogMaintenanceIfRequired: doDebugLogMaintenanceIfRequired,
    recentlyCached: recentlyCached,
    logAppUsage: logAppUsage,
    replaceImageUrl: replaceImageUrl,
    registerDataOperationListener: registerDataOperationListener,
    getBodyStubs: getBodyStubs,
    getBody: getBody,
    postDataRequest: postDataRequest,
    getAccessoryStubs: getAccessoryStubs,
    saveCustomAccessory: saveCustomAccessory,
    deleteCustomAccessory: deleteCustomAccessory,
    getAccessory: getAccessory,
    getNumVehicleOpensPropertyVal: getNumVehicleOpensPropertyVal,
    incrementNumVehicleOpensProperty: incrementNumVehicleOpensProperty,
    checkEmailVerificationStatusOnline: checkEmailVerificationStatusOnline,
    postVerificationCode: postVerificationCode,
    getCountries: getCountries,
    signUp: signUp,
    resendVerificationEmail: resendVerificationEmail,
    sendFeedback: sendFeedback,
    requestTrialExtension: requestTrialExtension,
    requestEnterprisePricing: requestEnterprisePricing,
    requestSalesTool: requestSalesTool,
    requestUpgrade: requestUpgrade,
    registerBuyNowClick: registerBuyNowClick,
    buyNow: buyNow,
    getPortalURL: getPortalURL,
    invalidateSecurityToken: invalidateSecurityToken,
    adjustLoggedInTrialUserAfterSuccessfulSubscribe: adjustLoggedInTrialUserAfterSuccessfulSubscribe,
    getUpdatePaymentMethodURL: getUpdatePaymentMethodURL,
    postEmailOffer: postEmailOffer,
    sendLoginDebugInfo: sendLoginDebugInfo,
    sendScreenSizeInfo: sendScreenSizeInfo,
    dismissAppOptionItem: dismissAppOptionItem,
    checkIsOnline: checkIsOnline,
    sendInfoToIntercom: sendInfoToIntercom,
    triggerEventInGoogleAnalytics: triggerEventInGoogleAnalytics,
    getOriginalsForItemsAddedToRig: getOriginalsForItemsAddedToRig,
    runDataIntegrityTests: runDataIntegrityTests,
    registerSyncOpSuccessResponseHandler: registerSyncOpSuccessResponseHandler,
    registerSyncOpFailureResponseHandler: registerSyncOpFailureResponseHandler,
    changePassword: changePassword,
    getTrailer: getTrailer,
    getTrailerStubs: getTrailerStubs,
    postDebugLogs: postDebugLogs,
    postUsage: postUsage,
    getPayloadStubs: getPayloadStubs,
    getPayload: getPayload,
    sendReferral: sendReferral,
    sendReferralPromptNotification: sendReferralPromptNotification,
    postCSVFileData: postCSVFileData,
    postDXFFileData: postDXFFileData,
    postTransSolveFileData: postTransSolveFileData,
    updatePendingVehicle: updatePendingVehicle,
    shareOffer: shareOffer,
    getUserAssociations: getUserAssociations,
    getShareeUserOfferDetails: getShareeUserOfferDetails,
    deleteUserOfferShareObject: deleteUserOfferShareObject,
    getSharedOfferStubs: getSharedOfferStubs,
    getOfferShareStatus: getOfferShareStatus,
    getSharedOfferStub: getSharedOfferStub,
    downloadVehicleCsv: downloadVehicleCsv,
    updateShareeUserDetails: updateShareeUserDetails,
    doDataActionsAfterLoginOrReAuth: doDataActionsAfterLoginOrReAuth,
    getLtvQualification: () => ltvQualification ? { ...ltvQualification } : undefined,
    prepareLtvCachedContinuation: versions => { if (ltvQualification) ltvCachedVersions = { owner: security.getIncarnation(), versions }; },
    updateOfferShareStatus: updateOfferShareStatus,
    registerSharedOfferSyncCallback: registerSharedOfferSyncCallback,
    validateNTEAUser: validateNTEAUser,
    doSync: doSync,
    pausedSyncJobs, refreshPausedSyncJobs, retrySyncJob, discardSyncJob,
    retainedDrafts, refreshRetainedDrafts, recoverRetainedDraft, getSessionOwner:()=>security.getIncarnation(),
    sendToRouteRequestBingMapsRestApi: sendToRouteRequestBingMapsRestApi,
    registerTeamSyncHandlerCallback: registerTeamSyncHandlerCallback,
    getSignalRConnectionInfo: getSignalRConnectionInfo,
    syncSharedOffers: syncSharedOffers,
    syncTeamItems: syncTeamItems,
    getBodyMasses: getBodyMasses,
    getLicenceCategories: getLicenceCategories,
    checkIsOnlineHeadVersion: checkIsOnlineHeadVersion,
    getFolderContents: getFolderContents,
    getSearchResults: getSearchResults,
    getFolderTreeStructure: getFolderTreeStructure,
    createFolder: createFolder,
    moveTo: moveTo,
    getSharers: getSharers,
    rename: rename,
    removeItems: removeItems,
    getSavedOfferStubs: getSavedOfferStubs,
    getResources: getResources,
    postTransferLicenceVerificationCode: postTransferLicenceVerificationCode,
    resendTransferLicenceVerificationEmail: resendTransferLicenceVerificationEmail,
    updateUserAfterCheckout: updateUserAfterCheckout,
    validateAccessToken: validateAccessToken,
    addStaleLastUsageLogUploadTimeStamp: addStaleLastUsageLogUploadTimeStamp,
    sendEmail: sendEmail,
    isCurrentSession: owner => security.incarnationMatches(owner),
    ensureFresh: () => security.ensureFresh(),
    runSessionOperation: (action, options) => sessionRecovery.run(action, options),
    createSessionOwner: () => sessionRecovery.createOwner(),
    switchLibrary,
    refreshApplicationData,
    getLibraryWriteError: libraryWriteError,
    handleAuthenticationFailure
};

export default dataManager;

function handleAuthenticationFailure(error) {
    if (error?.cancelled || error?.openingBusy) return;
    globals.hideSpinner();
    // Specification business failures already own their original 1601 dialog.
    if (error?.businessFailure) return;
    const code = error?.errorMessage === 'Offline' ? '1573' : '1571';
    globals.showDialog(new CustomMessageBox(config.getMessageText(code, globals.getActiveCultureCodeForTranslations()),
        config.applicationTitle, [config.getTranslationText('271')], config.getMessageReference(code)));
}

/**
 * Checks if there are any sync items stored in indexed db and if so calls doSync or else immediately calls back to caller.
 * @method doSyncIfNeeded
 * @param {Function} callbackSpinnerOn A callback to shell to turn spinner on if syncitems found.
 * @param {Function} callback A callback to turn off spinner for when doSync has finished syncing all item or if a problem is encountered.
 * @return {Boolean} Returns true or false to indicate if a screen refresh is required depending on whether or not any sync took place.
 */
function currentSyncItemsOwner(items, owner = security.getIncarnation()) {
    const origin = localDL.getSyncItemsOwner(items);
    return security.incarnationMatches(owner) && origin?.isCurrent() &&
        origin.cdg === globals.user.getCdg() && origin.username === globals.user.getUserName() ? origin : null;
}

function doSyncIfNeeded(callbackSpinnerOn, callback) {
    if (libraryTransitionActive || libraryRecoveryRequired || dataRefreshRequest) {
        globals.isSyncing.value = false; globals.offerSyncing.value = false;
        if (callback) callback(false); return;
    }
    const owner = security.getIncarnation();
    
    if (globals.isSyncing.value === true) {
        return;
    }
    const readToken = {}, priorSync = doSync.active;
    doSyncIfNeeded.active = readToken;
    const ownsRead = () => security.incarnationMatches(owner) && doSyncIfNeeded.active === readToken && doSync.active === priorSync;
    const retireRead = () => {
        if (ownsRead()) { globals.isSyncing.value = false; globals.offerSyncing.value = false; }
    };
    localDL.checkIfSyncItems(sessionCacheOwner(owner))
        .then(function (syncItems) {
            if (!currentSyncItemsOwner(syncItems, owner)) { retireRead(); return; }
            if (syncItems.length > 0) {
                if (callbackSpinnerOn !== undefined) {
                    callbackSpinnerOn();
                }
                if (!currentSyncItemsOwner(syncItems, owner)) { retireRead(); return; }
                
                globals.isSyncing.value = true;
                //globals.displayOnlineMessage(globals.user);
                log("Performing sync of offline activity", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                
                doSync(syncItems, callback);
            } else {
                publishPausedSyncJobs([], true, () => !!currentSyncItemsOwner(syncItems, owner));
                if (callback !== undefined) {
                    callback(false);
                }
                
            }
        }).fail(function (error) {
            if (!ownsRead()) return;
            if (error?.cancelled) { retireRead(); return; }
            globals.isSyncing.value = false;
            globals.offerSyncing.value = false;
            globals.offerSyncFailure = { localStorageUnavailable: true, errorMessage: 'Local work could not be read. Keep this draft open.' };
            if (callback) callback(true);
        });
}


function publishPausedSyncJobs(jobs, refreshDrafts = true, isCurrentContext) {
    if (isCurrentContext && !isCurrentContext()) return;
    if (releasePausedSyncJobs) releasePausedSyncJobs();
    releasePausedSyncJobs = security.registerCancellation(() => { pausedSyncJobs.value = [];retainedDrafts.value=[]; });
    if (security.isRememberedSuppressed() || (globals.getAlreadyAuthenticated && globals.getAlreadyAuthenticated() !== 'YES')) {
        pausedSyncJobs.value = [];retainedDrafts.value=[]; return;
    }
    pausedSyncJobs.value = jobs.map(job => ({ job, reason: syncJobBlockReason(job, jobs, OPERATION.SaveCustomAccessory?.Code,OPERATION.SaveOffer?.Code) }))
        .filter(entry => entry.reason).map(({ job, reason }) => ({
            timeStamp: job.TimeStamp, reason, owner: security.getIncarnation(),
            description: syncJobDescription(job),
            serverAcknowledged: !!job.ServerAcknowledged
        }));
    if(refreshDrafts)refreshRetainedDrafts(isCurrentContext).fail(()=>{});
}

function sessionCacheOwner(owner, isCurrentContext) {
    return { getIsCancelled: () => !security.incarnationMatches(owner) || !!(isCurrentContext && !isCurrentContext()),
        registerCancellation: callback => security.registerCancellation(callback),
        trackTransaction: transaction => security.registerCancellation(() => transaction.abort()) };
}

function nextRetainedWorkTimeStamp() {
    const value=Math.max(Date.now(),(nextRetainedWorkTimeStamp.lastValue||0)+1);
    nextRetainedWorkTimeStamp.lastValue=value;return value;
}

// A local queue receipt is not a server acknowledgement. Keep the completion
// bound to this exact work revision rather than the global syncing indicator.
function calculationSaveConfirmation(timeStamp, owner) {
    const dfd = $.Deferred();
    const pending = calculationSaveConfirmation.pending ||= new Map();
    pending.set(timeStamp, dfd);
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    const timer = setTimeout(() => dfd.reject({ localDurable: true, queued: true,
        confirmationRequired: true, timeout: true, workId: timeStamp }), 60000);
    dfd.always(() => { clearTimeout(timer); release(); pending.delete(timeStamp); });
    if (!security.incarnationMatches(owner)) dfd.reject({ cancelled: true });
    return dfd.promise();
}

function settleCalculationSave(timeStamp, result, error) {
    const dfd = calculationSaveConfirmation.pending?.get(timeStamp);
    if (!dfd) return;
    if (error) dfd.reject({ localDurable: true, queued: true, confirmationRequired: true,
        workId: timeStamp, ...error });
    else dfd.resolve({ savedOffer: result, localDurable: true, queued: false,
        serverPersisted: true, workId: timeStamp });
}

function refreshPausedSyncJobs() {
    const owner = security.getIncarnation();
    return localDL.checkIfSyncItems(sessionCacheOwner(owner)).then(function (jobs) {
        if (currentSyncItemsOwner(jobs, owner)) {
            publishPausedSyncJobs(jobs,false);
            return refreshRetainedDrafts(() => !!currentSyncItemsOwner(jobs, owner)).then(()=>currentSyncItemsOwner(jobs, owner) ? jobs : $.Deferred().reject({cancelled:true}).promise());
        }
        return $.Deferred().reject({cancelled:true}).promise();
    });
}

function retrySyncJob(timeStamp, owner, forceSave = false) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();
    if (globals.isSyncing.value) return $.Deferred().reject({ busy: true }).promise();
    return localDL.checkIfSyncItems(sessionCacheOwner(owner)).then(function (jobs) {
        const blocked = libraryWriteError();
        if (blocked) return $.Deferred().reject(blocked).promise();
        const origin = currentSyncItemsOwner(jobs, owner);
        if (!origin) return $.Deferred().reject({ cancelled: true }).promise();
        const job = jobs.find(item => item.TimeStamp === timeStamp);
        if (!job) return;
        job.NeedsSaveConfirmation = false;
        if (forceSave) job.Details.ForceSave = true;
        return localDL.addToCache(OPERATION.SyncQueue, job.TimeStamp, job, sessionCacheOwner(owner, () => !!currentSyncItemsOwner(jobs, owner))).then(function () {
            if (!currentSyncItemsOwner(jobs, owner)) return;
            globals.offerSyncFailure = null;
            doSyncIfNeeded();
        });
    });
}

function discardSyncJob(timeStamp, owner) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();
    if (globals.isSyncing.value) return $.Deferred().reject({ busy: true }).promise();
    if (!security.incarnationMatches(owner)) return $.Deferred().reject({ cancelled: true }).promise();
    // Explicitly discard only the submission. Keep the cached draft for recovery/copy.
    // Calculations with unresolved temporary source IDs remain paused and visible.
    return localDL.deleteRecord(STORE.SyncQueue, timeStamp, sessionCacheOwner(owner)).then(function () {
        if (security.incarnationMatches(owner)) { refreshPausedSyncJobs(); doSyncIfNeeded(); }
    });
}

function refreshRetainedDrafts(isCurrentContext) {
    const owner=security.getIncarnation();
    return localDL.readRetainedDrafts(sessionCacheOwner(owner, isCurrentContext)).then(drafts=>{
        if(security.incarnationMatches(owner)&&!security.isRememberedSuppressed()&&(!isCurrentContext||isCurrentContext()))retainedDrafts.value=drafts.map(({store,key,kind,description,revision})=>({store,key,kind,description,revision,owner}));
        return drafts;
    });
}

function recoverRetainedDraft(draft, description, owner) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();
    const fail=()=>$.Deferred().reject({recoverable:true,errorMessage:'Keep the retained draft and retry recovery.'}).promise();
    if(!security.incarnationMatches(owner)||security.isRememberedSuppressed())return $.Deferred().reject({cancelled:true}).promise();
    if(globals.isSyncing.value||typeof description!=='string'||!description.trim()||description.length>(draft.kind==='Calculation'?100:50))return fail();
    const request=localDL.readRetainedDrafts(sessionCacheOwner(owner),{store:draft.store,key:draft.key}).then(rows=>{
        if(!security.incarnationMatches(owner))return $.Deferred().reject({cancelled:true}).promise();
        if(rows.length!==1)return fail();const saved=rows[0];
        if(saved.kind==='Calculation'){
            const payload=saved.cache.RecoveryPayload?.Offer||saved.cache.Changes;
            if(!payload||typeof payload!=='object')return fail();
            const copy=JSON.parse(JSON.stringify(payload));copy.Id=saved.key;copy.Description=description.trim();
            const flags={};flags[saved.cache.Changes&&saved.cache.Offer?config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING:config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING_TSV]=true;
            return saveOffer(saved.key,0,config.FOLDER_OWNER_TYPE.USER,copy,flags,{notifyShareesofOfferChanges:false,optionalOfferChangesCommentForShareeEmail:''},[]);
        }
        if(!['Body','Crane','Other','Fridge','Taillift','FifthWheel','Hitch','Hooklift','Trailer','Payload'].includes(saved.kind))return fail();
        const copy=JSON.parse(JSON.stringify(saved.cache.data));
        Object.assign(copy,{Id:0,SourceDatabaseId:0,SavedFromSourceDatabaseId:0,UpdateCounter:0,Description:description.trim(),Name:description.trim(),LibraryOriginalUnavailable:false,EditLevel:config.ITEM_EDIT_LEVEL_OPTIONS.FULL_EDIT});
        delete copy.RetainWork;delete copy.WorkRevision;
        return saveCustomAccessory(0,saved.kind,copy,config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL,false,config.ITEM_EDIT_LEVEL_OPTIONS.NO_EDIT,false,config.ITEM_EDIT_LEVEL_OPTIONS.NO_EDIT);
    }).then(result=>{if(security.incarnationMatches(owner))refreshRetainedDrafts();return result;});
    return security.guardForSession(request,owner);
}

function doSync(syncItemsArr, callback) {
    if (libraryTransitionActive || libraryRecoveryRequired || dataRefreshRequest) {
        globals.isSyncing.value = false;
        globals.offerSyncing.value = false;
        if (callback) callback(false);
        return;
    }
    const queueOwner = currentSyncItemsOwner(syncItemsArr);
    if (!queueOwner) return;
    const syncOwner = security.getIncarnation();
    const syncToken = {};
    doSync.active = syncToken;
    const workOwner = { incarnation: syncOwner, username: queueOwner.username, cdg: queueOwner.cdg };
    const sessionCurrent = () => security.incarnationMatches(syncOwner);
    const syncCurrent = () => sessionCurrent() && queueOwner.isCurrent() && workOwner.username === globals.user.getUserName() && workOwner.cdg === globals.user.getCdg();
    const cacheOwner = { ...sessionCacheOwner(syncOwner, syncCurrent), namespace: workOwner };
    var curSyncItem;
    function retireCalculationSave(extra = {}) {
        if (!sessionCurrent()) return;
        settleCalculationSave(curSyncItem.TimeStamp, null, { cancelled: true, contextChanged: true, ...extra });
        if (doSync.active === syncToken) {
            globals.isSyncing.value = false;
            globals.offerSyncing.value = false;
        }
    }
    const retainedSave = job => job.Operation.Code === OPERATION.SaveCustomAccessory?.Code ||
        (job.RetainWork && job.Operation.Code === OPERATION.SaveOffer?.Code);
    if (syncItemsArr.some(job => job.Operation?.Code === OPERATION.SaveOffer?.Code)) globals.offerSyncing.value = true;
    const eligible = syncItemsArr.findIndex(job => !syncJobBlockReason(job, syncItemsArr, OPERATION.SaveCustomAccessory?.Code,OPERATION.SaveOffer?.Code));
    publishPausedSyncJobs(syncItemsArr, true, syncCurrent);
    if (syncItemsArr.length === 0 || eligible < 0) {
        log("Finished sync of offline activity", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);

        globals.isSyncing.value = false;
        globals.offerSyncing.value = false;
        if (syncItemsArr.length) globals.offerSyncFailure = { paused: true, errorMessage: 'Unsynchronised work requires recovery' };
        else globals.offerSyncFailure = null;
        if (callback !== undefined) {
            sendInfoToIntercom(config.OPERATION.SendIntercomEventData, globals.getIntercomObject(config.INTERCOM_EVENT.CHANGED_CONNECTIVITY, globals.getIntercomMetadataForIsOnline(globals.isOnline.value)));
            callback(true);
        }
    } else {
        if (!security.isRememberedSuppressed()) {
            curSyncItem = syncItemsArr.splice(eligible, 1)[0];
            if (curSyncItem !== undefined && curSyncItem !== null) {
                if (curSyncItem.ServerAcknowledged) { handleRemoteSyncResponse(curSyncItem.ServerAcknowledged); return; }
                if (retainedSave(curSyncItem)) {
                    // Persist the interrupted-attempt fence before issuing a write.
                    // A restart must never silently replay an ambiguous equipment save.
                    curSyncItem.NeedsSaveConfirmation = true;
                    localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem, cacheOwner).then(function () {
                        if (syncCurrent()) remoteDL.handleSync(security, curSyncItem).then(handleRemoteSyncResponse, handleRemoteSyncFailure);
                        else if (curSyncItem.Operation.Code === OPERATION.SaveOffer?.Code) retireCalculationSave({ serverPersisted: false });
                    }, handleRemoteSyncFailure);
                } else remoteDL.handleSync(security, curSyncItem).then(handleRemoteSyncResponse, handleRemoteSyncFailure);
            }
            
        } else {
            globals.isSyncing.value = false;
            if (callback !== undefined) {
                callback(true);
            }
        }
    }
    function deleteSyncItemIfNotAllowedOffline() {
        if (!syncCurrent()) return;
        if (curSyncItem.Operation.AllowOffline !== undefined && curSyncItem.Operation.AllowOffline === false) {
            localDL.deleteRecord(STORE.SyncQueue, curSyncItem.TimeStamp);
        }
    }
    function moveNext() {
        if (!syncCurrent()) return;
        localDL.deleteRecord(STORE.SyncQueue, curSyncItem.TimeStamp, cacheOwner).then(function () {
            if (!syncCurrent()) return;
            return localDL.checkIfSyncItems(sessionCacheOwner(syncOwner)).then(jobs => { if (syncCurrent()) doSync(jobs, callback); });
        }).fail(handleRemoteSyncFailure);
    }

    function pauseEquipment(failure) {
        if (!syncCurrent()) return;
        if (curSyncItem.Operation.Code === OPERATION.SaveOffer?.Code)
            settleCalculationSave(curSyncItem.TimeStamp, null, { serverPersisted: !!curSyncItem.ServerAcknowledged,
                businessDenied: !!failure?.Result && failure.Result.ReturnCode < 0,
                feedbackPresented: failure?.feedbackPresented === true,
                returnCode: failure?.Result?.ReturnCode, status: failure?.status || 0,
                renewalFailure: failure?.renewalFailure, protectedDispatched: failure?.protectedDispatched,
                authenticationFailed: failure?.authenticationFailed, permissionDenied: failure?.permissionDenied,
                retryable: failure?.retryable, throttled: failure?.throttled, retryAfterSeconds: failure?.retryAfterSeconds });
        curSyncItem.NeedsSaveConfirmation = true;
        curSyncItem.LastFailure = { status: failure?.status || 0, returnCode: failure?.Result?.ReturnCode || 0 };
        const remaining = [curSyncItem, ...syncItemsArr];
        localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem, cacheOwner).then(function () {
            if (!syncCurrent()) return;
            return localDL.checkIfSyncItems(sessionCacheOwner(syncOwner)).then(jobs => { if (syncCurrent()) doSync(jobs, callback); });
        }).fail(function () {
            if (!syncCurrent()) return;
            publishPausedSyncJobs(remaining, true, syncCurrent);
            globals.isSyncing.value = false;
            globals.offerSyncing.value = false;
            globals.offerSyncFailure = { paused: true, errorMessage: 'Local storage failed; keep this draft open' };
            if (callback) callback(true);
        });
    }

    function handleRemoteSyncResponse(data) {
        if (!sessionCurrent()) return;
        if (curSyncItem.RetainWork && curSyncItem.Operation.Code === OPERATION.SaveOffer?.Code) {
            // A confirmed late response is retained in its original queue slot.
            // Context replacement must not redirect completion into a new cache.
            if ([1, 2].includes(data?.Result?.ReturnCode) && data.Offer?.Id > 0 && !curSyncItem.ServerAcknowledged) {
                curSyncItem.ServerAcknowledged = data; curSyncItem.NeedsSaveConfirmation = false;
                localDL.acknowledgeCalculationSave(curSyncItem, data, { ...sessionCacheOwner(syncOwner), namespace: workOwner })
                    .then(() => {
                        if (syncCurrent()) handleRemoteSyncResponse(data);
                        else retireCalculationSave({ serverPersisted: true, localDurable: true });
                    }, error => {
                        if (syncCurrent()) pauseEquipment(error);
                        else retireCalculationSave({ serverPersisted: true, localCompletionFailed: true });
                    });
                return;
            }
            if (!syncCurrent()) {
                retireCalculationSave({ serverPersisted: !!curSyncItem.ServerAcknowledged });
                return;
            }
            if (!data?.Result || ([1, 2].includes(data.Result.ReturnCode) && !(data.Offer?.Id > 0))) { pauseEquipment(data); return; }
            if([1,2].includes(data.Result.ReturnCode)){
                localDL.completeCalculationSave(curSyncItem,data,cacheOwner).then(completion=>{
                    if(!syncCurrent()){retireCalculationSave({serverPersisted:true,localDurable:true});return;}
                    const newer=[completion.oldId,data.Offer.Id].some(id=>(equipmentRevisionStarts.get(syncOwner+'|Calculation|'+id)||0)>curSyncItem.TimeStamp);
                    if (completion.pendingRevision || newer) settleCalculationSave(curSyncItem.TimeStamp, null, { serverPersisted: true, newerRevision: true });
                    else settleCalculationSave(curSyncItem.TimeStamp, data);
                    if(!completion.pendingRevision&&!newer&&syncOpSuccessResponseHandlerCallback)syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code,curSyncItem.Details.Offer.Description,{savedOffer:data,workId:curSyncItem.TimeStamp,workOwner},completion.oldId);
                    return localDL.checkIfSyncItems(sessionCacheOwner(syncOwner)).then(jobs=>{if(syncCurrent())doSync(jobs,callback);});
                }).fail(error=>{if(syncCurrent())pauseEquipment(error);else retireCalculationSave({serverPersisted:true,localDurable:true});});return;
            }
        }
        if (!syncCurrent()) return;
        if (curSyncItem.Operation.Code === OPERATION.SaveCustomAccessory?.Code &&
            (!data?.Result || ![1, 2, 3].includes(data.Result.ReturnCode) || !(data.AccessoryId > 0))) {
            pauseEquipment(data); return;
        }

        var keyToUse;
        var deleteTempCustRecRequired = false;
        var deleteOfferRecRequired = false;
        var idOfOfferToDelete = '';
        var itemDescriptionText;
    
        switch (curSyncItem.Operation.Code) {
            case OPERATION.SaveOffer.Code:
                if (curSyncItem.ExistingId === 0) {
                    keyToUse = "offerId_" + curSyncItem.TimeStamp;
                } else {
                    keyToUse = curSyncItem.ExistingId;
                }

                itemDescriptionText = curSyncItem.Details.Offer.Description;
                if (curSyncItem.Details.Offer.AdditionalDescription && curSyncItem.Details.Offer.AdditionalDescription !== '') {
                    itemDescriptionText = itemDescriptionText + ' ' + curSyncItem.Details.Offer.AdditionalDescription;
                }
                if (data.Result.ReturnCode === 1 || data.Result.ReturnCode === 2) {
                    globals.offerSyncFailure = null;
                    //itemDescriptionText = data.Offer.Description;
                    var updatedOffer = null;
    
                    
                    //syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { savedOffer: data }, keyToUse);
                    //moveNext();
                    updateOffer(keyToUse)
                        .then(function () {
        if (!syncCurrent()) return;
                            if (deleteOfferRecRequired === true) {
                                localDL.deleteRecord(STORE.SavedOffers, idOfOfferToDelete, cacheOwner, true)
                                    .then(function () {
        if (!syncCurrent()) return;
                                        doRestOfCode();
                                    });
                            } else {
                                doRestOfCode();
                            }
                            function doRestOfCode() {
        if (!syncCurrent()) return;
                                if (deleteTempCustRecRequired === true) {

                                    if (updatedOffer !== null) {
                                        localDL.addToCache(OPERATION.SaveOffer.Code, updatedOffer.OfferId, updatedOffer);
                                    }
                                    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { savedOffer: data }, keyToUse);
                                    moveNext();

                                } else {
                                    if (updatedOffer !== null) {
                                        localDL.addToCache(OPERATION.SaveOffer.Code, updatedOffer.OfferId, updatedOffer);
                                    }
                                    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { savedOffer: data }, keyToUse);
                                    moveNext();
                                }
                            }
                        })
    
                    
                } else if (data.Result.ReturnCode === -3 || data.Result.ReturnCode === -4 || 
                            data.Result.ReturnCode === -5 || data.Result.ReturnCode === -6 || 
                            data.Result.ReturnCode === -7) {
                    handleSyncItemFailureAdvanced(curSyncItem.Operation.Code, itemDescriptionText, data, data.Result.ReturnCode, data.Result.Message)
                } else {
                    handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.Result.ReturnCode, data.Result.Message);
                }
    
                break;
            case OPERATION.DeleteOffer.Code:
                itemDescriptionText = curSyncItem.Details.Description;
                if (data.ReturnCode === 1) {
                    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText);
                    moveNext();
                } else {
                    handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
                }
                break;
            case OPERATION.DeleteCustomAccessory.Code:
                if (curSyncItem.ExistingId === 0) {
                    keyToUse = "accessoryId_" + curSyncItem.TimeStamp;
                } else {
                    keyToUse = curSyncItem.ExistingId;
                }
                itemDescriptionText = curSyncItem.Details.Description;
                if (data.ReturnCode === 1) {
                    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText);
                    moveNext();
                } else if (data.ReturnCode === -2 || data.ReturnCode === -3) {
                    data.stubId = keyToUse;
                    handleSyncItemFailureAdvanced(curSyncItem.Operation.Code, itemDescriptionText, data, data.ReturnCode, data.Message);
                } else {
                    handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
                }
                break;
            case OPERATION.SaveCustomAccessory.Code: {
                const oldId = curSyncItem.OriginalLocalId || curSyncItem.ExistingId || 'accessoryId_' + curSyncItem.TimeStamp;
                const kind = curSyncItem.Details.AccessoryType;
                const saved = JSON.parse(curSyncItem.Details.AccessoryDetail);
                Object.assign(saved, { Id: data.AccessoryId, UpdateCounter: data.UpdateCounter,
                    Description: data.AccessoryDescription || saved.Description,
                    Source: config.VEHICLE_SOURCE_TYPES.CUSTOM, AccessLevel: data.AccessLevel });
                Object.assign(data, { AccessoryType: kind, Source: saved.Source, Description: saved.Description,
                    Type: saved.Type, ParentType: curSyncItem.Details.ParentType, PersistedOnServer: true });
                curSyncItem.ServerAcknowledged = data;
                curSyncItem.NeedsSaveConfirmation = false;
                // Acknowledge durably before cache changes; re-entry resumes local completion.
                localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem, cacheOwner).then(function () {
                    if (!syncCurrent()) return;
                    return localDL.completeEquipmentSave(curSyncItem,data,cacheOwner).then(function (completion) {
                        if (!syncCurrent()) return;
                        const newerStarted=[oldId,data.AccessoryId].some(id=>(equipmentRevisionStarts.get(syncOwner+'|'+kind+'|'+id)||0)>curSyncItem.TimeStamp);
                        if (!completion.pendingRevision && !newerStarted && syncOpSuccessResponseHandlerCallback) syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code,saved.Description,{savedAccessory:data},oldId);
                        return localDL.checkIfSyncItems(sessionCacheOwner(syncOwner)).then(jobs=>{if(syncCurrent())doSync(jobs,callback);});
                    });
                }).fail(pauseEquipment);
                break;
            }
            case OPERATION.SaveUserSettings.Code:
                itemDescriptionText = config.getTranslationText('644');
                if (data.Result.ReturnCode === 1) {
                    if(globals.userSettingsSaveDfd) {
                        globals.userSettingsSaveDfd.resolve();
                        globals.userSettingsSaveDfd = null;
                    }
                    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { data: data }, keyToUse);
                    moveNext();
                } else {
                    handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
                }
                //if (data.ReturnCode === 1) {
                //    syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { data: data }, keyToUse);
                //    moveNext();
                //} else {
                //    handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
                //}
                break;
            // case OPERATION.ChangePassword.Code:
            //     itemDescriptionText = config.getTranslationText('652');
            //     if (data.ReturnCode === 1) {
            //         globals.user.updateUser({ password: curSyncItem.Details.NewPassword });
            //         syncOpSuccessResponseHandlerCallback(curSyncItem.Operation.Code, itemDescriptionText, { data: data }, keyToUse);
            //         moveNext();
            //     } else {
            //         handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
            //     }
            //     break;
            case OPERATION.PostAppOptionItem.Code:
                if (data.ReturnCode === 1) {
                    moveNext();
                } else {
                    handleSyncItemFailure(curSyncItem.Operation.Code, {}, data.ReturnCode, data.Message);
                }
                break;
            case OPERATION.SendIntercomEventData.Code:
            case OPERATION.SendIntercomUserData.Code:
                if (syncItemsArr.length > 0 && (syncItemsArr[0].Operation.Code === config.OPERATION.SendIntercomEventData.Code || syncItemsArr[0].Operation.Code === config.OPERATION.SendIntercomUserData.Code)) {
                    setTimeout(function () {
        if (!syncCurrent()) return;
                        moveNext();
                    }, 500);
                } else {
                    moveNext();
                }
    
                //if (data.ReturnCode === 1) {
                //    moveNext();
                //} else {
                //    handleSyncItemFailure(data.ReturnCode);
                //}
                break;
            default:
                log("doSync, remoteDL.handleSync, then, default", null, 'dataManager', config.LOG_MESSAGE_TYPE.ERROR);
                globals.isSyncing.value = false;
                callback(true);
                return;
        }
        function handleSyncItemFailure(curOp, itemDescriptionText, returnCode, cloudServicesMessage) {
        if (!syncCurrent()) return;
            if (curSyncItem.RetainWork && curOp === OPERATION.SaveOffer.Code) {
                if (syncOpFailureResponseHandlerCallback) syncOpFailureResponseHandlerCallback(curOp, itemDescriptionText,
                    { Result: { ReturnCode: returnCode, Message: cloudServicesMessage }, syncItem: curSyncItem, sessionOwner: syncOwner });
                pauseEquipment({ Result: { ReturnCode: returnCode } }); return;
            }
            if (curOp === OPERATION.SaveOffer.Code) {
                globals.offerSyncFailure = { businessDenied: true, returnCode, errorMessage: 'Calculation save requires confirmation or correction' };
                globals.offerSyncing.value = false;
                globals.isSyncing.value = false;
                curSyncItem.NeedsSaveConfirmation = true;
                localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem);
                const saveData = { Result: { ReturnCode: returnCode, Message: cloudServicesMessage } };
                saveData.syncItem = curSyncItem;
                saveData.sessionOwner = syncOwner;
                syncOpFailureResponseHandlerCallback(curOp, itemDescriptionText, saveData);
                if (callback !== undefined) callback(true);
                return;
            }

            var errorDescription = "doSync, handleSyncItemFailure, remoteDL.handleSync, Op=" + curSyncItem.Operation.Code + ", ReturnCode=" + returnCode;
            var obj = {};
            obj[config.INTERCOM_METADATA_ITEM.FAILED_OPERATION] = curSyncItem.Operation.Code;
            obj[config.INTERCOM_METADATA_ITEM.CS_TRANSACTION_MESSAGE] = cloudServicesMessage;
            log(errorDescription, cloudServicesMessage, 'dataManager', config.LOG_MESSAGE_TYPE.ERROR, obj);
            //var obj = {};
            //obj[config.INTERCOM_METADATA_ITEM.FAILED_OPERATION] = curSyncItem.Operation.Code;
            //obj[config.INTERCOM_METADATA_ITEM.ORIGIN] = system.getModuleId(dataManager);
            //obj[config.INTERCOM_METADATA_ITEM.DESCRIPTION] = errorDescription;
            //obj[config.INTERCOM_METADATA_ITEM.CS_TRANSACTION_MESSAGE] = cloudServicesMessage;
            //sendInfoToIntercom(config.OPERATION.SendIntercomEventData, globals.getIntercomObject(config.INTERCOM_EVENT.SYNC_FAILED, obj));
            if (curSyncItem && curSyncItem !== null) {
                localDL.deleteRecord(STORE.SyncQueue, curSyncItem.TimeStamp);
                doSync(syncItemsArr, callback);
            }
            syncOpFailureResponseHandlerCallback(curOp, itemDescriptionText);
        }
        function handleSyncItemFailureAdvanced(curOp, itemDescriptionText, data, returnCode, message) {
        if (!syncCurrent()) return;
            if (curSyncItem.RetainWork && curOp === OPERATION.SaveOffer.Code) {
                const feedbackPresented = syncOpFailureResponseHandlerCallback?.(curOp, itemDescriptionText,
                    { ...data, syncItem: curSyncItem, sessionOwner: syncOwner }) === true;
                pauseEquipment({ ...data, feedbackPresented }); return;
            }
            if (curOp === OPERATION.SaveOffer.Code) {
                globals.offerSyncFailure = { businessDenied: true, returnCode, errorMessage: 'Calculation save requires confirmation or correction' };
                globals.offerSyncing.value = false;
                globals.isSyncing.value = false;
                curSyncItem.NeedsSaveConfirmation = true;
                localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem);
                const saveData = data;
                saveData.syncItem = curSyncItem;
                saveData.sessionOwner = syncOwner;
                syncOpFailureResponseHandlerCallback(curOp, itemDescriptionText, saveData);
                if (callback !== undefined) callback(true);
                return;
            }

            var errorDescription = "doSync, handleSyncItemFailure, remoteDL.handleSync, Op=" + curSyncItem.Operation.Code + ", ReturnCode=" + returnCode;
            var obj = {};
            obj[config.INTERCOM_METADATA_ITEM.FAILED_OPERATION] = curSyncItem.Operation.Code;
            obj[config.INTERCOM_METADATA_ITEM.CS_TRANSACTION_MESSAGE] = message;
            log(errorDescription, message, 'dataManager', config.LOG_MESSAGE_TYPE.ERROR, obj);
            //var obj = {};
            //obj[config.INTERCOM_METADATA_ITEM.FAILED_OPERATION] = curSyncItem.Operation.Code;
            //obj[config.INTERCOM_METADATA_ITEM.ORIGIN] = system.getModuleId(dataManager);
            //obj[config.INTERCOM_METADATA_ITEM.DESCRIPTION] = errorDescription;
            //obj[config.INTERCOM_METADATA_ITEM.CS_TRANSACTION_MESSAGE] = message;
            //sendInfoToIntercom(config.OPERATION.SendIntercomEventData, globals.getIntercomObject(config.INTERCOM_EVENT.SYNC_FAILED, obj));
            if (curSyncItem && curSyncItem !== null) {
                localDL.deleteRecord(STORE.SyncQueue, curSyncItem.TimeStamp);
                data.syncItem = curSyncItem;
                doSync(syncItemsArr, callback);
            }
            syncOpFailureResponseHandlerCallback(curOp, itemDescriptionText, data);
        }
        function updateOffer(keyToUse) {
        if (!syncCurrent()) return;
            var updateOfferDfd = $.Deferred();

            localDL.getFromCache(OPERATION.GetExistingOfferDetails, keyToUse)
            .then(function (offerData) {
        if (!syncCurrent()) return;
                if (offerData !== null) {
                    delete offerData.RetainWork;
                    if (offerData.OfferId !== data.Offer.Id) {
                        deleteOfferRecRequired = true;
                        if (typeof keyToUse === 'string' && keyToUse.indexOf('_') !== -1) {
                            idOfOfferToDelete = keyToUse;
                        } else {
                            idOfOfferToDelete = offerData.OfferId;
                        }
                        offerData.OfferId = data.Offer.Id;
                        offerData.Changes.Id = data.Offer.Id;
                    }

                    offerData.Changes.Description = data.Offer.Description;
                    //offerData.Changes.AdditionalDescription = '';

                    //offerData.UpdateCounter = data.Offer.UpdateCounter;
                    //offerData.Changes.UpdateCounter = data.Offer.UpdateCounter;

                    
                    offerData.OfferDate = moment(data.Offer.OfferDate, ["M/D/YYYY", "DD-MM-YYYY", "MM-DD-YYYY", "DD/MM/YYYY", "YYYY-MM-DD", "YYYY/MM/DD"]).format('YYYY-MM-DDTHH:mm:ss:SSS');

                    updatedOffer = offerData;

                }
                updateOfferDfd.resolve();
            });
            return updateOfferDfd.promise();
        }
    }

    function handleRemoteSyncFailure(errorMessage) {
        if (sessionCurrent() && !syncCurrent() && curSyncItem?.RetainWork && curSyncItem.Operation.Code === OPERATION.SaveOffer?.Code) {
            retireCalculationSave({ serverPersisted: !!curSyncItem.ServerAcknowledged });
            return;
        }
        if (!syncCurrent()) return;
        if (retainedSave(curSyncItem)) { pauseEquipment(errorMessage); return; }
        log("doSync, remoteDL.handleSync, fail", errorMessage, 'dataManager', config.LOG_MESSAGE_TYPE.ERROR);
        globals.offerSyncFailure = errorMessage;
        globals.isSyncing.value = false;
        globals.offerSyncing.value = false;
        // An ambiguous write may have committed. Retain local work and require
        // deliberate confirmation rather than silently submitting it again.
        if (curSyncItem && (errorMessage.cancelled || errorMessage.timeout || errorMessage.errorMessage === 'timeout' ||
            (errorMessage.status === 0 && !errorMessage.cancelled))) {
            curSyncItem.NeedsSaveConfirmation = true;
            localDL.addToCache(OPERATION.SyncQueue, curSyncItem.TimeStamp, curSyncItem);
        }
        if (!curSyncItem.NeedsSaveConfirmation) deleteSyncItemIfNotAllowedOffline();
        if (callback !== undefined) callback(true);
    }

}



/**
* Checks if there are any related operations already in the sync queue and acts accordingly by updating or removing depending on the circumstance. The idea is to only have one occurance
* for each object in the sync queue or remove sub objects that are now defunct as their parent object has been deleted.
* @method manageSyncQueue
* @param {Number/String} existingId An id for the object on which the operation is being carried out. If the id is a temporary id it will be a String, otherwise a Number.
* @param {String} saveOrDelete A flag to denote the type of the new operation.
*/
function manageSyncQueue(existingId, saveOrDelete, newOp, excludeTimeStamp, owner = security.getIncarnation(), localSettingsSave, isCurrentContext) {
    let readItems;
    const ownerCurrent = () => security.incarnationMatches(owner) && (!isCurrentContext || isCurrentContext()) &&
        (!readItems || !!currentSyncItemsOwner(readItems, owner));
    const blocked = libraryWriteError(newOp === OPERATION.SaveUserSettings ? localSettingsSave : undefined);
    if (blocked) return $.Deferred().reject(blocked).promise();
    if (!ownerCurrent()) return $.Deferred().reject({ cancelled: true }).promise();
    var dfd = $.Deferred();

    var abortAdd = false;
    const deletions = [];

    localDL.checkIfSyncItems(sessionCacheOwner(owner))
        .then(function (syncItems) {
            readItems = syncItems;
            const blocked = libraryWriteError(newOp === OPERATION.SaveUserSettings ? localSettingsSave : undefined);
            if (blocked) { dfd.reject(blocked); return; }
            if (!ownerCurrent()) { dfd.reject({ cancelled: true }); return; }
            if (saveOrDelete === 'SAVE' && newOp === OPERATION.SaveCustomAccessory) {
                const newKind = syncItems.find(item => String(item.TimeStamp) === String(excludeTimeStamp))?.Details?.AccessoryType;
                for (const item of syncItems) {
                    if (Number(item.TimeStamp) >= Number(excludeTimeStamp) || item.Operation.Code !== newOp.Code) continue;
                    if (!newKind || item.Details?.AccessoryType !== newKind) continue;
                    const alias = item.ExistingId || 'accessoryId_' + item.TimeStamp;
                    if (![String(alias), String(item.OriginalLocalId)].includes(String(existingId))) continue;
                    // Keep a producer for its temporary ID, and every in-flight or
                    // acknowledged write. A later edit is an ordered new job.
                    if ((!item.ExistingId && String(existingId) === String(alias)) || item.ServerAcknowledged ||
                        (item.NeedsSaveConfirmation && globals.isSyncing.value)) continue;
                    deletions.push(localDL.deleteRecord(STORE.SyncQueue, item.TimeStamp, sessionCacheOwner(owner, ownerCurrent)));
                }
                $.when.apply($, deletions).then(() => dfd.resolve(false), dfd.reject);
                return;
            }
            if(saveOrDelete==='SAVE'&&newOp.Code===OPERATION.SaveOffer.Code){
                for(const item of syncItems){
                    if(!item.RetainWork||item.Operation?.Code!==newOp.Code||String(item.TimeStamp)===String(excludeTimeStamp)||Number(item.TimeStamp)>=Number(excludeTimeStamp))continue;
                    const alias=item.ExistingId||'offerId_'+item.TimeStamp;
                    if(String(alias)!==String(existingId))continue;
                    if(!item.ExistingId||String(item.ExistingId).startsWith('offerId_')||item.ServerAcknowledged||item.NeedsSaveConfirmation)continue;
                    deletions.push(localDL.deleteRecord(STORE.SyncQueue,item.TimeStamp,sessionCacheOwner(owner, ownerCurrent)));
                }
                $.when.apply($,deletions).then(()=>dfd.resolve(false),dfd.reject);return;
            }
            var i;
            if (saveOrDelete === "DELETE") {
                for (i = 0; i < syncItems.length; i++) {
                    if (String(syncItems[i].TimeStamp) === String(excludeTimeStamp)) continue;
                    if (existingId !== 0) {
                        if (syncItems[i].ExistingId === 0) {
                            if (typeof existingId === 'string' && existingId.indexOf(syncItems[i].TimeStamp) !== -1) {
                                deletions.push(localDL.deleteRecord(STORE.SyncQueue, syncItems[i].TimeStamp, sessionCacheOwner(owner, ownerCurrent)));
                                abortAdd = true;
                                break;
                            }
                        } else {
                            if (syncItems[i].ExistingId === existingId) {
                                if (typeof syncItems[i].ExistingId === 'string' && syncItems[i].ExistingId.indexOf("_") !== -1) {
                                    abortAdd = true;
                                }
                                deletions.push(localDL.deleteRecord(STORE.SyncQueue, syncItems[i].TimeStamp, sessionCacheOwner(owner, ownerCurrent)));
                                break;
                            }
                        }
                    }
                }
            } else {
                for (i = 0; i < syncItems.length; i++) {
                    if (String(syncItems[i].TimeStamp) === String(excludeTimeStamp)) continue;
                    //syncItems[i].ExistingId
                    if (existingId !== 0) {
                        if (syncItems[i].ExistingId === 0) {
                            if (typeof existingId === 'string' && existingId.indexOf(syncItems[i].TimeStamp) !== -1 && (syncItems[i].Operation.Code === newOp.Code)) {
                                deletions.push(localDL.deleteRecord(STORE.SyncQueue, syncItems[i].TimeStamp, sessionCacheOwner(owner, ownerCurrent)));
                                break;
                            }
                        } else {
                            if (syncItems[i].ExistingId === existingId && (syncItems[i].Operation.Code === newOp.Code)) {
                                deletions.push(localDL.deleteRecord(STORE.SyncQueue, syncItems[i].TimeStamp, sessionCacheOwner(owner, ownerCurrent)));
                                break;
                            }
                        }
                    }
                }
            }
            $.when.apply($, deletions).then(() => dfd.resolve(abortAdd), dfd.reject);
        }, dfd.reject);

    return dfd.promise();
}

/**
* Adds an item to the SyncQueue object store making sure the correct ids are set on the way through.
* @method queueSyncJob
* @param {Object} op The operation that is to be carried out. This op object also encapsulates the associated request url.
* @param {Number} timeStamp A timeStamp for when the operation was attempted. It acts as a uinique id for the sync item and also is utilised in creating the temporary id for the object if necessary.
* @param {Object} params An object whose proprties are the url params required to complete the request url. Note these must be named correctly as the key is used to build the request.
* @param {Object} details An optional argument that specifies any payload relating to the operation that needs to be POST-ed to the server.
*/
function queueSyncJob(op, timeStamp, params, details, owner = security.getIncarnation(), existingLocalId, localSettingsSave, isCurrentContext) {
    if (isCurrentContext && !isCurrentContext()) return $.Deferred().reject({ cancelled: true }).promise();
    const blocked = libraryWriteError(op === OPERATION.SaveUserSettings ? localSettingsSave : undefined);
    if (blocked) return $.Deferred().reject(blocked).promise();
    var queueSyncJobDfd = $.Deferred();
    var existingId = 0;
    var parentId = 0;
    switch (op) {
        case OPERATION.SaveOffer:
            existingId = params["offerId"] || 0;
            break;
        case OPERATION.SaveUserSettings:
            existingId = params["id"]|| 0;
            break;
        // These cases may not be needed
        case OPERATION.SendIntercomEventData.Code:
            //params = null;
            break;
        default:
            existingId = params["id"] || 0;
            break;
    }
    var syncItem = {
        TimeStamp: timeStamp,
        Details: details || null,
        Operation: op,
        ExistingId: existingLocalId === undefined ? existingId : existingLocalId,
        OriginalLocalId: typeof (existingLocalId===undefined?existingId:existingLocalId) === 'string' ? (existingLocalId===undefined?existingId:existingLocalId) : undefined,
        Params: params,
        RetainWork: op === OPERATION.SaveCustomAccessory || op === OPERATION.SaveOffer
    };
    localDL.addToCache(OPERATION.SyncQueue, timeStamp, syncItem, sessionCacheOwner(owner, isCurrentContext))
        .then(function () {
            queueSyncJobDfd.resolve();
        }, queueSyncJobDfd.reject);
    return queueSyncJobDfd.promise();
}
    
function registerSyncOpSuccessResponseHandler(syncOpSuccessHandler) {
    syncOpSuccessResponseHandlerCallback = syncOpSuccessHandler;
}
function registerSyncOpFailureResponseHandler(syncOpFailureHandler) {
    syncOpFailureResponseHandlerCallback = syncOpFailureHandler;
}

function registerSharedOfferSyncCallback(sharedOfferSyncHandler) {
    sharedOfferSyncHandlerCallback = sharedOfferSyncHandler;
}

function registerTeamSyncHandlerCallback(teamSyncHandler) {
    teamSyncHandlerCallback = teamSyncHandler;
}


function getSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range) {

    
    // var dfd = $.Deferred();
    if(globals.selectionListDfd) {
        return globals.selectionListDfd.promise();
    } else {
        globals.selectionListDfd = $.Deferred();
    }

    if (doingBackgroundSelectionRetrieval.value === true || globals.isSyncing.value === true) {
        if (canUseRawSelectionListData()) {
            handleRawSelectionListDataResolve();
        } else {
            var doingBackgroundSelectionRetrievalSubscriptionRef = watch(doingBackgroundSelectionRetrieval, function (newValue) {
                doingBackgroundSelectionRetrievalSubscriptionRef();
                doingBackgroundSelectionRetrievalSubscriptionRef = null;
                if (canUseRawSelectionListData()) {
                    handleRawSelectionListDataResolve();
                } else {
                    if (newValue === false && globals.isSyncing.value === false) {
                        doGetSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range, globals.selectionListDfd);
                    }
                }
            });
            var globalsIsSyncingSubscriptionRef = watch(globals.isSyncing, function (newVal) {
                globalsIsSyncingSubscriptionRef();
                globalsIsSyncingSubscriptionRef = null;
                if (canUseRawSelectionListData()) {
                    handleRawSelectionListDataResolve();
                } else {
                    if (newVal === false && doingBackgroundSelectionRetrieval.value === false) {
                        doGetSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range, globals.selectionListDfd);
                    }
                }
                
            });
        }
        
    } else {
        if (canUseRawSelectionListData(true)) {
            handleRawSelectionListDataResolve();
        } else {
            doGetSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range, globals.selectionListDfd);
        }
        
    }

    function canUseRawSelectionListData(doWizardCheck) {
        if (doWizardCheck !== undefined && doWizardCheck === true) {
            if (selectionListResult !== undefined && selectionListResult !== null) {
                return true;
            }else {
                return false;
            }
        } else {
            if (selectionListResult !== undefined && selectionListResult !== null) {
                return true;
            } else {
                return false;
            }
        }
    }
    function handleRawSelectionListDataResolve() {
        doSelectionResolve(globals.selectionListDfd, JSON.parse(JSON.stringify(selectionListResult)));
        //selectionListResult = null;
    }


    return globals.selectionListDfd.promise();
}

/**
* Retrieves the vehicles to be displayed in the selection list. Will atempt to reteieve locally first and then online if no records found.
* @method getSelectionList
* @param {String} application The applcation of the vehicle(s) to be retrieved. 
* @param {String} vehicleType The vehicle type of the vehicle(s) to be retrieved.
* @return {Object} Returns data for selection.
*/
function doGetSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range, dfd, forceOnline) {
    //var dfd = $.Deferred();
    // var results;
        



    if (application === undefined) {
        application = '';
    }
    if (vehicleType === undefined) {
        vehicleType = '';
    }
    if (vehicleTypeCode === undefined) {
        vehicleTypeCode = '';
    }
    if (axleLayout === undefined) {
        axleLayout = '';
    }
    if (make === undefined) {
        make = '';
    }
    if (range === undefined) {
        range = '';
    }
    if (typeof forceOnline !== 'boolean') {
        forceOnline = false;
    }

    //var request = 'offers/newoffers?application=' + application + '&vehicleType=' + vehicleType + '&pageNumber=1&pageSize=30';
    var request = 'offers/newoffers?application=' + application +
                    '&vehicleType=' + vehicleType +
                    '&vehicleTypeCode=' + vehicleTypeCode +
                    '&axleLayout=' + axleLayout +
                    '&make=' + make +
                    '&range=' + range +
                    '&pageNumber=1&pageSize=30';


    handleDataRequest(function () { return localDL.getSelectionListLocal(application, vehicleType, vehicleTypeCode, axleLayout, make, range); }, request, OPERATION.GetSelectionList, forceOnline)
        .then(function (data) {
            const responseError = selectionResponseError(data, ['Offers']);
            if (responseError) { dfd.reject(responseError); return; }
            //if (globals.user.getNumberOfOffersFromServer() === 0 || data.Offers.length === globals.user.getNumberOfOffersFromServer()) {
            if (data.Offers.length === globals.user.getNumberOfOffersFromServer()) {
                doSelectionResolve(dfd, data);
            } else {
                doGetSelectionList(application, vehicleType, vehicleTypeCode, axleLayout, make, range, dfd, true);
            }
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doSelectionResolve(dfd, theData) {
    dfd.resolve({ selection: theData });
}

// function mapDataFromJS(cachedData) {
//     var mapDataDfd = $.Deferred();

//     function getData() {
//         var temp = ko.mapping.fromJS(cachedData);
//         return mapDataDfd.resolve(temp);
//     }
//     //Timeout required to show spinner on selection page when data has been cached
//     window.setTimeout(getData, 50);

//     return mapDataDfd.promise();
// }

/**
* Retrieves the default offer details for any vehicle displayed in selection list.
* @method getUserSettings        
* @return {Object} Returns data containing user settings from user licence
*/
let userSettingsRequest = null, userSettingsReady = null;
const userSettingsLocalSaves = new Set();
function settingsContext() {
    return { owner: security.getIncarnation(), revision: security.getUserContextRevision(), proof: security.captureProof(), user: globals.user,
        username: globals.user.getUserName(), instance: globals.user.getUserLicenceInstanceId(), cdg: globals.user.getCdg(), storage: globals.getStorageKey() };
}
function settingsContextMatches(context) {
    return security.incarnationMatches(context.owner) && context.revision === security.getUserContextRevision() && security.isCurrent(context.proof) && context.user === globals.user &&
        context.username === globals.user.getUserName() && context.instance === globals.user.getUserLicenceInstanceId() && context.cdg === globals.user.getCdg() && context.storage === globals.getStorageKey();
}
function isCalculationContextCurrent(context) {
    return !!context && settingsContextMatches(context) && !!context.dashboard &&
        context.dashboard === globals.user.getDashboardConfiguration();
}
function settingsUnavailable(error = {}) {
    error = error || {};
    return { ...error, settingsUnavailable: true, retryable: !error.authenticationFailed && !error.permissionDenied,
        errorMessage: 'Calculation settings unavailable' };
}
function validateUserSettings(data) {
    const settings = data?.UserSettings, dashboard = settings?.DashboardConfiguration;
    const positiveId = value => Number.isInteger(value) && value > 0;
    if (!(data?.Result?.ReturnCode > 0) || !settings || !positiveId(settings.DefaultLegislationId) ||
        !positiveId(settings.DefaultMeasurementSystemId) || !dashboard || !positiveId(dashboard.Id)) return false;
    // The server/constructor permit omitted or null lists. Preserve that contract.
    return [['DashboardLegislations', ['LegislationId', 'CountryId']], ['DashboardDataPoints', ['DataPointId']],
        ['InternalStandards', ['DataPointId']]].every(([key, ids]) => dashboard[key] == null ||
        (Array.isArray(dashboard[key]) && dashboard[key].every(item => item && ids.every(id => positiveId(item[id])))));
}

// Credential renewal deliberately does not imply that the user model is ready.
// Join startup/reload/save work, then hydrate settings for this exact authority.
function ensureCalculationContext() {
    const dfd = $.Deferred(), owner = security.getIncarnation();
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    const timer = setTimeout(() => dfd.reject(settingsUnavailable({ timeout: true })), 20000);
    dfd.always(() => { clearTimeout(timer); release(); });
    function waitForPendingContext() {
        if (dfd.state() !== 'pending' || !security.incarnationMatches(owner)) return dfd.reject({ cancelled: true });
        // Server acknowledgement can remain pending while valid local work is
        // queued offline. Only unfinished local saves belong in this barrier.
        const pending = [globals.dmLoginDfd, globals.refreshDfd,
            ...Array.from(userSettingsLocalSaves).filter(save => settingsContextMatches(save.context)).map(save => save.dfd)]
            .filter(work => work?.state?.() === 'pending');
        if (pending.length) return $.when(...pending).then(waitForPendingContext).fail(error => dfd.reject(settingsUnavailable(error)));
        const context = settingsContext();
        if (!context.username || !(context.instance > 0) || !(context.cdg > 0) ||
            !Array.isArray(globals.user.getAvailableLegislations()) || !globals.user.getAvailableLegislations().length)
            return dfd.reject(settingsUnavailable());
        const dashboard = globals.user.getDashboardConfiguration();
        if (!userSettingsRequest && userSettingsReady && settingsContextMatches(userSettingsReady.context) &&
            dashboard && dashboard === userSettingsReady.dashboard) return dfd.resolve({ ...context, dashboard });
        getUserSettings().then(() => {
            if (dfd.state() !== 'pending') return;
            if (!settingsContextMatches(context)) return dfd.reject({ cancelled: true });
            if (!globals.user.getDashboardConfiguration()) return dfd.reject(settingsUnavailable());
            dfd.resolve({ ...context, dashboard: globals.user.getDashboardConfiguration() });
        }).fail(error => dfd.reject(error));
    }
    security.ensureFresh().then(waitForPendingContext).fail(error => dfd.reject(error));
    return dfd.promise();
}

// Capture pending preference intent before a server GET. Its acknowledgement
// may complete while that older GET is in flight, so keep this snapshot for the
// read. A later local edit cancels the read through saveUserSettings.
function readPendingUserSettings(context, current) {
    const dfd = $.Deferred();
    function read() {
        if (!current()) return dfd.reject({ cancelled: true });
        const pending = Array.from(userSettingsLocalSaves).filter(save => settingsContextMatches(save.context) && save.dfd.state() === 'pending');
        if (pending.length) return $.when(...pending.map(save => save.dfd)).then(read).fail(error => dfd.reject(error));
        localDL.checkIfSyncItems(sessionCacheOwner(context.owner)).then(jobs => {
            if (!current() || !currentSyncItemsOwner(jobs, context.owner)) return dfd.reject({ cancelled: true });
            const queued = jobs.filter(job => job?.Operation?.Code === OPERATION.SaveUserSettings.Code && job.Details && typeof job.Details === 'object')
                .sort((a, b) => Number(b.TimeStamp) - Number(a.TimeStamp))[0];
            dfd.resolve(queued ? JSON.parse(JSON.stringify(queued.Details)) : null);
        }).fail(error => dfd.reject(error));
    }
    read();
    return dfd.promise();
}

function getUserSettings(isCurrentContext) {
    const context = settingsContext();
    if (!isCurrentContext && userSettingsRequest && settingsContextMatches(userSettingsRequest.context)) return userSettingsRequest.dfd.promise();
    if (userSettingsRequest) userSettingsRequest.dfd.reject({ cancelled: true });
    const dfd = $.Deferred(), request = { context, dfd, ready: userSettingsReady };
    userSettingsRequest = request;
    globals.userSettingsDfd = dfd;
    userSettingsReady = null;
    const current = () => dfd.state() === 'pending' && settingsContextMatches(context) && (!isCurrentContext || isCurrentContext());
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    const timer = setTimeout(() => dfd.reject(settingsUnavailable({ timeout: true })), 15000);
    dfd.always(() => {
        clearTimeout(timer); release();
        if (userSettingsRequest === request) userSettingsRequest = null;
        if (globals.userSettingsDfd === dfd) globals.userSettingsDfd = null;
    });

    var uniqueRequest = 'usersettings/get';
    var operation = OPERATION.GetUserSettings;
    
    readPendingUserSettings(context, current).then(pending => { if (current()) doRequest(globals.isOnline.value, pending); else dfd.reject({ cancelled: true }); })
        .fail(error => dfd.reject(error));

    function doRequest(forceOnline, pending) {
        handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.UserSettings); }, uniqueRequest, operation, forceOnline, undefined, current)
        .then(function (data) {
            if (dfd.state() !== 'pending') return;
            if (!current()) return dfd.reject({ cancelled: true });
            try {
            // Only preferences are overlaid. Business result, identity and grants
            // remain authoritative; never modify the retained queue envelope.
            if (pending) data = { ...data, UserSettings: { ...data.UserSettings, ...pending,
                ...(Object.prototype.hasOwnProperty.call(pending, 'ReportLogoToSave') ? { ReportLogo: pending.ReportLogoToSave } : {}) } };
            if (!validateUserSettings(data)) return dfd.reject(settingsUnavailable());
            //any custom pre-processing of data should be handled here
            if (globals.user.isTrialUser()) {
                data.UserSettings.ReportLogo = null;
            }
            const userData = {
                "activeLegislationId": data.UserSettings.DefaultLegislationId,
                "activeMeasurementSystemConfigurationId": data.UserSettings.DefaultMeasurementSystemId,
                "lengthIncrement": data.UserSettings.DefaultLengthIncrement,
                "massIncrement": data.UserSettings.DefaultMassIncrement,
                "percentageIncrement": data.UserSettings.DefaultPercentageIncrement,
                "colourDrawings": data.UserSettings.ColourDrawings,
                "showComplianceScorecard": data.UserSettings.ShowComplianceScorecard,
                "specifyWheelbaseAs": data.UserSettings.DefaultSpecifyWheelbaseAs,
                "specifyCabDimensionsAs": data.UserSettings.DefaultSpecifyCabDimensionsAs,
                "specifyChassisLengthAs": data.UserSettings.DefaultSpecifyChassisLengthAs,
                "specifyFuelConsumptionAs": data.UserSettings.DefaultSpecifyFuelConsumptionAs,
                "specifyLicencingRegionAs": data.UserSettings.DefaultSpecifyLicencingRegionAs,
                "specifyAxleRatingAs": data.UserSettings.DefaultSpecifyAxleRatingAs,
                "defaultReportPdfPageSize": data.UserSettings.DefaultReportPdfPageSize,
                "reportLogo": data.UserSettings.ReportLogo,
                "dashboardConfiguration": data.UserSettings.DashboardConfiguration,
                "showTips": data.UserSettings.ShowTips,
                "reportViewLayout": data.UserSettings.ReportViewLayout,
                "reportViews": data.UserSettings.ReportViews,
                "nteaUserValidationType": data.UserSettings.NteaUserValidationType,
                "validateLeftRight": data.UserSettings.ValidateLeftRight,
                ...(pending ? {
                    // Integration save deltas are not admitted integration definitions.
                    // Keep those queued operations for their ordinary server response.
                    companyAdditionalNote: pending.AdditionalNote,
                    specifyBodyPositionAs: pending.SpecifyBodyPositionAs, specifyEquipmentPositionAs: pending.SpecifyEquipmentPositionAs,
                    reportPdfStamps: pending.ReportPdfStamps,
                    reportPdfSignatures: typeof pending.ReportPdfSignatures === 'string' ? JSON.parse(pending.ReportPdfSignatures) : pending.ReportPdfSignatures
                } : {})
            };
            // Cache the effective preferences, not the older server projection.
            // This also preserves the choice after a later queue acknowledgement.
            const cacheReady = pending || forceOnline ? localDL.addToCache(operation, PROPERTY.UserSettings, data, sessionCacheOwner(context.owner, current)) : $.Deferred().resolve();
            cacheReady.then(() => {
                if (!current()) return dfd.reject({ cancelled: true });
                try {
                    globals.user.updateUser(userData);
                    userSettingsReady = { context, dashboard: globals.user.getDashboardConfiguration() };
                    dfd.resolve({ userSettings: data.UserSettings });
                } catch { dfd.reject(settingsUnavailable()); }
            }).fail(error => dfd.reject(settingsUnavailable(error)));
            } catch { dfd.reject(settingsUnavailable()); }
        })
        .fail(function (errorMessage) {
            dfd.reject(settingsContextMatches(context) ? settingsUnavailable(errorMessage) : { cancelled: true });
            
        });
    }

    return dfd.promise();
}

/**
* Retrieves the default offer details for any vehicle displayed in selection list.
* @method getNewOfferDetails
* @param {Number} vehicleId The id of the vehicle the offer relates to. 
* @param {Number} vehicleDistributionOptionId The id of the vehicle options distribution associated with the offer.
* @param {Number} vehicleDistributionGroupId The id of the distribution group the vehicle relating to the offer is associated with.
* @return {Object} Returns data for a new offer except specification and advantages.
*/
function getNewOfferDetails(vehicleId, vehicleDistributionOptionId, vehicleDistributionGroupId, isOpeningCurrent) {
    var dfd = $.Deferred();
    const openingOwner = security.getIncarnation();
    const openingCurrent = () => security.incarnationMatches(openingOwner) && (!isOpeningCurrent || isOpeningCurrent());
    if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: 'Session changed' }).promise();
    selectedVehicleId = vehicleId;
    var uniqueRequest = 'offers/newoffer?vehicleId=' + vehicleId + '&vehicleDistributionOptionId=' + vehicleDistributionOptionId + '&vehicleDistributionGroupId=' + vehicleDistributionGroupId;
    var operation = OPERATION.GetNewOfferDetails;
    log("New Offer Opened", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
    handleDataRequest(function () { return localDL.getFromCache(operation, vehicleId); }, uniqueRequest, operation, false)
        .then(function (data, doReplace) {
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
            //any custom pre-processing of data should be handled here

            //decompress any graphic files
            if(utils.itemHasSideViewGraphic(data.Offer.Configuration)) {
                utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleDrawing).then(function (sideElevationXmlDoc) {
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
                    data.Offer.Configuration.DecompressedVehicleDrawing = sideElevationXmlDoc;
                    if(utils.itemHasTopViewGraphic(data.Offer.Configuration)) {
                        utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleTopViewDrawing).then(function (topElevationXmlDoc) {
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
                            data.Offer.Configuration.DecompressedVehicleTopViewDrawing = topElevationXmlDoc;
                            dfd.resolve({ offer: data });
                        });
                    }else {
                        dfd.resolve({ offer: data });
                    }
                }).catch(function (){
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
                    dfd.resolve({ offer: data });
                });
            }else if(utils.itemHasTopViewGraphic(data.Offer.Configuration)) {
                utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleTopViewDrawing).then(function (topElevationXmlDoc) {
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
                    data.Offer.Configuration.DecompressedVehicleTopViewDrawing = topElevationXmlDoc;
                    dfd.resolve({ offer: data });
                });
            }else {
                dfd.resolve({ offer: data });
            }

            // if (doReplace === true) {
            //     dfd.resolve({ offer: data });
            // } else {
            //     dfd.resolve({ offer: data });
            // }
            
        })
        .fail(function (errorMessage) {
            if (!openingCurrent()) return dfd.reject({ cancelled: true, errorMessage: "Session changed" });
            dfd.reject(errorMessage);
        });
                
    return dfd.promise();
}

/**
* Retrieves the specification and advantages details for any vehicle displayed in selection list or for an existing offer if the existingOfferId param is supplied.
* @method getOfferSpecificationDetails
* @param {Number} vehicleId The id of the vehicle the specification relates to. 
* @param {Number} vehicleDistributionOptionId The id of the vehicle options distribution associated with the offer.
* @param {Number} vehicleDistributionGroupId The id of the distribution group the vehicle relating to the offer is associated with.
* @param {Number} languageId The id of the language the offer is presented in.
* @param {Number} currencySymbol The currency symbol.
* @param {Number} existingOfferId The id of the offer in the case that it is a saved offer and not a new offer the specification is being retrieved for. Used to determine 
* which op to implement and also as a DB key depending on the op.
* @return {Object} Returns specification and advantages data for a new offer or existing offer.
*/
function getOfferSpecificationDetails(vehicleId, vehicleDistributionOptionId, vehicleDistributionGroupId, languageId, currencySymbol, existingOfferId, competitor1Id, competitor2Id) {

    var dfd = $.Deferred(),
        competitor1VehicleId = competitor1Id !== undefined ? competitor1Id : 0,
        competitor2VehicleId = competitor2Id !== undefined ? competitor2Id : 0;

    var request = 'offers/specification?vehicleId=' + vehicleId + '&vehicleDistributionOptionId=' + vehicleDistributionOptionId +
        '&vehicleDistributionGroupId=' + vehicleDistributionGroupId + '&languageId=' + languageId + '&currencySymbol=' + currencySymbol + 
        '&competitor1VehicleId=' + competitor1VehicleId + '&competitor2VehicleId=' + competitor2VehicleId;
    
    var operation, key;
    if (existingOfferId !== undefined) {
        operation = OPERATION.GetExistingOfferSpecificationDetails;
        key = existingOfferId;
    } else {
        operation = OPERATION.GetNewOfferSpecificationDetails;
        key = vehicleId;
    }
        

    handleDataRequest(function () { return localDL.getFromCache(operation, key); }, request, operation, false)
        .then(function (data, doReplace) {
            //any custom pre-processing of data should be handled here
            if (doReplace === true) {
                var promises = [];
                replaceVehicleSpecificationImageURLs(data.Specification.BaseVehicle, promises);
                for (var i = 0; i < data.Specification.CompetitorVehicles.length; i++) {
                    replaceVehicleSpecificationImageURLs(data.Specification.CompetitorVehicles[i], promises);
                }
                $.when.apply($, promises)
                    .done(function () {
                        dfd.resolve({ offer: data });
                    });
            } else {
                if (operation === OPERATION.GetExistingOfferSpecificationDetails) {
                    localDL.addToCache(operation, existingOfferId, data)
                        .then(function () {
                            existingSpecDataCached.value = true;
                        });
                }
                dfd.resolve({ offer: data });
            }



        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Retrieves existing offer details for any saved offer.
* @method getExistingOfferDetails
* @param {Number} offerId The id of the saved offer to be retrieved. 
* @param {Number} ownerId The id of the user or company assocaited with the offer.
* @param {Number} ownerType The type of owner association ie. USER or COMPANY
* @return {Object} Returns data for an existing/saved offer except specification and advantages.
*/
function getExistingOfferDetails(offerId, ownerId, ownerType, isSharedOffer) {

    var dfd = $.Deferred();
    //this transaction cannot be used offline at the moment
    var offlineUses = 0;

    var request = 'offers/existingoffer?offerId=' + offerId + '&ownerId=' + ownerId + '&ownerType=' + ownerType + '&offlineUses=' + offlineUses;
    var operation;
    var localFunc = null;
    var forceOnline = true;
    if (isSharedOffer === true) {
        operation = OPERATION.GetSharedOfferDetails.Code;
        forceOnline = true;
        request += '&isSharedOffer=true';
    } else {
        operation = OPERATION.GetExistingOfferDetails;
        //localFunc = function () { return localDL.getFromCache(operation, offerId); };
    }
    
    log("Existing Offer Opened", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
    handleDataRequest(localFunc, request, operation, forceOnline)
        .then(function (data, doReplace) {
            if(data.Result.ReturnCode > 0) {
                let decompressionPromises = [];
                if(data.Changes.Trailers.length > 0) {
                    data.Changes.Trailers.forEach(function (trailer) { 
                        decompressionPromises.push(decompressAnyGraphicFilesWithPromise(trailer));
                        // Object.keys(trailer.Accessories).forEach(function (key) {
                        //     if(trailer.Accessories[key].length > 0) {
                        //         trailer.Accessories[key].forEach(function (trailerItem) {
                        //             decompressionPromises.push(decompressAnyGraphicFilesWithPromise(trailerItem));
                        //         });
                        //     }
                        // });
                    });
                }
                if(data.Changes.Payloads.length > 0) {
                    data.Changes.Payloads.forEach(function (payload) { 
                        decompressionPromises.push(decompressAnyGraphicFilesWithPromise(payload));
                    });
                }
                Object.keys(data.Changes.Accessories).forEach(function (key) {
                    if(data.Changes.Accessories[key] && data.Changes.Accessories[key].length > 0) {
                        data.Changes.Accessories[key].forEach(function (item) {
                            decompressionPromises.push(decompressAnyGraphicFilesWithPromise(item));
                        });
                    }
                });
                $.when.apply($, decompressionPromises)
                .done(function () {
                    if(utils.itemHasSideViewGraphic(data.Offer.Configuration)) {
                        utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleDrawing).then(function (sideElevationXmlDoc) {
                            data.Offer.Configuration.DecompressedVehicleDrawing = sideElevationXmlDoc;
                            if(utils.itemHasTopViewGraphic(data.Offer.Configuration)) {
                                utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleTopViewDrawing).then(function (topElevationXmlDoc) {
                                    data.Offer.Configuration.DecompressedVehicleTopViewDrawing = topElevationXmlDoc;
                                    dfd.resolve({ offer: data });
                                });
                            }else {
                                dfd.resolve({ offer: data });
                            }
                        }).catch(function (error){
                            dfd.resolve({ offer: data });
                        });
                    }else if(utils.itemHasTopViewGraphic(data.Offer.Configuration)) {
                        utils.translateGraphicToXmlDoc(data.Offer.Configuration.VehicleTopViewDrawing).then(function (topElevationXmlDoc) {
                            data.Offer.Configuration.DecompressedVehicleTopViewDrawing = topElevationXmlDoc;
                            dfd.resolve({ offer: data });
                        });
                    }else {
                        dfd.resolve({ offer: data });
                    }
                })
                .fail(function (error) {
                    dfd.reject(error);
                });
            } else {
                dfd.reject({ 
                    Result: data.Result,
                    DeletedByInfo: data.DeletedByInfo,
                    calculationUnavailable: true
                });
            }
            //any custom pre-processing of data should be handled here
            // if (doReplace === true) {
            //     dfd.resolve({ offer: data });
            // } else {
            //     dfd.resolve({ offer: data });
            // }
            
            


        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });


    return dfd.promise();

}

function equipmentStubsRequest(background, cached, load, resolve, canProceed = () => true) {
    const dfd = $.Deferred(), owner = security.getIncarnation();
    let allowed = true, started = false, stops = [];
    const current = () => allowed && security.incarnationMatches(owner) && canProceed();
    const cleanup = () => { stops.splice(0).forEach(stop => stop()); };
    dfd.fail(() => { allowed = false; });
    dfd.always(cleanup);
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    dfd.always(release);
    function resume() {
        if (dfd.state() !== 'pending') return;
        if (!current()) { dfd.reject({ cancelled: true }); return; }
        const data = cached();
        if (data !== undefined && data !== null) {
            resolve(dfd, JSON.parse(JSON.stringify(data)));
        } else if (!started && !background.value && !globals.isSyncing.value) {
            started = true;
            cleanup();
            load(dfd, false, current);
        }
    }
    if (dfd.state() === 'pending') {
        stops = [watch(background, resume), watch(globals.isSyncing, resume)];
        resume();
    }
    return dfd.promise();
}

function getBodyStubs(canProceed) {
    return equipmentStubsRequest(doingBackgroundBodiesRetrieval, () => bodiesResult, doGetBodies, doBodiesResolve, canProceed);
}

function doGetBodies(dfd, forceRemote, current = () => true) {
    if (!current()) { dfd.reject({ cancelled: true }); return; }
    var request = 'accessories/allbodystubs';
    if (typeof forceRemote !== 'boolean') {
        forceRemote = false;
    }

    handleDataRequest(function () { return localDL.getBodiesLocal(); }, request, OPERATION.GetBodyStubs, forceRemote, undefined, current)
    .then(function (data) {
        if (!current()) { dfd.reject({ cancelled: true }); return; }
        if (data.Bodies.length === globals.user.getNumberOfBodiesFromServer()) {
            //any custom pre-processing of data should be handled here
            doBodiesResolve(dfd, data);
        } else {
            doGetBodies(dfd, true, current);
        }
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doBodiesResolve(dfd, theData) {
    dfd.resolve({ bodies: theData });
}

function getBody(bodyId, source, accessLevel = config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL) {
    var dfd = $.Deferred();

    var request = 'accessories/bodydetail?bodyId=' + bodyId + '&source=' + source + '&accessLevel=' + accessLevel;
    var forceOnline = preventCaching;
    if (accessLevel === config.CUSTOM_ITEM_ACCESS_LEVEL.COMPANY) {
        forceOnline = true;
    }

    handleDataRequest(function () { return localDL.getFromCache(OPERATION.GetBody, bodyId + '_' + config.ACCESSORY_TYPES.BODY + '_' + source); }, request, OPERATION.GetBody, forceOnline)
        .then(function (data) {
            if (!equipmentDetailAvailable(data, 'Body')) { settleEquipmentDetailFailure(dfd, data); return; }
            decompressAnyGraphicFilesAndResolve(data.Body, function() { dfd.resolve(data); });
            // dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getAccessoryStubs(canProceed) {
    return equipmentStubsRequest(doingBackgroundAccessoriesRetrieval, () => accessoriesResult, doGetAccessories, doAccessoriesResolve, canProceed);
}

function doGetAccessories(dfd, forceRemote, current = () => true) {
    if (!current()) { dfd.reject({ cancelled: true }); return; }
    var request = 'accessories/allaccessorystubs';
    if (typeof forceRemote !== 'boolean') {
        forceRemote = false;
    }

    handleDataRequest(function () { return localDL.getAccessoriesLocal(); }, request, OPERATION.GetAccessoryStubs, forceRemote, undefined, current)
        .then(function (data) {
        if (!current()) { dfd.reject({ cancelled: true }); return; }
            if (data.Accessories.length === globals.user.getNumberOfAccessoriesFromServer()) {
                //any custom pre-processing of data should be handled here
                doAccessoriesResolve(dfd, data);
            } else {
                doGetAccessories(dfd, true, current);
            }
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doAccessoriesResolve(dfd, theData) {
    dfd.resolve({ accessories: theData });
}

function getAccessory(accessoryId, accessoryType, source, accessLevel = config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL) {
    var dfd = $.Deferred();

    var request = 'accessories/accessorydetail?accessoryId=' + accessoryId + '&accessoryType=' + accessoryType + '&source=' + source + '&accessLevel=' + accessLevel;
    var forceOnline = preventCaching;
    if (accessLevel === config.CUSTOM_ITEM_ACCESS_LEVEL.COMPANY) {
        forceOnline = true;
    }


    handleDataRequest(function () { return localDL.getFromCache(OPERATION.GetAccessory, accessoryId + '_' + accessoryType + '_' + source); }, request, OPERATION.GetAccessory, forceOnline)
        .then(function (data) {
            if (!equipmentDetailAvailable(data, 'Accessory')) { settleEquipmentDetailFailure(dfd, data); return; }
            decompressAnyGraphicFilesAndResolve(data.Accessory, function() { doResolve(data); });
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();

    function doResolve(data) {
        dfd.resolve(data);
    }
}

function decompressAnyGraphicFilesAndResolve(objectWithGraphics, resolveFunc) {
    //decompress any graphic files
    if(objectWithGraphics && utils.itemHasSideViewGraphic(objectWithGraphics)) {
        utils.translateGraphicToXmlDoc(objectWithGraphics.GraphicBlob || objectWithGraphics.Graphic).then(function (sideElevationXmlDoc) {
            objectWithGraphics.DecompressedGraphicBlob = sideElevationXmlDoc;
            if(utils.itemHasTopViewGraphic(objectWithGraphics)) {
                utils.translateGraphicToXmlDoc(objectWithGraphics.TopViewGraphicBlob).then(function (topElevationXmlDoc) {
                    objectWithGraphics.DecompressedTopViewGraphicBlob = topElevationXmlDoc;
                    resolveFunc();
                }).catch(function (){
                    resolveFunc();
                });
            }else {
                resolveFunc();
            }
        }).catch(function (){
            resolveFunc();
        });
    }else if(objectWithGraphics && utils.itemHasTopViewGraphic(objectWithGraphics)) {
        utils.translateGraphicToXmlDoc(objectWithGraphics.TopViewGraphicBlob).then(function (topElevationXmlDoc) {
            objectWithGraphics.DecompressedTopViewGraphicBlob = topElevationXmlDoc;
            resolveFunc();
        }).catch(function (){
            resolveFunc();
        });
    }else {
        resolveFunc();
    }
}

function decompressAnyGraphicFilesWithPromise(objectWithGraphics) {
    let dfd = $.Deferred()

    //decompress any graphic files
    if(objectWithGraphics && utils.itemHasSideViewGraphic(objectWithGraphics)) {
        utils.translateGraphicToXmlDoc(objectWithGraphics.GraphicBlob || objectWithGraphics.Graphic).then(function (sideElevationXmlDoc) {
            objectWithGraphics.DecompressedGraphicBlob = sideElevationXmlDoc;
            if(utils.itemHasTopViewGraphic(objectWithGraphics)) {
                utils.translateGraphicToXmlDoc(objectWithGraphics.TopViewGraphicBlob).then(function (topElevationXmlDoc) {
                    objectWithGraphics.DecompressedTopViewGraphicBlob = topElevationXmlDoc;
                    dfd.resolve();
                }).catch(function (){
                    dfd.resolve();
                });
            }else {
                dfd.resolve();
            }
        }).catch(function (){
            dfd.resolve();
        });
    }else if(objectWithGraphics && utils.itemHasTopViewGraphic(objectWithGraphics)) {
        utils.translateGraphicToXmlDoc(objectWithGraphics.TopViewGraphicBlob).then(function (topElevationXmlDoc) {
            objectWithGraphics.DecompressedTopViewGraphicBlob = topElevationXmlDoc;
            dfd.resolve();
        }).catch(function (){
            dfd.resolve();
        });
    }else {
        dfd.resolve();
    }

    return dfd.promise();
}

function getTrailerStubs(canProceed) {
    return equipmentStubsRequest(doingBackgroundTrailersRetrieval, () => trailersResult, doGetTrailers, doTrailersResolve, canProceed);
}

function doGetTrailers(dfd, forceRemote, current = () => true) {
    if (!current()) { dfd.reject({ cancelled: true }); return; }
    var request = 'accessories/alltrailerstubs';
    if (typeof forceRemote !== 'boolean') {
        forceRemote = false;
    }

    handleDataRequest(function () { return localDL.getTrailersLocal(); }, request, OPERATION.GetTrailerStubs, forceRemote, undefined, current)
    .then(function (data) {
        if (!current()) { dfd.reject({ cancelled: true }); return; }
        if (data.Trailers.length === globals.user.getNumberOfTrailersFromServer()) {
            //any custom pre-processing of data should be handled here
            doTrailersResolve(dfd, data);
        } else {
            doGetTrailers(dfd, true, current);
        }
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doTrailersResolve(dfd, theData) {
    dfd.resolve({ trailers: theData });
}

function getTrailer(trailerId, source, accessLevel = config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL) {
    var dfd = $.Deferred();

    var request = 'accessories/trailerdetail?trailerId=' + trailerId + '&source=' + source + '&accessLevel=' + accessLevel;
    var forceOnline = preventCaching;
    if (accessLevel === config.CUSTOM_ITEM_ACCESS_LEVEL.COMPANY) {
        forceOnline = true;
    }

    handleDataRequest(function () { return localDL.getFromCache(OPERATION.GetTrailer, trailerId + '_' + config.ACCESSORY_TYPES.TRAILER + '_' + source); }, request, OPERATION.GetTrailer, forceOnline)
        .then(function (data) {
            if (!equipmentDetailAvailable(data, 'Trailer')) { settleEquipmentDetailFailure(dfd, data); return; }
            if(globals.chassisHasAddedEquipment(data.Trailer)) {    
                let decompressionPromises = [];             
                decompressionPromises.push(decompressAnyGraphicFilesWithPromise(data.Trailer));
                Object.keys(data.Trailer.Accessories).forEach(function (key) {
                    if(data.Trailer.Accessories[key]) {
                        if(data.Trailer.Accessories[key].length && data.Trailer.Accessories[key].length > 0) {
                            data.Trailer.Accessories[key].forEach(function (trailerItem) {
                                decompressionPromises.push(decompressAnyGraphicFilesWithPromise(trailerItem));
                            });
                        }else {
                            decompressionPromises.push(decompressAnyGraphicFilesWithPromise(data.Trailer.Accessories[key]));
                        }
                    }
                    
                });        
                $.when.apply($, decompressionPromises)
                    .done(function () {
                        dfd.resolve(data);
                    })
                    .fail(function (error) {
                        dfd.reject(error);
                    });        
            }else {
                decompressAnyGraphicFilesAndResolve(data.Trailer, function() { dfd.resolve(data); });
            }
            
            // dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getPayloadStubs(canProceed) {
    return equipmentStubsRequest(doingBackgroundPayloadsRetrieval, () => payloadsResult, doGetPayloads, doPayloadsResolve, canProceed);
}

function doGetPayloads(dfd, forceRemote, current = () => true) {
    if (!current()) { dfd.reject({ cancelled: true }); return; }
    var request = 'accessories/allpayloadstubs';
    if (typeof forceRemote !== 'boolean') {
        forceRemote = false;
    }

    handleDataRequest(function () { return localDL.getPayloadsLocal(); }, request, OPERATION.GetPayloadStubs, forceRemote, undefined, current)
    .then(function (data) {
        if (!current()) { dfd.reject({ cancelled: true }); return; }
        if (data.Payloads.length === globals.user.getNumberOfPayloadsFromServer()) {
            //any custom pre-processing of data should be handled here
            doPayloadsResolve(dfd, data);
        } else {
            doGetPayloads(dfd, true, current);
        }
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doPayloadsResolve(dfd, theData) {
    dfd.resolve({ payloads: theData });
}

function getPayload(payloadId, source, accessLevel = config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL) {
    var dfd = $.Deferred();

    var request = 'accessories/payloaddetail?payloadId=' + payloadId + '&source=' + source + '&accessLevel=' + accessLevel;
    var forceOnline = preventCaching;
    if (accessLevel === config.CUSTOM_ITEM_ACCESS_LEVEL.COMPANY) {
        forceOnline = true;
    }

    handleDataRequest(function () { return localDL.getFromCache(OPERATION.GetPayload, payloadId + '_' + config.ACCESSORY_TYPES.PAYLOAD + '_' + source); }, request, OPERATION.GetPayload, forceOnline)
        .then(function (data) {
            if (!equipmentDetailAvailable(data, 'Payload')) { settleEquipmentDetailFailure(dfd, data); return; }
            decompressAnyGraphicFilesAndResolve(data.Payload, function() { dfd.resolve(data); });
            // dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getSharedOfferStubs(sharerUserId, sharedWithUserId, canProceed = () => true) {
    var dfd = $.Deferred();

    if (!canProceed()) return dfd.reject({ cancelled: true }).promise();
    // Existing shared-link callers supply decimal query-string IDs.
    if (![sharerUserId, sharedWithUserId].every(id =>
        (typeof id === 'number' || (typeof id === 'string' && /^\d+$/.test(id))) &&
        Number.isInteger(Number(id)) && Number(id) >= 0 && Number(id) <= 2147483647)) {
        return dfd.reject({ errorMessage: 'Invalid selection response', malformedResponse: true }).promise();
    }
    sharerUserId = Number(sharerUserId); sharedWithUserId = Number(sharedWithUserId);
    // The background result is the entire ordinary inbox, not a cache keyed by
    // sharer/recipient. A drilled-down or support listing needs its own result,
    // including an authoritative empty result, under the current operation.
    if (sharerUserId !== 0 || sharedWithUserId !== 0) {
        const owner = security.getIncarnation();
        doGetSharedOfferStubs(sharerUserId, sharedWithUserId, dfd, error => security.incarnationMatches(owner) && canProceed(error));
        return dfd.promise();
    }

    if (doingBackgroundSharedOfferStubsRetrieval.value === true || globals.isSyncing.value === true) {
        if (canUseRawSharedOfferStubsData()) {
            handleRawSharedOfferStubsDataResolve();
        } else {
            var doingBackgroundSharedOfferStubsRetrievalSubscriptionRef = watch(doingBackgroundSharedOfferStubsRetrieval, function (newValue) {
                doingBackgroundSharedOfferStubsRetrievalSubscriptionRef();
                doingBackgroundSharedOfferStubsRetrievalSubscriptionRef = null;
                if (canUseRawSharedOfferStubsData()) {
                    handleRawSharedOfferStubsDataResolve();
                } else {
                    if (newValue === false && globals.isSyncing.value === false) {
                        doGetSharedOfferStubs(sharerUserId, sharedWithUserId, dfd);
                    }
                }
            });
            var globalsIsSyncingSubscriptionRef = watch(globals.isSyncing, function (newVal) {
                globalsIsSyncingSubscriptionRef();
                globalsIsSyncingSubscriptionRef = null;
                if (canUseRawSharedOfferStubsData()) {
                    handleRawSharedOfferStubsDataResolve();
                } else {
                    if (newVal === false && doingBackgroundSharedOfferStubsRetrieval.value === false) {
                        doGetSharedOfferStubs(sharerUserId, sharedWithUserId, dfd);
                    }
                }

            });
        }

    } else {
        if (canUseRawSharedOfferStubsData(true)) {
            handleRawSharedOfferStubsDataResolve();
        } else {
            doGetSharedOfferStubs(sharerUserId, sharedWithUserId, dfd);
        }

    }

    function canUseRawSharedOfferStubsData(doWizardCheck) {
        if (doWizardCheck !== undefined && doWizardCheck === true) {
            if (sharedOfferStubsResult !== undefined && sharedOfferStubsResult !== null) {
                return true;
            } else {
                return false;
            }
        } else {
            if (sharedOfferStubsResult !== undefined && sharedOfferStubsResult !== null) {
                return true;
            } else {
                return false;
            }
        }
    }
    function handleRawSharedOfferStubsDataResolve() {
        doSharedOfferStubsResolve(dfd, JSON.parse(JSON.stringify(sharedOfferStubsResult)));
        //selectionListResult = null;
    }


    return dfd.promise();
}

function doGetSharedOfferStubs(sharerUserId, sharedWithUserId, dfd, current = () => true) {
    if (!current()) { dfd.reject({ cancelled: true }); return; }
    
    var request = OPERATION.GetSharedOfferStubs.BaseUrl + '?sharerUserId=' + sharerUserId + '&sharedWithUserId=' + sharedWithUserId;
    var operation = OPERATION.GetSharedOfferStubs.Code;

    handleDataRequest(/*function () { return localDL.getSharedOfferStubsLocal(); }*/null, request, operation, true, undefined, current)
    .then(function (data) {
        if (!current()) { dfd.reject({ cancelled: true }); return; }
        doSharedOfferStubsResolve(dfd, data);
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
}

function doSharedOfferStubsResolve(dfd, theData) {
    dfd.resolve({ sharedOfferStubs: theData });
}

function getSharedOfferStub(offerId) {
    
    
    return localDL.getFromCache(OPERATION.GetSharedOfferStub, offerId);

}

function getVehicleSpecificationAdvantages(securityToken, vehicleId, vehicleDistributionOptionId, vehicleDistributionGroupId) {
    return security.protectedAjax(utils.setOptions('vehiclespecificationadvantages/vehicleadvantages?vehicleId=' + vehicleId + '&vehicleDistributionOptionId=' + vehicleDistributionOptionId + '&vehicleDistributionGroupId=' + vehicleDistributionGroupId, operationTypeGet, dataTypeJson, setQueryHeader, globals.cloudServicesVersion1)).then(data => ({ advantages: data }));
}

function getCountries() {

    var dfd = $.Deferred();

    security.protectedAjax(utils.setOptions('countries/getall', operationTypeGet, dataTypeJson, setQueryHeader, globals.cloudServicesVersion1))
        .then(requestSucceeded)
        .fail(requestFailed);

    // handle the ajax callback
    function requestSucceeded(data, textStatus, jqXHR) {
        var countryList = [];
        data.forEach(function (countryData) {
            countryData.disable = false;
            countryList.push(countryData);
        });
        dfd.resolve(countryList);
    }

    function requestFailed(xhr, ajaxOptions, thrownError) {
        dfd.reject({ errorMessage: xhr.statusText });
    }

    return dfd.promise();

}

/**
 * Send a list of waypoints to Bing Maps Rest API and resolve with the results returned from Bing Maps
 * @param {string} requestString - Should be a list of the waypoints in the format 'wp.' + waypointNumber + '=' + waypoint.getLatitude() + ',' + waypoint.getLongitude()
 * @param {any} credentials - Should be the same as globals.mapCredentials
 */
function sendToRouteRequestBingMapsRestApi(requestString, credentials) {
    var dfd = $.Deferred(),
        urlToUse = config.BING_MAPS_REST_URLS.ROUTES + '?';

    urlToUse += requestString;
    urlToUse += '&ra=routePath';
    // urlToUse += '&ra=routePath&optimize=time';
    // urlToUse += '&ra=routePath&optimize=timeWithTraffic';
    // urlToUse += '&ra=routePath&optimize=time&&dateTime=09:00:00';
    // urlToUse += '&ra=routePath&optimize=time&&dateTime=25/12/2024 00:00:00';

    // let timeToUse = "2025-08-03T00:00:00"
    
    // let dateToUse = new Date(timeToUse)
    // let dateParameter = dateToUse.toISOString()
    // urlToUse += '&ra=routePath&optimize=time&&dateTime=' + dateParameter;
    // let dateTimeDocs="03/08/2025 01:00:00"
    // urlToUse += '&ra=routePath&optimize=timeWithTraffic&&dateTime=' + dateTimeDocs;
    urlToUse += '&key=' + credentials;

    $.ajax({
        url: urlToUse,
        dataType: "jsonp",
        jsonp: "jsonp"
    })
        .then(requestSucceeded)
        .fail(requestFailed);

    // handle the ajax callback
    function requestSucceeded(data, textStatus, jqXHR) {
        if (data.errorDetails !== undefined) {
            var errorDetailsString = 'Error returned from Bing Maps REST API.';
            data.errorDetails.forEach(function (errorString) {
                if (errorDetailsString.charAt(errorDetailsString.length - 1) === '.') {
                    errorDetailsString += ' ';
                } else if (errorDetailsString.charAt(errorDetailsString.length - 1) !== ' ') {
                    errorDetailsString += '. ';
                }
                errorDetailsString += errorString;
            });
            dfd.reject({ errorMessage: errorDetailsString });
        } else {
            dfd.resolve(data);
        }
    }

    function requestFailed(xhr, ajaxOptions, thrownError) {
        dfd.reject({ errorMessage: xhr.statusText });
    }

    return dfd.promise();

}

function postSimulationResults(securityToken, simulationDetails) {
    return security.protectedAjax(utils.setPostOptions('performance/simulationresults', operationTypePost, 'json', simulationDetails, setQueryHeader, globals.cloudServicesVersion1, true, 120000)).then(data => ({ simulation: data }));
}




function getNumVehicleOpensPropertyVal() {
    var dfd = $.Deferred();

    localDL.getFromCache(OPERATION.NumVehicleOpens, PROPERTY.NumVehicleOpens)
        .then(function (numVehOpens) {
            if (numVehOpens === undefined || numVehOpens === null) {
                dfd.resolve(0);
            } else {
                dfd.resolve(numVehOpens);
            }
        });

    return dfd.promise()
}

function incrementNumVehicleOpensProperty() {
    getNumVehicleOpensPropertyVal()
        .then(function (currentNumVehOpensVal) {
            if (currentNumVehOpensVal === undefined || currentNumVehOpensVal === null) {
                localDL.addToCache(OPERATION.NumVehicleOpens, PROPERTY.NumVehicleOpens, 1);
            } else {
                localDL.addToCache(OPERATION.NumVehicleOpens, PROPERTY.NumVehicleOpens, currentNumVehOpensVal+1);
            }
        });
}

function checkEmailVerificationStatusOnline() {
    return makeCallToPostVerificationStatusController(OPERATION.CheckEmailVerificationStatus, { Email: globals.user.getEmailAddress() });
}

function postVerificationCode(code) {
    return makeCallToPostVerificationStatusController(OPERATION.VerifyCode, { Email: globals.user.getEmailAddress(), VerificationCode: code });
}

function makeCallToPostVerificationStatusController(operation, data) {
    var dfd = $.Deferred();

    var request = 'verificationcode/verificationstatus';

    handleDataRequest(null, request, operation, true, data)
        .then(function (emailVerifiedYesOrNo) {
            dfd.resolve(emailVerifiedYesOrNo);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function resendVerificationEmail() {
    var dfd = $.Deferred();

    var request = 'verificationcode/resendverificationemail?emailAddress=' + globals.user.getEmailAddress() + '&cultureCode=' + globals.user.getCultureCode();

    handleDataRequest(null, request, OPERATION.ResendVerificationEmail, true)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function postTransferLicenceVerificationCode(code) {
    var dfd = $.Deferred();

    var request = OPERATION.VerifyTransferLicenceCode.BaseUrl;

    handleDataRequest(null, request, OPERATION.VerifyTransferLicenceCode.Code, true, { Email: globals.user.getEmailAddress(), VerificationCode: code })
        .then(function (transferLicenceCodeVerifiedYesOrNo) {
            dfd.resolve(transferLicenceCodeVerifiedYesOrNo);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function resendTransferLicenceVerificationEmail() {
    var dfd = $.Deferred();

    var request = OPERATION.ResendTransferLicenceVerificationEmail.BaseUrl + '?emailAddress=' + globals.user.getEmailAddress() + '&cultureCode=' + globals.user.getCultureCode();

    handleDataRequest(null, request, OPERATION.ResendTransferLicenceVerificationEmail.Code, true)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function postEmailOffer(emailDetails) {
    var dfd = $.Deferred();

    var request = 'offers/emailoffer';

    handleDataRequest(null, request, OPERATION.PostEmailOffer, true, emailDetails)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Saves a new offer or updates an existing saved offer. Will reflect any changes locally and queue a new sync item if the app is offline or the online save fails. 
* The app will attempt to sync the item next time it goes online. 
* @method saveOffer
* @param {Number} offerId The id of the saved offer. 0 if it's a newly saved offer.
* @param {Number} ownerId The id of the associated owner, could ba user or company id
* @param {String} ownerType The type of the associated ownerId of the saved offer. Could be USER or COMPANY.
* @param {Object} offerObject Payload containing the customisable details of the given offer.
* @param {Object} saveOfferFlags describing the context or save scenario
* @param {Object} shareOptions describing optional info for shared offers
* @return {Object} Returns data supplied in response to successful server call, or dummies the data response if offline. Response data includes relevant offer Id and update counter.
*/
function saveOffer(offerId, ownerId, ownerType, offerObject, saveOfferFlags, shareOptions, folderPath, requireServerConfirmation = false) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();

    const saveOwner = security.getIncarnation();
    const namespace = { username: globals.user.getUserName(), cdg: globals.user.getCdg() };
    const saveCurrent = () => security.incarnationMatches(saveOwner) && namespace.username === globals.user.getUserName() && namespace.cdg === globals.user.getCdg();
    var dfd = $.Deferred();

    
    var timeStamp = nextRetainedWorkTimeStamp();
    var operation = OPERATION.SaveOffer.Code;

    var offerDetailsObject;

    

    offerDetailsObject = {
        Offer: offerObject,
        ownerId: ownerId,
        ownerType: ownerType,
        NotifyShareesofOfferChanges: shareOptions.notifyShareesofOfferChanges,
        OptionalOfferChangesCommentForShareeEmail: shareOptions.optionalOfferChangesCommentForShareeEmail,
        FolderPath: folderPath
    };

    var SAVE_TYPE = {
        NEW: 'new',
        EXISTING: 'existing',
        OVERWRITE: 'overwrite'
    };

    var saveType, key, dataToGet;

    
    var tempOfferId = 'offerId_' + timeStamp;
    equipmentRevisionStarts.set(saveOwner+'|Calculation|'+(offerId||offerObject.Id||tempOfferId),timeStamp);
    equipmentRevisionStarts.set(saveOwner+'|Calculation|'+tempOfferId,timeStamp);
    var offerIdToUse;

    if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_NEW] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_NEW] === true) {
        saveType = SAVE_TYPE.NEW;
        key = offerObject.VehicleId;
        dataToGet = "new";
        offerIdToUse = tempOfferId;
    } if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_SHARE] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_SHARE] === true) {
        saveType = SAVE_TYPE.NEW;
        key = offerObject.Id;
        dataToGet = "share";
        offerIdToUse = tempOfferId;
    } else if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING] === true) {
        saveType = SAVE_TYPE.NEW;
        key = offerObject.Id;
        offerObject.Id = 0;
        offerId = 0;
        dataToGet = "existing";
        offerIdToUse = tempOfferId;
    } else if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_NEW_TSV] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_NEW_TSV] === true) {
        saveType = SAVE_TYPE.NEW;
        //key = offerObject.VehicleId;
        dataToGet = "none";
        offerIdToUse = tempOfferId;
    } else if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_EXISTING_TSV] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_EXISTING_TSV] === true) {
        saveType = SAVE_TYPE.EXISTING;
        dataToGet = "none";
        offerIdToUse = offerId;
    } else if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING_TSV] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.SAVE_AS_EXISTING_TSV] === true) {
        saveType = SAVE_TYPE.NEW;
        dataToGet = "none";
        offerObject.Id = 0;
        offerId = 0;
        offerIdToUse = tempOfferId;

    }else if (offerObject.Id === 0) {
        saveType = SAVE_TYPE.NEW;
        key = offerObject.VehicleId;
        dataToGet = "new";
        offerIdToUse = tempOfferId;
    } else if (saveOfferFlags !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.OVERWRITE] !== undefined && saveOfferFlags[config.SAVE_OFFER_FLAGS.OVERWRITE] === true) {
        saveType = SAVE_TYPE.OVERWRITE;
        if (offerObject.Id > 0) {
            key = offerObject.Id;
            dataToGet = "existing";
        } else {
            key = offerObject.VehicleId;
            dataToGet = "new";
        }
        
        offerIdToUse = offerId;
    } else {

        saveType = SAVE_TYPE.EXISTING;
        key = offerObject.Id;
        dataToGet = "existing";
        offerIdToUse = offerId;
    }
    if (dataToGet === 'none') {
        doOfflineSave(offerDetailsObject, {OfferId:offerIdToUse,VehicleId:offerObject.VehicleId,
            Changes:offerObject,UpdateCounter:offerObject.UpdateCounter||0,Complete:0});
    } else {
        getDataForSave(key, dataToGet)
            .then(function (cachedData) {
                if (cachedData === null || cachedData === undefined) {
                    if (dataToGet === 'existing') {
                        key = offerObject.VehicleId;
                        dataToGet = 'new';
                    } else {
                        key = offerId;
                        dataToGet = 'existing';
                    }
                    getDataForSave(key, dataToGet)
                        .then(function (cachedData) {
                            doActionsAfterRetrievingCachedData(cachedData);
                        }, dfd.reject);
                } else {
                    doActionsAfterRetrievingCachedData(cachedData);
                }


            }, dfd.reject);
    }
    

    function doActionsAfterRetrievingCachedData(cachedData) {
        if (!cachedData?.Offer) { dfd.reject({ localStorageUnavailable: true }); return; }
        var offerSaveObject = {};
        

        if (saveType === SAVE_TYPE.NEW || saveType === SAVE_TYPE.OVERWRITE) {
            offerSaveObject.CachedDate = Date.now();
            if (cachedData !== null && cachedData !== undefined && cachedData.Complete) {
                offerSaveObject.Complete = cachedData.Complete;
            } else {
                offerSaveObject.Complete = 0;
            }
            offerSaveObject.Changes = offerObject;
            offerSaveObject.OfferId = offerIdToUse;
            offerSaveObject.VehicleId = offerObject.VehicleId;
            offerSaveObject.UpdateCounter = 0;
            offerSaveObject.Offer = cachedData.Offer;
            offerSaveObject.Specification = cachedData.Specification;
            offerSaveObject.Advantages = cachedData.Advantages;
        } else {
            cachedData.CachedDate = Date.now();
            cachedData.Changes = offerObject;
            offerSaveObject = cachedData;
        }

        if (offerSaveObject.Changes.VehicleId !== offerSaveObject.Offer.VehicleId) {
            localDL.getFromCache(OPERATION.GetNewOfferDetails, offerSaveObject.Changes.VehicleId)
                .then(function (data) {
                    if (!data?.Offer) { dfd.reject({ localStorageUnavailable: true }); return; }
                    offerSaveObject.Offer = data.Offer;
                    continueWithSave();
                }, dfd.reject);
        } else {
            continueWithSave();
        }

        function continueWithSave() {    
            doOfflineSave(offerDetailsObject, offerSaveObject);
        }
        
    }

    function getDataForSave(key, dataToGet) {
        var dataDfd = $.Deferred();

        if (dataToGet === "new") {
            localDL.getFromCache(OPERATION.GetNewOfferDetails, key)
                .then(function (data) {
                    dataDfd.resolve(data);
                }, dataDfd.reject);
        } else if (dataToGet === "share") {
            localDL.getFromCache(OPERATION.GetSharedOfferStub, key)
                .then(function (data) {
                    dataDfd.resolve(data);
                }, dataDfd.reject);
        } else {
            localDL.getFromCache(OPERATION.GetExistingOfferDetails, key)
                .then(function (data) {
                    dataDfd.resolve(data);
                }, dataDfd.reject);
        }

        return dataDfd.promise();
    }

    function doOfflineSave(offerDetailsObject, localSaveObject) {
        if (!saveCurrent()) { dfd.reject({ cancelled: true }); return; }
        localSaveObject.RetainWork = true;
        localSaveObject.WorkRevision = timeStamp;
        localSaveObject.RecoveryPayload = JSON.parse(JSON.stringify(offerDetailsObject));
        updateLocalCache(localSaveObject)
            .then(function () {
                var offId;

                offId = localSaveObject.OfferId;

                return queueSyncJob(OPERATION.SaveOffer, timeStamp, { offerId: offId }, offerDetailsObject, saveOwner, undefined, undefined, saveCurrent)
                    .then(function () {
                        if (!saveCurrent()) return $.Deferred().reject({ cancelled: true }).promise();
                        return manageSyncQueue(offId, "SAVE", OPERATION.SaveOffer, timeStamp, saveOwner, undefined, saveCurrent)
                            .then(function () {
                                if (!saveCurrent()) return $.Deferred().reject({ cancelled: true }).promise();

                                const serverSave = requireServerConfirmation ? calculationSaveConfirmation(timeStamp, saveOwner) : undefined;
                                if (requireServerConfirmation && !globals.isOnline.value)
                                    settleCalculationSave(timeStamp, null, { offline: true, serverPersisted: false });

                                var offlineDataVersion = {
                                    Offer: { 
                                        Id: localSaveObject.OfferId, 
                                        UpdateCounter: localSaveObject.UpdateCounter, 
                                        OfferDate: localSaveObject.OfferDate, 
                                        Description: offerDetailsObject.Offer.Description, 
                                        AdditionalDescription: offerDetailsObject.Offer.AdditionalDescription ,
                                        OwnerId: ownerId,
                                        OwnerType: ownerType
                                    },
                                    Result: { ReturnCode: 1 }
                                };
                                dfd.resolve({ savedOffer: offlineDataVersion, localDurable: true,
                                    queued: true, serverPersisted: false, workId: timeStamp, serverSave });
                                if (globals.isOnline.value) {
                                    globals.offerSyncFailure = null;
                                    globals.offerSyncing.value = true;
                                    doSyncIfNeeded();
                                }
                            });
                    });
            }).fail(error => dfd.reject(error?.libraryRecoveryRequired || error?.cancelled ? error : { recoverable: true, errorMessage: 'Local calculation save failed. Keep this work open and retry.' }));
    }

    function updateLocalCache(localSaveObject) {
        return localDL.addToCache(operation, localSaveObject.OfferId, localSaveObject, sessionCacheOwner(saveOwner, saveCurrent));
    }

    return security.guardForSession(dfd.promise().then(result => result, error =>
        $.Deferred().reject(error?.libraryRecoveryRequired || error?.cancelled ? error : { ...error, localSaveFailed: true }).promise()), saveOwner);
}

function saveCustomAccessory(accessoryId, accessoryType, accessoryDetail, accessLevel, submitToPublicLibrary, publicLibraryEditLevel, submitToDistributorLibrary, distributorLibraryEditLevel) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();
    const saveOwner = security.getIncarnation();
    const originalLocalId = accessoryId;
    var dfd = $.Deferred();
    var newSave, accessorySaveObject, accessoryObjectString;
    var timeStamp = nextRetainedWorkTimeStamp();
    var tempAccessoryId = 'accessoryId_' + timeStamp;
    equipmentRevisionStarts.set(saveOwner+'|'+accessoryType+'|'+(originalLocalId||tempAccessoryId),timeStamp);

    accessoryDetail.AccessLevel = accessLevel;
    
    accessoryDetail.Source = config.VEHICLE_SOURCE_TYPES.CUSTOM;
    accessoryDetail.AccessLevel = accessLevel;
    //accessoryDetail.SubmitToPublicLibrary = submitToPublicLibraryBoolean;

    accessorySaveObject = JSON.parse(JSON.stringify(accessoryDetail));
    accessoryDetail.Id = typeof accessoryId === 'string' ? 0 : accessoryId;
    if (accessoryDetail.SourceDatabaseId && typeof accessoryDetail.SourceDatabaseId === 'string') {
        accessoryDetail.SourceDatabaseId = 0;
    }

    accessoryObjectString = JSON.stringify(accessoryDetail);
    var remotePayloadObject = {
        AccessoryType: accessoryType,
        AccessoryDetail: accessoryObjectString,
        AccessLevel: accessLevel,
        ParentType: accessoryDetail.ParentType,
        ForceSave: false,
        SubmitToPublicLibrary: typeof submitToPublicLibrary === 'boolean' ? submitToPublicLibrary : false,
        PublicLibraryEditLevel: typeof publicLibraryEditLevel === 'string' ? publicLibraryEditLevel : config.ITEM_EDIT_LEVEL_OPTIONS.NO_EDIT,
        SubmitToDistributorLibrary: typeof submitToDistributorLibrary === 'boolean' ? submitToDistributorLibrary : false,
        DistributorLibraryEditLevel: typeof distributorLibraryEditLevel === 'string' ? distributorLibraryEditLevel : config.ITEM_EDIT_LEVEL_OPTIONS.NO_EDIT,
        ClientStructureVersionNumber: globals.getPaddedStructureVersionNumber()
    };
    if (accessoryId === 0) {
        accessorySaveObject.Id = tempAccessoryId;
        newSave = true;
    } else {
        newSave = false;
    }

    if (typeof accessoryId === 'string' && accessoryId.indexOf('_') !== -1) {
        accessoryId = 0;

    }
    
    doOfflineSave(remotePayloadObject, accessorySaveObject);
    
    function doOfflineSave(syncObjectDetails, localSaveObject) {
        localSaveObject.RetainWork = true;
        localSaveObject.WorkRevision = timeStamp;
        if (localSaveObject.IsComplete === true) {
            localSaveObject.CompletionDate = moment().format('DD/MM/YYYY');
        } else {
            localSaveObject.CompletionDate = "01/01/1900";
        }
        updateLocalCache(localSaveObject, newSave)
            .then(function () {
                // Commit the replacement before retiring any previous submission.
                return queueSyncJob(OPERATION.SaveCustomAccessory, timeStamp, { id: accessoryId }, syncObjectDetails, saveOwner, originalLocalId)
                    .then(function () {
                        return manageSyncQueue(localSaveObject.Id, "SAVE", OPERATION.SaveCustomAccessory, timeStamp, saveOwner)
                            .then(function () {
                                if (globals.isOnline.value) {
                                    doSyncIfNeeded();
                                }
                                var offlineDataVersion = {
                                    AccessoryId: localSaveObject.Id,
                                    UpdateCounter: localSaveObject.UpdateCounter,
                                    Description: localSaveObject.Description,
                                    Source: config.VEHICLE_SOURCE_TYPES.CUSTOM,
                                    AccessoryType: localSaveObject.AccessoryType,
                                    AccessLevel: localSaveObject.AccessLevel,
                                    SubmitToPublicLibrary: localSaveObject.SubmitToPublicLibrary,
                                    PublicLibraryEditLevel: localSaveObject.PublicLibraryEditLevel,
                                    SubmitToDistributorLibrary: localSaveObject.SubmitToDistributorLibrary,
                                    DistributorLibraryEditLevel: localSaveObject.DistributorLibraryEditLevel,
                                    Type: localSaveObject.Type,
                                    PersistedOnServer: false,
                                    SyncState: 'QUEUED',
                                    Result: { ReturnCode: 1 }
                                };
                                dfd.resolve({ savedAccessory: offlineDataVersion, newSave: newSave });
                            });
                    });
            }).fail(function (error) { dfd.reject(error?.libraryRecoveryRequired ? error : { recoverable: true, errorMessage: 'Local equipment save failed. Keep this draft open and retry.' }); });
    }

    function updateLocalCache(accessory, isNewAccessory) {
        return updateLocallyCachedAccessory(accessory.Id, accessory, isNewAccessory);
    }

    function updateLocallyCachedAccessory(accessoryId, accessory, isNew, optionalNewId) {

        var op;
        if (accessory.AccessoryType === config.ACCESSORY_TYPES.BODY) {
            op = OPERATION.SaveBody;
        } else if (accessory.AccessoryType === config.ACCESSORY_TYPES.TRAILER) {
            op = OPERATION.SaveTrailer;
        } else if (accessory.AccessoryType === config.ACCESSORY_TYPES.PAYLOAD) {
            op = OPERATION.SavePayload;
        } else {
            op = OPERATION.SaveAccessory;
            //localDL.addToCache(OPERATION.SaveAccessory, accessory.Id + '_' + accessory.AccessoryType + '_' + accessory.Source, accessory);
        }
        return localDL.addToCache(op, accessory.Id + '_' + accessory.AccessoryType + '_' + accessory.Source, accessory, sessionCacheOwner(saveOwner));
    }

    return security.guardForSession(dfd.promise(), saveOwner);
}

/**
     * Deletes a custom accessory. Will reflect any changes locally and queue a new sync item if the app is offline or the online save fails.
     * The app will attempt to sync the item next time it goes online. 
     * @method deleteCustomAccessory
     * @param {Number} id Id of the Custom Accessory to be deleted.
     * @param {Number} updateCounter Update counter of the Custom Accessory to be deleted.
     * @param {String} accessoryType .
     * @return {Object} Returns data supplied in response to successful server call, or dummies the data response if offline. Data response is a ReturnCode indicating success or failure of delete operation. 
     */
function deleteCustomAccessory(id, description, updateCounter, accessoryType, accessorySource, accessLevel) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();

    var dfd = $.Deferred();

    var timeStamp = Date.now();
    //if (globals.isOnline.value === true) {
    //    deleteCustomAccessoryOnline(id, updateCounter, accessoryType, accessorySource);
    //} else {
        doOfflineDelete(id, description, updateCounter, accessoryType, accessorySource, accessLevel);
    //}

    //function deleteCustomAccessoryOnline(id, updateCounter, accessoryType, accessorySource) {
    //    var request = 'accessories/delete?id=' + id + '&accessoryType=' + accessoryType;
    //    handleDataRequest(null, request, OPERATION.DeleteCustomAccessory.Code, true)
    //        .then(function (data) {
    //            if (data.ReturnCode === 1) {
    //                updateCacheLocally(id, accessoryType, accessorySource);
    //            }
    //            dfd.resolve({ deletedCustomAccessory: data });
    //        })
    //        .fail(function () {
    //            doOfflineDelete(id, updateCounter, accessoryType, accessorySource);
    //        });
    //}

    function updateCacheLocally(id, accessoryType, accessorySource, accessLevel) {
        var updateCacheLocallyDfd = $.Deferred();

        if (accessoryType === config.ACCESSORY_TYPES.BODY) {
            localDL.deleteRecord(localDL.STORE.Bodies, id + '_' + accessoryType + '_' + accessorySource, undefined, true)
                .then(function () {
                    updateCacheLocallyDfd.resolve();
                });
        } else if (accessoryType === config.ACCESSORY_TYPES.TRAILER) {
            localDL.deleteRecord(localDL.STORE.Trailers, id + '_' + accessoryType + '_' + accessorySource, undefined, true)
                .then(function () {
                    updateCacheLocallyDfd.resolve();
                });
        } else if (accessoryType === config.ACCESSORY_TYPES.PAYLOAD) {
            localDL.deleteRecord(localDL.STORE.Payloads, id + '_' + accessoryType + '_' + accessorySource, undefined, true)
                .then(function () {
                    updateCacheLocallyDfd.resolve();
                });
        } else {
            localDL.deleteRecord(localDL.STORE.Accessories, id + '_' + accessoryType + '_' + accessorySource, undefined, true)
                .then(function () {
                    updateCacheLocallyDfd.resolve();
                });
        }
        //updateLocallyCachedOffersEquipmentSetSourceDatabaseIdToZero(id, accessoryType);
        return updateCacheLocallyDfd.promise();
    }

    function doOfflineDelete(id, description, updateCounter, accessoryType, accessorySource, accessLevel) {

        var offlineDataVersion = {
            ReturnCode: 1
        };

        updateCacheLocally(id, accessoryType, accessorySource, accessLevel)
            .then(function () {
                manageSyncQueue(id, "DELETE", OPERATION.DeleteCustomAccessory)
                    .then(function (abortAdd) {
                        if (abortAdd === false) {
                            var params = { id: id, accessoryType: accessoryType, accessLevel: accessLevel };
                            queueSyncJob(OPERATION.DeleteCustomAccessory, timeStamp, params, {Description : description})
                                .then(function () {
                                    if (globals.isOnline.value) {
                                        doSyncIfNeeded();
                                    }
                                    dfd.resolve({ deletedCustomAccessory: offlineDataVersion });
                                });
                        } else {
                            if (globals.isOnline.value) {
                                doSyncIfNeeded();
                            }
                            dfd.resolve({ deletedCustomAccessory: offlineDataVersion });
                        }
                    });
            });

        
    }

    return dfd.promise();
}


/**
 * Saves/updates a user's settings. Will reflect any changes locally and queue a new sync item if the app is offline or the online save fails.
 * The app will attempt to sync the item next time it goes online. 
 * @method saveUserSettings
 * @param {Number} legislationId Id of the legislation against which the action is being saved.
 * @param {Number} measurementSystemId Id of the action to be saved. 0 in the case of a new action, an existing action id in the case of an update, and a string if updating an action that was created offline.
 * @param {Number} lengthIncrement The date the action is due by.
 * @param {Number} massIncrement The action text.
 * @param {Number} percentageIncrement A flag showing if the action has been completed or not.
 * @param {boolean} colourDrawings Indicates if user would like to see colour in the drawings on configuration page
 * @param {boolean} showComplianceScorecard Indicates if the user would like to see the compliance dashboard on configuration page
 * @param {string} specifyWheelbaseAs What type of wheelbase that will be used in calculation
 * @param {Array} integrations Array of integrations (NTEA) that the user has with TruckScience
 * @param {string} specifyCabDimensionsAs Indicates if the user can change BBC/AC in menu
 * @param {string} specifyChassisLengthAs Indicates if the user can change Wheelbase/CA in menu
 * @param {string} specifyFuelConsumptionAs Indicates whether the fuel consumption is expressed as l/100km or km/l
 * @param {string} specifyLicencingRegionAs Indicates default licencing region
 * @param {string} specifyAxleRatingAs Indicates whether to display the SIMPLIFIED (USA) or DETAILED (ROW)
 * @param {string} defaultReportPdfPageSize Sets the page size of the PDF on summary when opening an offer
 * @param {string?} reportLogo Image to be displayed in the upper left corner of the report
 * @param {object} dashboardConfiguration Object containing data points, internal standards and legislations
 * @param {string} companyAdditionalNote String containing the company disclaimer/additional notes
 * @param {string} specifyBodyPositionAs String containing value from config.SPECIFY_POSITION_AS_OPTIONS enum
 * @param {string} specifyEquipmentPositionAs String containing value from config.SPECIFY_POSITION_AS_OPTIONS enum
 * @param {boolean} showTips Indicates if the user would like to see legislation tips
 * @param {string} reportViewLayout String containing value from config.REPORT_VIEW_LAYOUT_OPTIONS enum
 * @param {Array} reportViews Array of objects containing report view data
 * @param {string} reportPdfSignatures String of the signatures saved by the user on the Summary screen
 * @param {string} reportPdfStamps String of the rubber stamps saved by the user on the Summary screen
 * @param {boolean} validateLeftRight Indicates if the user would like to validate left right
 * @return {Object} Returns data supplied in response to successful server call, or dummies the data response if offline. Data response includes the action's id, update counter, completion date and 
 * due date and also the overall update counter for the parent cutomer.
 */
function saveUserSettings(legislationId, measurementSystemId, lengthIncrement, massIncrement, percentageIncrement, colourDrawings, showComplianceScorecard, specifyWheelbaseAs, integrations, specifyCabDimensionsAs, specifyChassisLengthAs, specifyFuelConsumptionAs, specifyLicencingRegionAs, specifyAxleRatingAs, defaultReportPdfPageSize, reportLogo, dashboardConfiguration, companyAdditionalNote, specifyBodyPositionAs, specifyEquipmentPositionAs, showTips, reportViewLayout, reportViews, reportPdfSignatures, reportPdfStamps, validateLeftRight) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();

    var dfd = $.Deferred();
    const context = settingsContext(), ready = userSettingsReady || userSettingsRequest?.ready;
    // A delayed settings GET must not overwrite locally chosen preferences.
    userSettingsRequest?.dfd.reject({ cancelled: true });
    if (ready && settingsContextMatches(ready.context) && globals.user.getDashboardConfiguration())
        userSettingsReady = { context, dashboard: globals.user.getDashboardConfiguration() };
    const localSave = { context, dfd };
    userSettingsLocalSaves.add(localSave);
    const release = security.registerCancellation(() => dfd.reject({ cancelled: true }));
    dfd.always(() => { userSettingsLocalSaves.delete(localSave); release(); });
    globals.userSettingsSaveDfd = $.Deferred();
    var timeStamp = Math.max(Date.now(), (saveUserSettings.lastTimeStamp || 0) + 1),
        base64Position,
        reportLogoToSave = '';
    saveUserSettings.lastTimeStamp = timeStamp;

    if(reportLogo !== undefined && reportLogo !== null && typeof reportLogo === 'string') {
        if (reportLogo.indexOf('base64,') > -1) {
            base64Position = reportLogo.indexOf('base64,') + 7;
            reportLogoToSave = reportLogo.slice(base64Position);
        }
    } else {
        reportLogoToSave = '';
    }
    

    var userSettingsObject = {
        "DefaultLegislationId": legislationId,
        "DefaultMeasurementSystemId": measurementSystemId,
        "DefaultLengthIncrement": lengthIncrement,
        "DefaultMassIncrement": massIncrement,
        "DefaultPercentageIncrement": percentageIncrement,
        "ColourDrawings": colourDrawings,
        "ShowComplianceScorecard": showComplianceScorecard,
        "DefaultSpecifyWheelbaseAs": specifyWheelbaseAs,
        "DefaultSpecifyCabDimensionsAs": specifyCabDimensionsAs,
        "DefaultSpecifyChassisLengthAs": specifyChassisLengthAs,
        "DefaultSpecifyFuelConsumptionAs": specifyFuelConsumptionAs,
        "DefaultSpecifyLicencingRegionAs": specifyLicencingRegionAs,
        "DefaultSpecifyAxleRatingAs": specifyAxleRatingAs,
        "DefaultReportPdfPageSize": defaultReportPdfPageSize,
        "ReportLogoToSave": reportLogoToSave,
        "AdditionalNote": companyAdditionalNote,
        "DashboardConfiguration": dashboardConfiguration,
        "Integrations": integrations,
        "SpecifyBodyPositionAs": specifyBodyPositionAs,
        "SpecifyEquipmentPositionAs": specifyEquipmentPositionAs,
        "ShowTips": showTips,
        "ReportViewLayout": reportViewLayout,
        "ReportViews": reportViews,
        "ReportPdfSignatures": JSON.stringify(reportPdfSignatures),
        "ReportPdfStamps": reportPdfStamps,
        "ValidateLeftRight": validateLeftRight
    };

    var userSettingsLocalSaveObject = JSON.parse(JSON.stringify(userSettingsObject));
    doOfflineSave(userSettingsObject, userSettingsLocalSaveObject);
    

    function doOfflineSave(userSettingsObject, localSaveObject) {
        
        manageSyncQueue(0, "SAVE", OPERATION.SaveUserSettings, undefined, context.owner, localSave)
            .then(function () {      
                if (dfd.state() !== 'pending' || !settingsContextMatches(context)) return dfd.reject({ cancelled: true });
                return queueSyncJob(OPERATION.SaveUserSettings, timeStamp, {}, userSettingsObject, context.owner, undefined, localSave)
                    .then(function () {
                        if (dfd.state() !== 'pending' || !settingsContextMatches(context)) return dfd.reject({ cancelled: true });
                        if (globals.isOnline.value) {
                            doSyncIfNeeded();
                        }
                        var offlineDataVersion = {
                            Result: {
                                ReturnCode: 1
                            }
                        };
                        dfd.resolve({ data: offlineDataVersion });
                        // globals.userSettingsSaveDfd = null;
                    });
            }).fail(error => dfd.reject(error));

            
    }

    function updateLocalCache(userSettings) {
        var updateLocalCacheDfd = $.Deferred();

        localDL.addToCache(OPERATION.SaveUserSettings.Code, PROPERTY.UserSettings, userSettings)
            .then(function () {
                updateLocalCacheDfd.resolve();
            });

        return updateLocalCacheDfd.promise();
    }

    return dfd.promise();
}

function changePassword(oldPassword, newPassword) {
    var dfd = $.Deferred();

    var timeStamp = Date.now();
    var tempChangePasswordId = 'changePasswordId_' + timeStamp;

    

    //securityTokenValue = securityToken;

    var passwordObject = {
        "OldPassword": oldPassword,
        "NewPassword": newPassword
    }
    let request = OPERATION.ChangePassword.BaseUrl
    if (globals.isOnline.value) {
        // queueSyncJob(OPERATION.ChangePassword, timeStamp, {}, passwordObject)
        //     .then(function () {

        //         doSyncIfNeeded();

        //         var changePasswordResponse = {
        //             ReturnCode: 1,
        //             Message:''
        //         };
        //         dfd.resolve({ chgPwd: changePasswordResponse });
        //     });
            handleDataRequest(undefined, request, OPERATION.ChangePassword.Code, true, passwordObject)
                .then(function (data) {
                    // if (data.ReturnCode === 1) {
                    //     // globals.user.updateUser({ password: curSyncItem.Details.NewPassword });
                    //     var changePasswordResponse = {
                    //             ReturnCode: 1,
                    //             Message:''
                    //         };
                    //     dfd.resolve({ chgPwd: changePasswordResponse });
                    // } else {
                    //     handleSyncItemFailure(curSyncItem.Operation.Code, itemDescriptionText, data.ReturnCode, data.Message);
                    // }
                    dfd.resolve(data);
                })
                .fail(function (errorMessage) {
                    dfd.reject(errorMessage);
                });
    } else {
        dfd.reject({ errorMessage: 'offline' });
    }
    

    return dfd.promise();
}


/**
     * Deletes a given offer. Will reflect any changes locally and queue a new sync item if the app is offline or the online save fails.
     * The app will attempt to sync the item next time it goes online. 
     * @method deleteOffer
     * @param {Number} offerId Id of the offer to be deleted.
     * @param {Number} updateCounter Update counter of the offer to be deleted.
     * @param {Number} description description of the offer to be deleted.
     * @return {Object} Returns data supplied in response to successful server call, or dummies the data response if offline. Data response is a ReturnCode indicating success or failure of delete operation. 
     */
function deleteOffer(offerId, updateCounter, description) {
    const blocked = libraryWriteError();
    if (blocked) return $.Deferred().reject(blocked).promise();

    var dfd = $.Deferred();

    var timeStamp = Date.now();
    
    doOfflineDelete(offerId, updateCounter, description);
    
    function updateCacheLocally(offerId) {
        var updateCacheLocallyDfd = $.Deferred();

        localDL.deleteRecord(STORE.SavedOffers, offerId, undefined, true)
            .then(function () {
                updateCacheLocallyDfd.resolve();
            });

        return updateCacheLocallyDfd.promise();
    }

    function doOfflineDelete(offerId, updateCounter, description) {

        var offlineDataVersion = {
            ReturnCode: 1
        };

        updateCacheLocally(offerId)
            .then(function () {
                manageSyncQueue(offerId, "DELETE", OPERATION.DeleteOffer)
                    .then(function (abortAdd) {
                        if (abortAdd === false) {
                            var params = { id: offerId, updateCounter: updateCounter };
                            queueSyncJob(OPERATION.DeleteOffer, timeStamp, params, {Description: description})
                                .then(function () {
                                    if (globals.isOnline.value) {
                                        doSyncIfNeeded();
                                    }
                                    dfd.resolve({ deletedOffer: offlineDataVersion });
                                });
                        } else {
                            if (globals.isOnline.value) {
                                doSyncIfNeeded();
                            }
                            dfd.resolve({ deletedOffer: offlineDataVersion });
                        }
                    });      
            });

        
    }

    return dfd.promise();
}



function getLoginPageInformation(cultureCode) {

    var dfd = $.Deferred();

    //securityTokenProvided = securityToken;
    cultureCodeProvided = cultureCode;

    // make ajax call
    $.ajax(utils.setOptions('language/unauthenticatedtranslations?cultureCode=' + cultureCode, operationTypeGet, dataTypeJson, setUnAuthenticatedQueryHeader, globals.cloudServicesVersion1, true))
        .then(requestSucceeded)
        .fail(requestFailed);

    // handle the ajax callback
    function requestSucceeded(data, textStatus, jqXHR) {



        //dfd.resolve({ legislation: ko.mapping.fromJS(data) });
        dfd.resolve({messages : data });

    }

    function requestFailed(xhr, ajaxOptions, thrownError) {
        dfd.reject({ errorMessage: xhr.statusText });
    }

    return dfd.promise();
}


/**
     * Retrieves specified database attribute translations. Translations are cached during startup and then updated by the version control system as and when required, so this method effectively 
     * always retrieves translations locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getTranslations
     * @param {Number} custDistGroupId Id of the customer distribution group the user belongs to. Passing the CDG Id enables the server to determine which CommonDistributionGroup 
     * the user belongs to and therefore which translations data to return.
     * @return {Object} Returns data array of Translations. 
     */
function getTranslations(custDistGroupId) {
    var dfd = $.Deferred();

    var request = 'databaseattributevalues/get?customerDistributionGroupId=' + custDistGroupId + "&databaseAttributeIds=" + config.databaseAttributesForTranslation;
    var operation = OPERATION.GetTranslations;

    handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.Translations); }, request, operation, false)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve({ translations: data });
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Retrieves common pack details which includes specified database attribute translations. Common pack details are cached during startup and then updated by the version control system as and when required, so this method effectively 
     * always retrieves common pack details locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getCommonPackDetails
     * @param {Number} custDistGroupId Id of the customer distribution group the user belongs to. Passing the CDG Id enables the server to determine which CommonDistributionGroup 
     * the user belongs to and therefore which translations data to return.
     * @return {Object} Returns object containing data array of Translations, data array of available legislations and a data array of tolls/toll locations
     */
function getCommonPackDetails(custDistGroupId, isCurrentContext) {
    var dfd = $.Deferred();

    var request = 'commonpackdetails/get?customerDistributionGroupId=' + custDistGroupId + "&databaseAttributeIds=" + config.databaseAttributesForTranslation;
    var operation = OPERATION.GetCommonPackDetails;

    handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.CommonPackDetails); }, request, operation, false, undefined, isCurrentContext)
        .then(function (data) {
            if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true });
            //any custom pre-processing of data should be handled here                    
            var availableLegislations = [];

            data.Legislations.forEach(function (legislation) {
                var temp = {
                    id: legislation.Id,
                    legislationId: legislation.LegislationId,
                    countryId: legislation.CountryId,
                    description: legislation.Description,
                    name: legislation.Name,
                    isDefault: legislation.IsDefault,
                    countryAbbreviation: legislation.CountryAbbreviation,
                    roadNames: legislation.RoadNames
                };
                availableLegislations.push(temp);
            });

            dfd.resolve({
                translations: data.Translations,
                availableLegislations: availableLegislations,
                tolls: data.Tolls,
                dataPoints: data.DataPoints,
                dDRTranslations: data.DDRTranslations,
                accessoryTranslations: data.AccessoryTranslations,
                companyInterface: data.CompanyInterface || {}
            });
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Retrieves body masses. Body masses are cached during startup and then updated by the version control system as and when required, so this method effectively 
     * always retrieves body masses locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getBodyMasses
     * @return {Object} Returns object containing data array of body masses
     */
function getBodyMasses() {
    var dfd = $.Deferred();

    var request = OPERATION.GetBodyMasses.BaseUrl;
    var operation = OPERATION.GetBodyMasses.Code;

    handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.BodyMasses); }, request, operation, false)
        .then(function (data) {
            
            dfd.resolve({bodyMasses: data.BodyMasses});
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Retrieves licence categories. Licence Categories are cached during startup and then updated by the version control system as and when required, so this method effectively 
     * always retrieves licence categories locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getLicenceCategories
     * @return {Object} Returns object containing data array of licence categories
     */
function getLicenceCategories() {
    var dfd = $.Deferred();

    var request = OPERATION.GetLicenceCategories.BaseUrl + '?companyId=' + globals.user.getCompanyId()
    var operation = OPERATION.GetLicenceCategories.Code;

    handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.LicenceCategories); }, request, operation, false)
        .then(function (data) {

            dfd.resolve({ licenceCategories: data.CountryLicenceCategoryInfo });
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Retrieves legislation details which includes name and Id and list of legislation instructions
     * always retrieves common pack details locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getLegislationDetails
     * @param {Number} legislationId Legislation Id of the active legislation object on the user/offer. 
     * @param {Number} countryId Country Id of the active legislation object on the user/offer. 
     * the user belongs to and therefore which translations data to return.
     * @return {Object} Returns object that has legislation Id, name and data array of legislation instructions. 
     */
function getLegislationDetails(legislationId, countryId) {
    var dfd = $.Deferred();

    var request = 'legislations/get?legislationId=' + legislationId + "&countryId=" + countryId;
    var operation = OPERATION.GetLegislationDetails;

    handleDataRequest(function () { return localDL.getLocalLegislationDetailsById(legislationId, countryId); }, request, operation, false)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    
            
            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Retrieves specified database attribute translations. Translations are cached during startup and then updated by the version control system as and when required, so this method effectively 
     * always retrieves translations locally, however if local retrieval fails for some reason it will then attempt to get them online.
     * @method getTranslations
     * @param {Number} custDistGroupId Id of the customer distribution group the user belongs to. Passing the CDG Id enables the server to determine which CommonDistributionGroup 
     * the user belongs to and therefore which translations data to return.
     * @return {Object} Returns data array of Translations. 
     */
function getAppConfig(isCurrentContext) {
    var dfd = $.Deferred();

    var request = OPERATION.GetAppConfig.BaseUrl;
    var operation = OPERATION.GetAppConfig.Code;

    handleDataRequest(function () { return localDL.getFromCache(operation, PROPERTY.AppConfig); }, request, operation, false, undefined, isCurrentContext)
        .then(function (data) {
            if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true });
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
     * Sends data request to cloud services which will email user and notify support
     * @method postDataRequest
     * @param {string} make - Make of vehicle that user has requested
     * @param {string} model - Model of vehicle that user has requested
     * @param {string} additionalInfo - Any additional info that the user has provided
     * @param {string} type - The item type to request (vehicle/body/crane/etc)
     * @param {string} market - Country code/abbreviation of markket country
     * @param {boolean} isEnterpriseUser - Whether the user is an enterprise user or not
     * @return {Promise} Returns object that contains success/failure return codes
     */
function postDataRequest(make, model, additionalInfo, type, market, isEnterpriseUser) {
    var dfd = $.Deferred();

    var request = 'customers/datarequest';
    var operation = OPERATION.PostDataRequest;
    var returnObject = {
        "Make": make,
        "Model": model,
        "AdditionalInfo": additionalInfo,
        "Type": type,
        "Market": market,
        "IsEnterpriseUser": isEnterpriseUser
    };

    handleDataRequest(function () { return "" }, request, operation, true, returnObject)
        .then(function (data) {
            //any custom pre-processing of data should be handled here
            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendFeedback(feedback) {
    var dfd = $.Deferred();

    var request = 'customers/feedback?feedback';
    var operation = OPERATION.SendFeedback;
    var data = {
        Feedback: feedback
    };
    handleDataRequest(null, request, operation, true, data)
        .then(function (transactionResult) {
            dfd.resolve(transactionResult);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendReferral(referralData) {
    var dfd = $.Deferred();

    var request = 'customers/referral?referral';
    var operation = OPERATION.SendReferral;
    var data = {
        FirstName: referralData.firstName,
        LastName: referralData.lastName,
        CompanyName: referralData.companyName,
        Phone: referralData.telephoneNumber,
        Email: referralData.emailAddress
    };
    handleDataRequest(null, request, operation, true, data)
        .then(function (transactionResult) {
            dfd.resolve(transactionResult);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendReferralPromptNotification(numberOfUses) {
    var dfd = $.Deferred();

    var request = 'users/referralprompt?numberOfUsesFromClient=' + numberOfUses;
    var operation = OPERATION.SendReferralPromptNotification;

    handleDataRequest(null, request, operation, true)
        .then(function (transactionResult) {
            dfd.resolve(transactionResult);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function postCSVFileData(formData) {
    var dfd = $.Deferred();

    var operation = OPERATION.PostCSVData.Code;

    var request = OPERATION.PostCSVData.BaseUrl + '?operationType=CSV-VEHICLES&persistToRequester=true';
    

    handleDataRequest(null, request, operation, true, formData)
        .then(function (transactionResult) {
            dfd.resolve(transactionResult);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function postDXFFileData(formData) {
    var dfd = $.Deferred();

    var operation = OPERATION.PostDXFData.Code;

    //var request = OPERATION.PostDXFData.BaseUrl + '?operationType=CSV-VEHICLES&persistToRequester=true';
    var request = OPERATION.PostDXFData.BaseUrl;
    // var request = 'http://localhost:7071/api/Function1';// + '/?rand=' + Date.now();

    handleDataRequest(null, request, operation, true, formData)
        .then(function (transactionResult) {
            // jQuery 2 callbacks do not turn thrown exceptions into rejections.
            let response;
            try { response = normalizeDxfResponse(transactionResult); }
            catch (error) { dfd.reject({ invalidDxfResponse: true }); return; }
            dfd.resolve(response);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function postTransSolveFileData(formData) {
    var dfd = $.Deferred();

    var operation = OPERATION.PostTransSolveData.Code;

    //var request = OPERATION.PostDXFData.BaseUrl + '?operationType=CSV-VEHICLES&persistToRequester=true';
    var request = OPERATION.PostTransSolveData.BaseUrl;

    handleDataRequest(null, request, operation, true, formData)
        .then(function (transactionResult) {
            dfd.resolve(transactionResult);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function updatePendingVehicle(id, vehicleData, vehicleChanges, isSubmit, postValidatedVehicleStatus, comments) {
    var dfd = $.Deferred();

    var isSubmitVal = isSubmit || false;
    var postValidatedVehicleStatusVal = postValidatedVehicleStatus || '';

    var postPayload = {
        VehicleDetail: vehicleData,
        PostValidatedVehicleStatus: postValidatedVehicleStatusVal,
        Comments: comments,
        IsSubmit: isSubmitVal
    };

    var operation = OPERATION.UpdatePendingVehicle.Code;

    //var request = OPERATION.UpdatePendingVehicle.BaseUrl + '/' + id + '?isSubmit=' + isSubmitVal + '&postValidatedVehicleStatus=' + postValidatedVehicleStatusVal;
    var request = OPERATION.UpdatePendingVehicle.BaseUrl + '/' + id;

    handleDataRequest(null, request, operation, true, postPayload)
    //handleDataRequest(null, request, operation, true, vehicleData)
        .then(function (result) {
            if (result.Result.ReturnCode === 1 && isSubmit) {
                //remove locally cached offer
                removeLocallyCachedVehicleAndVehicleStub(id)
                    .then(function () {
                        dfd.resolve(result);
                    });
                //remove offer stub from user
            } else if (result.Result.ReturnCode === 1) {
                //update locally cached offer representing this pending vehicle
                vehicleChanges.UpdateCounter = result.UpdateCounter;
                updateVehicleDetailLocally(id, vehicleChanges)
                    .then(function () {
                        dfd.resolve(result);
                    });
            } else {
                dfd.resolve(result);
            }
            
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();

    function removeLocallyCachedVehicleAndVehicleStub(vehicleId) {
        var dfd = $.Deferred();

        localDL.deleteRecord(localDL.STORE.NewOffers, vehicleId)
            .then(function () {
                localDL.deleteRecord(localDL.STORE.Vehicles, vehicleId)
                    .then(function () {
                        dfd.resolve();
                    });
            });

        return dfd.promise();
    }

    function updateVehicleDetailLocally(vehicleId, changesToApply) {
        var dfd = $.Deferred();

        localDL.getFromCache(OPERATION.GetNewOfferDetails, vehicleId)
            .then(function (data) {
                
                var vehicleDetails = data.Offer.Configuration;
                Object.keys(changesToApply).forEach(function (key) {
                    if (vehicleDetails[key] !== undefined) {
                        vehicleDetails[key] = changesToApply[key];
                    }
                });
                var axleCounter = 0;
                changesToApply.Axles.forEach(function (axleAsJSON) {
                    Object.keys(axleAsJSON).forEach(function (key) {
                        if (vehicleDetails.Axles[axleCounter][key] !== undefined) {
                            vehicleDetails.Axles[axleCounter][key] = axleAsJSON[key];
                        }
                    });
                    axleCounter++;
                });
                

                localDL.addToCache(OPERATION.GetNewOfferDetails, vehicleId, data)
                    .then(function () {
                        dfd.resolve();
                    });
            });

        return dfd.promise();
    }
}

function shareOffer(shareeInfoStubs, offerInfoStubs, optionalCommentForSharee, copyMeIn) {

    var dfd = $.Deferred();

    var shareData = {
        SharerOfferInfoStubs: offerInfoStubs,
        ShareeInfoStubs: shareeInfoStubs,
        OptionalCommentForSharee: optionalCommentForSharee,
        CopyMeInOnInvitation: copyMeIn
    }

    var operation = config.OPERATION.ShareOffer.Code;
    var request = config.OPERATION.ShareOffer.BaseUrl;

    handleDataRequest(null, request, operation, true, shareData)
        .then(function (result) {
            if (result.Result.ReturnCode === 1) {
                dfd.resolve(result);
            } else {
                dfd.resolve(result);
            }

        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function deleteUserOfferShareObject(shareeUserId, offerStubs, drivenBySharer) {

    var dfd = $.Deferred();


    var requestPayload = {
        OfferStubs: offerStubs,
        ShareeUserId: shareeUserId,
        SharerDriven: drivenBySharer
    }

    var operation = OPERATION.DeleteUserOfferShare.Code;
    var request = OPERATION.DeleteUserOfferShare.BaseUrl;


    handleDataRequest(null, request, operation, true, requestPayload)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (error) {
            dfd.reject(error);
        });

    return dfd.promise();
}

function getOfferShareStatus(userId, offerId, shareeUserId) {
    //GetOfferShareStatus
    var dfd = $.Deferred();

    var operation = OPERATION.GetOfferShareStatus.Code;
    var request = OPERATION.GetOfferShareStatus.BaseUrl + '?userId=' + userId + '&offerId=' + offerId + '&shareeUserId=' + shareeUserId;

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            // The share-status consumer validates the response shape.
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getUserAssociations() {
    var dfd = $.Deferred();

    var operation = OPERATION.GetUserAssociations;
    var request = 'users/userAssociations';

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            if (result.Result.ReturnCode === 1) {


                dfd.resolve(result);
            } else {
                dfd.resolve(result);
            }

        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getShareeUserOfferDetails(offerStubs) {
    var dfd = $.Deferred();

    var offerDetails = {
        OfferStubs: offerStubs
    }

    var operation = OPERATION.GetShareeUserOfferDetails.Code;
    var request = OPERATION.GetShareeUserOfferDetails.BaseUrl;

    handleDataRequest(null, request, operation, true, offerDetails)
        .then(function (result) {
            if (result.Result.ReturnCode === 1) {
                

                dfd.resolve(result);
            } else {
                dfd.resolve(result);
            }

        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function updateShareeUserDetails(signUpFirstName, signUpLastName, signUpPassword, authenticationMethod) {
    var dfd = $.Deferred();

    /*
    
Public Property Password As String
Public Property AuthenticationMethod As String
Public Property FirstName As String
Public Property LastName As String
    */

    var shareeData = {
        FirstName: signUpFirstName,
        LastName: signUpLastName,
        Password: signUpPassword,
        AuthenticationMethod: authenticationMethod
    }

    var operation = config.OPERATION.UpdateShareeUserDetails.Code;
    var request = config.OPERATION.UpdateShareeUserDetails.BaseUrl;

    handleDataRequest(null, request, operation, true, shareeData)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function updateUserAfterCheckout(hostedPageId, boughtEdition) {
    var dfd = $.Deferred();

    

    var updateData = {
        HosteddPageIdFromCheckout: hostedPageId,
        VerifiedEmail: globals.user.getEmailAddress(),
        BoughtEdition: boughtEdition
    }

    var operation = config.OPERATION.UpdateUserAfterCheckout.Code;
    var request = config.OPERATION.UpdateUserAfterCheckout.BaseUrl;

    handleDataRequest(null, request, operation, true, updateData)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function updateOfferShareStatus(offerId, shareeUserId, shareStatus) {
    var dfd = $.Deferred();

    var userOfferData = {
        // UserId: userId,
        ShareeUserId: shareeUserId,
        OfferId: offerId,
        ShareStatus: shareStatus
    }

    var operation = config.OPERATION.UpdateOfferShareStatus.Code;
    var request = config.OPERATION.UpdateOfferShareStatus.BaseUrl;

    handleDataRequest(null, request, operation, true, userOfferData)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}


/**
 * Always calls CS online to retrieve folders and calc stubs at the given level. Note root level is denoted by folderId of zero
 * @method getFolderContents
 * @param {Integer} folderId of folder to retrieve contents for         
 * @return {Object} Returns object that contains success/failure return codes, list of folders if any and list of calcs if any 
 */
function getFolderContents(folderId, folderOwnerType) {
    var dfd = $.Deferred();

    var operation = OPERATION.GetFolderContents.Code;
    var request = OPERATION.GetFolderContents.BaseUrl + '?folderId=' + folderId + '&folderOwnerType=' + folderOwnerType;

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to retrieve folders and calc stubs that match a given search term.
* @method getSearchResults
* @param {String} searchTerm to match folders and calcs against         
* @return {Object} Returns object that contains success/failure return codes, list of search results, each of which contains information on the path to the folder/calc  
*/
function getSearchResults(searchTerm, searchType) {
    var dfd = $.Deferred();

    var operation = OPERATION.GetSearchResults.Code;
    var request = OPERATION.GetSearchResults.BaseUrl + '?searchTerm=' + searchTerm + '&searchType=' + searchType;

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to retrieve the users complete folder tree structure.
* @method getFolderTreeStructure         
* @return {Object} Returns object that contains success/failure return codes, a dummy root folder object that contains the full folder structure  
*/
function getFolderTreeStructure() {
    var dfd = $.Deferred();

    var operation = OPERATION.GetFolderTreeStructure.Code;
    var request = OPERATION.GetFolderTreeStructure.BaseUrl;

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            if (result.Result.ReturnCode === 1) {


                dfd.resolve(result);
            } else {
                dfd.resolve(result);
            }

        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to create new folder with given name in given location.
* @method getSearchResults
* @param {String} searchTerm to match folders and calcs against         
* @return {Object} Returns object that contains success/failure return codes, list of search results, each of which contains information on the path to the folder/calc  
*/
function createFolder(name, parentFolderId, folderOwnerType) {
    var dfd = $.Deferred();
    
    var folderInfo = {
        name: name,
        parentFolderId: parentFolderId,
        ownerType: folderOwnerType
    }

    var operation = OPERATION.CreateFolder.Code;
    var request = OPERATION.CreateFolder.BaseUrl;

    handleDataRequest(null, request, operation, true, folderInfo)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to move selected item(s) to a given target folder.
* @method moveTo
* @param {Array} selectedItems to move         
* @param {Integer} targetFolderId to move selected item(s) to         
* @return {Object} Returns object that contains success/failure return codes, list of duplicate folder names if operation failed due to duplicate folder names in target directory
*/
function moveTo(selectedItems, pathToFolder, moveToScope) {
    var dfd = $.Deferred();

    var moveToInfo = {
        SelectedItems: selectedItems,
        FolderPath: pathToFolder,
        MoveToScope: moveToScope
    }

    var operation = OPERATION.MoveTo.Code;
    var request = OPERATION.MoveTo.BaseUrl;

    handleDataRequest(null, request, operation, true, moveToInfo)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to retrieve the list of sharers for the user.
* @method getSharers         
* @return {Object} Returns object that contains success/failure return codes, a list of sharers  
*/
function getSharers() {
    var dfd = $.Deferred();

    var operation = OPERATION.GetSharers.Code;
    var request = OPERATION.GetSharers.BaseUrl;

    handleDataRequest(null, request, operation, true)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to rename selected item(offer or folder).
* @method rename
* @param {Integer} objectId of item to rename          
* @param {String} newName of item         
* @param {String} renameType flag to indicate to server whether item is a folder or offer         
* @return {Object} Returns object that contains success/failure return codes
*/
function rename(objectId, newName, renameType) {
    var dfd = $.Deferred();

    var renameInfo = {
        RenameObjectId: objectId,
        NewName: newName,
        RenameType: renameType
    }

    var operation = OPERATION.Rename.Code;
    var request = OPERATION.Rename.BaseUrl;

    handleDataRequest(null, request, operation, true, renameInfo)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}
/**
* Always calls CS online to remove selected items(offers and/or folders).
* @method removeItems
* @param {Object} selectedItems list of one or more folders and calculations to be removed          
* @return {Object} Returns object that contains success/failure return codes
*/
function removeItems(selectedItems) {
    var dfd = $.Deferred();

    var removeInfo = {
        SelectedItems: selectedItems
    }

    var operation = OPERATION.Remove.Code;
    var request = OPERATION.Remove.BaseUrl;

    handleDataRequest(null, request, operation, true, removeInfo)
        .then(function (result) {
            dfd.resolve(result);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
* Always calls CS online to fetch user's saved offer stubs.
* @method getSavedOfferStubs
* @return {Object} Returns object that contains success/failure return codes and a list of offer stubs
*/
function getSavedOfferStubs() {

    var dfd = $.Deferred();

    var request = OPERATION.GetSavedOfferStubs.BaseUrl;
    var operation = OPERATION.GetSavedOfferStubs.Code;

    handleDataRequest(null, request, operation, true)
    .then(function (data) {
        
        dfd.resolve(data);

    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });

    return dfd.promise();
}

/**
* Always calls intercom to fetch TSc resources and messages the response into a format for local consumption in the app.
* @method getResources
* @return {Object} Returns array of resource sections that contain resource articles
*/
function getResources() {

    var dfd = $.Deferred();
    
    var request = OPERATION.GetResources.BaseUrl;
    var operation = OPERATION.GetResources.Code;

    handleDataRequest(null, request, operation, true)
    .then(function (data) {
        dfd.resolve(data);
    })
    .fail(function (errorMessage) {
        dfd.reject(errorMessage);
    });
    
    return dfd.promise();
}

function getOriginalsForItemsAddedToRig(accessories, trailers, payloads, axles) {
    const dfd = $.Deferred();
    const owner = security.getIncarnation();
    if ((accessories && (typeof accessories !== 'object' || Array.isArray(accessories))) ||
        [trailers, payloads, axles].some(items => items != null && !Array.isArray(items)) ||
        Object.values(accessories || {}).some(items => items != null && !Array.isArray(items))) {
        return dfd.reject({ recoverable: true, snapshotIncomplete: true,
            errorMessage: 'Incomplete saved equipment snapshot. Your work is retained.' }).promise();
    }
    const graph = { accessories, trailers, payloads, axles };
    const groups = { Bodies: 'Body', Cranes: 'Crane', Others: 'Other', Fridges: 'Fridge',
        Taillifts: 'Taillift', FifthWheels: 'FifthWheel', Hitches: 'Hitch', Hooklifts: 'Hooklift' };
    const result = { accessories: accessories ? {} : null, trailers: trailers ? [] : null,
        payloads: payloads ? [] : null, snapshotFallbacks: [] };
    const pending = [], identities = [];
    preventCaching = true;
    for (const [group, kind] of Object.entries(groups)) {
        if (!accessories) break;
        result.accessories[group] = [];
        for (const item of accessories[group] || []) load(item, kind, result.accessories[group]);
    }
    for (const item of trailers || []) load(item, 'Trailer', result.trailers);
    for (const item of payloads || []) load(item, 'Payload', result.payloads);
    $.when.apply($, pending).done(function () {
        preventCaching = false;
        if (!security.incarnationMatches(owner)) { dfd.reject({ cancelled: true }); return; }
        // Publish identity changes only after the entire saved graph has qualified.
        identities.forEach(change => { change.item.SourceDatabaseId = change.id; });
        allOriginalAccessoriesRetrievedObv.value = true;
        dfd.resolve(result);
    }).fail(function (error) { preventCaching = false; dfd.reject(error); });
    return dfd.promise();

    function load(item, kind, destination) {
        const ready = $.Deferred(); pending.push(ready.promise());
        const key = ['Body', 'Trailer', 'Payload'].includes(kind) ? kind : 'Accessory';
        function fallback() {
            if (!security.incarnationMatches(owner)) { ready.reject({ cancelled: true }); return; }
            try {
                const baseline = savedEquipmentBaseline(item, kind, config, graph);
                if (!baseline.Id) {
                    baseline.Id = kind === 'Payload' && item.PayloadType === config.PAYLOAD_TYPES.SIMPLE ? 0 : 'unknown_' + globals.nextId();
                    identities.push({ item, id: baseline.Id });
                }
                destination.push(baseline);
                result.snapshotFallbacks.push({ kind, sourceDatabaseId: item.SourceDatabaseId });
                ready.resolve();
            } catch (error) { ready.reject(error); }
        }
        if (!item || !(item.SourceDatabaseId > 0)) { fallback(); return; }
        let request;
        const access = item.AccessLevel === config.CUSTOM_ITEM_ACCESS_LEVEL.COMPANY ? item.AccessLevel : config.CUSTOM_ITEM_ACCESS_LEVEL.PERSONAL;
        if (kind === 'Body') request = getBody(item.SourceDatabaseId, item.Source, access);
        else if (kind === 'Trailer') request = getTrailer(item.SourceDatabaseId, item.Source, access);
        else if (kind === 'Payload') request = getPayload(item.SourceDatabaseId, item.Source, access);
        else request = getAccessory(item.SourceDatabaseId, kind, item.Source, access);
        request.then(function (data) {
            if (!security.incarnationMatches(owner)) { ready.reject({ cancelled: true }); return; }
            if (!equipmentDetailAvailable(data, key)) { fallback(); return; }
            destination.push({ ...data[key], ParentType: item.ParentType, RigPosition: item.RigPosition });
            ready.resolve();
        }, function (error) {
            if (error && error.cancelled) ready.reject(error);
            else fallback();
        });
    }
}

// function sendDataToGoogleAnalytics(category, action, label, value) {
//     doSendDataToGoogleAnalytics(category, action, label, value);
    
//     function doSendDataToGoogleAnalytics(category, action, label, value) {
//         var fieldsObject = {};
//         fieldsObject.hitType = 'event';
//         fieldsObject.eventCategory = category;
//         fieldsObject.eventAction = action;
//         if (label !== undefined) {
//             fieldsObject.eventLabel = label;
//         }
//         if (value !== undefined) {
//             fieldsObject.eventValue = value;
//         }

//         window.ga('send', fieldsObject);
//     }
// }

//#region Internal methods
function setQueryHeader(xhr) {
    xhr.setRequestHeader('SecurityToken', securityTokenProvided);
    xhr.setRequestHeader('cultureCode', cultureCodeProvided);
    //xhr.setRequestHeader('machineCode', cultureCodeProvided);
}

function setUnAuthenticatedQueryHeader(xhr) {
    xhr.setRequestHeader('cultureCode', cultureCodeProvided);
}


//#endregion


//#region code

/**
 * Retrieves specified image and converts it to a dataURL which it resolves to the caller. If it fails it will retry for the specified number of times.
 * @method convertImgToBase64
 * @param {String} url The url of the image to be retrieved and converted.
 * @param {String} outputFormat The format of the image described by the resulting dataUrl.
 * @return {String} Returns dataUrl representation of image. 
 */
function convertImgToBase64(url, outputFormat) {
    var dfd = $.Deferred();

    var canvas = document.createElement('CANVAS'),
        ctx = canvas.getContext('2d');
        //img = new Image;
    if (outputFormat === undefined) {
        outputFormat = "image/jpeg";
    }

    doImageRetrieval(1);

    function doImageRetrieval(numRetries) {
        var img = new Image;

        img.crossOrigin = 'Anonymous';

        img.onload = function () {
            var dataURL;
            canvas.height = img.height;
            canvas.width = img.width;
            ctx.drawImage(img, 0, 0);
            dataURL = canvas.toDataURL(outputFormat);
            canvas = null;
            dfd.resolve(dataURL);
        };

        img.onerror = function () {
            if (numRetries > 0) {
                numRetries--;
                img.onerror = null;
                img.onload = null;
                img = null;
                doImageRetrieval(numRetries);
            } else {
                dfd.resolve();
            }
            
        };
        img.src = decodeURI(url);
    }

    return dfd.promise();
}

/**
 * Replaces image url on a given object with the dataUrl stored locally. If there is no locally stored dataUrl then the method will kick off a job to retrieve and 
 * store the image but return to the caller without waiting for image to be retrieved.
 * @method replaceImageUrl
 * @param {Object} object The object on which an image url is to be swapped for a dataUrl.
 * @param {String} property The property on the object that contains the image url to be replaced.
 * @param {String} pack The pack the image is associated with. This is only used in the event that the image is not stored locally and needs to be retrieved and stored. 
 * @returns {Promise} Promise resolves when localDL.getImageIfInDB resolves
 */
function replaceImageUrl(object, property, pack, isCurrentContext) {
    var dfd = $.Deferred();
    if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true }).promise();
    if (object[property] === "" || object[property] === undefined || object[property] === null) {
        dfd.resolve();
    }else {
        localDL.getImageIfInDB(object[property])
        .then(function (url, dataUrl) {
            if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true });
            if (dataUrl === undefined) {
                convertAndAddImageToDB(url, pack);
                dfd.resolve();
            } else {
                object[property] = dataUrl;
                dfd.resolve();
            }
        })
        .fail(function () {
            dfd.resolve();
        });
    }
    

    return dfd.promise();
}

/**
        * Replaces vehicleBodyPlus image url on a given offer object with the dataUrl stored locally. If there is no locally stored dataUrl then the method will kick off a job to retrieve and 
        * store the image but return to the caller without waiting for image to be retrieved.
        * @method replaceVehiclePlusBodyImageURL
        * @param {Object} offer The offer object on which the vehicleBodyPlus image url is to be swapped for a dataUrl.
        * @returns {Promise} Promise resolves when localDL.getImageIfInDB resolves
        */
function replaceVehiclePlusBodyImageURL(offer) {
    var dfd = $.Deferred();

    localDL.getImageIfInDB(offer.VehiclePlusBodyImageURL)
        .then(function (url,dataUrl) {
            if (dataUrl === undefined) {
                convertAndAddImageToDB(url, localDL.PACK.Vehicle);
                dfd.resolve();
            } else {
                offer.VehiclePlusBodyImageURL = dataUrl;
                dfd.resolve();
            }
        })
        .fail(function () { dfd.resolve()});
    return dfd.promise();
}

/**
        * Replaces image url in HTML in brochure item with the dataUrl stored locally. If there is no locally stored dataUrl then the method will kick off a job to retrieve and 
        * store the image but return to the caller without waiting for image to be retrieved.
        * @method replaceBrochureItemURLs
        * @param {Array} brochureItemGroups An array of brochure item groups with each group containing borchure items with image urls to be swapped for a dataUrl.
        * @returns {Promise} Promise resolves when localDL.getImageIfInDB resolves
        */
function replaceBrochureItemURLs(brochureItemGroups) {
    var dfd = $.Deferred();
    var promises = [];

    var i = 0, j, tempItems, tempItem;
    for (; i < brochureItemGroups.length; i++) {
        tempItems = brochureItemGroups[i].VehicleBrochureItems;
        for (j = 0; j < tempItems.length; j++) {
            tempItem = tempItems[j];
            if (tempItem["HTML"] !== undefined) {
                promises.push(localDL.getImageIfInDB(getImageSrc(tempItem["HTML"]), tempItem));
            }
        }
        $.when.apply($, promises).done(function () {
            var results = Array.prototype.slice.call(arguments);
            var k = 0;
            var urlPromises = [];

            if (typeof results !== 'string' && typeof results[0] === 'string' && typeof results[2] !== 'string') {
                processResult(urlPromises, results);
            } else {
                for (; k < results.length; k++) {
                    processResult(urlPromises, results[k]);
                }
            }
            
            $.when.apply($, urlPromises).done(function () {
                dfd.resolve();
            });
        });

        
    }
    function processResult(urlPromiseArray, result) {
        var url;
        var dataUrl;
        var brochureItem;
        //if result is a string then only the image url has been returned i.e. image/dataUrl not in DB
        if (typeof result === 'string') {
            url = result;
        } else {
            url = result[0];
            dataUrl = result[1];
            brochureItem = result[2];
        }

        if (dataUrl === undefined) {
            var imageFormat = getImageFormat(url);
            convertAndAddImageToDB(url, localDL.PACK.Vehicle);
        } else {
            urlPromiseArray.push(replaceSrcInHTML(brochureItem, url, dataUrl));
        }
    }
    return dfd.promise();
}

/**
    * Queues replaceImageUrl jobs onto a given promise array to replace the necessary image urls on a given vehicle specification.
    * @method replaceVehicleSpecificationImageURLs
    * @param {Object} vehicleSpec The vehicle specification with image urls to be swapped for a dataUrl.
    * @param {Array} promises An array the replaceImageUrl jobs will be pushed onto.
    */
function replaceVehicleSpecificationImageURLs(vehicleSpec, promises) {
    var pack = localDL.PACK.Vehicle;
    
    promises.push(replaceImageUrl(vehicleSpec, "ManufacturerLogo", pack));
    promises.push(replaceImageUrl(vehicleSpec, "ReportManufacturerLogo", pack));
    promises.push(replaceImageUrl(vehicleSpec, "ReportVehicleChassisImage", pack));
    promises.push(replaceImageUrl(vehicleSpec, "VehicleChassisImage", pack));
}

function replaceSrcInHTML(brochureItem, url, dataUrl) {
    
    brochureItem["HTML"] = brochureItem["HTML"].replace(url, dataUrl);
}

function getImageSrc(html) {
    var regex = /<img[^>]+src="(https:\/\/[^">]+)"/g;
    var temp;

    temp = regex.exec(html);
    if (temp === null) {
        return '';
    } else {
        return temp[1];
    }            
}

function getVideoSrc(html) {
    var regex = /<iframe[^>]+src="(\/\/[^">]+)"/g;
    var temp;

    temp = regex.exec(html);

    return temp[1];
}

function doRegExReplace(dataURL, url, htmlDoc) {
    var dfd = $.Deferred();

    htmlDoc.replace(url, dataURL);
    dfd.resolve();

    return dfd.promise();
}

/**
* Wrapper method for setting up indexed db and then making sure it's open and ready to be used.
* @method setupIndexedDB
*/
function setupIndexedDB() {
    var dfd = $.Deferred();

    localDL.setupDBAndObjectStore()
        .then(function () {
            localDL.openDB().then(function () {
                dfd.resolve("db_ready");
            })
            .fail(function () {
                dfd.reject("db_failed");
            });
        })
        .fail(function () {
            dfd.reject("db_failed");
        });

    return dfd.promise();
}

function setupDataManager() {

}

function sendInfoToIntercom(operation, dataToSend) {
    var dfd = $.Deferred();
    if (globals.preventSendingOfIntercomEvent(dataToSend.eventName, dataToSend.metadata) === false) {
        if (globals.isOnline.value === true) {
            // Add sync queue code
            //handleDataRequest(null, null, config.OPERATION.SendIntercomEventData.Code, true, dataToSend)
            handleDataRequest(null, null, operation.Code, true, dataToSend)
                .then(function (data) {
                    //any custom pre-processing of data should be handled here                    
                    dfd.resolve(data);
                })
                .fail(function (e) {
                    if (e.errorType === 'INTERCOM') {
                        if (dataToSend.eventName !== config.INTERCOM_EVENT.ERROR_RAISED) {
                            logger.log(e.errorMessage, dataToSend, 'dataManager', config.showDebugToast);
                            log(e.errorMessage, e.errorDetails, 'sendInfoToIntercom', config.LOG_MESSAGE_TYPE.ERROR);
                        }
                    } else {
                        saveItemToSyncQueue();
                    }
                    //dfd.reject(errorMessage);
                });
        } else {
            saveItemToSyncQueue();
        }
    } else {
        dfd.resolve()
    }
        function saveItemToSyncQueue() {
            queueSyncJob(OPERATION.SendIntercomEventData, Date.now(), {}, dataToSend);
            dfd.resolve();
        }
    //} else {
    //    dfd.resolve();
    //}
    
    return dfd.promise();
}

// function triggerEventInGoogleAnalytics(eventName, eventCategory, eventAction, eventLabel, eventValue) {
function triggerEventInGoogleAnalytics(eventName, parametersObj) {
    let objToSend = {
        'event': eventName
    };
    if (parametersObj !== undefined && typeof parametersObj === 'object') {
        objToSend = Object.assign(objToSend, parametersObj);
    }
    dataLayer.push(objToSend);
    // dataLayer.push({
    //     'event': eventName,
    //     'variable_name': 'variable_value'
    // });
}

//function sendUserInfoToIntercom(dataToSend) {
//    var dfd = $.Deferred();
//    if (globals.isOnline.value === true) {
//        // Add sync queue code
//        handleDataRequest(null, null, config.OPERATION.SendIntercomUserData.Code, true, dataToSend)
//            .then(function (data) {
//                //any custom pre-processing of data should be handled here
//                dfd.resolve(data);
//            })
//            .fail(function (errorMessage) {
//                saveItemToSyncQueue();
//                //dfd.reject(errorMessage);
//            });
//    } else {
//        saveItemToSyncQueue();
//    }

//    function saveItemToSyncQueue() {
//        queueSyncJob(OPERATION.SendIntercomUserData.Code, Date.now(), undefined, dataToSend);
//        dfd.resolve();
//    }

//    return dfd.promise();
//}

//function sendEventInfoToIntercom(dataToSend) {
//    var dfd = $.Deferred();
//    if (globals.isOnline.value === true) {
//        // Add sync queue code
//        handleDataRequest(null, null, config.OPERATION.SendIntercomEventData.Code, true, dataToSend)
//            .then(function (data) {
//                //any custom pre-processing of data should be handled here                    
//                dfd.resolve(data);
//            })
//            .fail(function (errorMessage) {
//                saveItemToSyncQueue();
//                //dfd.reject(errorMessage);
//            });
//    } else {
//        saveItemToSyncQueue();
//    }

//    //checkIsOnline()
//    //    .then(function () {
//    //        // Add sync queue code
//    //        handleDataRequest(null, null, config.OPERATION.SendIntercomEventData.Code, true, dataToSend)
//    //            .then(function (data) {
//    //                //any custom pre-processing of data should be handled here                    
//    //                dfd.resolve(data);
//    //            })
//    //            .fail(function (errorMessage) {
//    //                saveItemToSyncQueue();
//    //                //dfd.reject(errorMessage);
//    //            });
//    //    })
//    //    .fail(function () {
//    //        saveItemToSyncQueue();
//    //    });

//    function saveItemToSyncQueue() {
//        queueSyncJob(OPERATION.SendIntercomEventData, Date.now(), {}, dataToSend);
//        dfd.resolve();
//    }
//    return dfd.promise();
//}

function loadProperties(isCurrentContext) {
    var dfd = $.Deferred();
    const current = () => !isCurrentContext || isCurrentContext();
    
    localDL.getFromCache(OPERATION.LastDebugLogUpload, PROPERTY.LastDebugLogUpload)
        .then(function (lastDebugLogUpload) {
            if (!current()) return dfd.reject({ cancelled: true });
            if (lastDebugLogUpload === undefined || lastDebugLogUpload === null) {
                lastDebugLogUploadTimeStamp = 0;
            } else {
                lastDebugLogUploadTimeStamp = Number(lastDebugLogUpload);
            }
            localDL.getFromCache(OPERATION.LastUsageLogUpload, PROPERTY.LastUsageLogUpload)
                .then(function (lastUsageLogUpload) {
            if (!current()) return dfd.reject({ cancelled: true });
                    if (lastUsageLogUpload === undefined || lastUsageLogUpload === null) {
                        lastUsageLogUploadTimeStamp = 0;
                    } else {
                        lastUsageLogUploadTimeStamp = Number(lastUsageLogUpload);
                    }
                    getAppConfig(isCurrentContext)
                        .then(function (appConfigData) {
            if (!current()) return dfd.reject({ cancelled: true });
                            if (appConfigData.Result.ReturnCode === 1) {
                                setConfigVals(appConfigData.AppConfig);
                            } else {
                                setConfigVals(config.defaultAppConfig);
                            }
                            updatePriorityFromConfigValues();
                            //config.debugLogCommunicationPeriod = 60;//hardcoded seconds = 1 min for testing, will be replace with app config values loaded from properties in due course. 
                            //config.loggingLevel = LOGGING_LEVEL.DETAILED;// hardcoded for testing, will be replaced with app config value loaded from properties...
                            //config.debugLogRetentionPeriod = 120;// hardcoded to 2 mins for testing etc
                            dfd.resolve();
                        })
                        .fail(function () {
                            if (!current()) return dfd.reject({ cancelled: true });
                            setConfigVals(config.defaultAppConfig);
                            updatePriorityFromConfigValues();
                            //config.debugLogCommunicationPeriod = 60;//hardcoded seconds = 1 min for testing, will be replace with app config values loaded from properties in due course. 
                            //config.loggingLevel = LOGGING_LEVEL.DETAILED;// hardcoded for testing, will be replaced with app config value loaded from properties...
                            //config.debugLogRetentionPeriod = 120;// hardcoded to 2 mins for testing etc
                            dfd.resolve();
                        });
            
                });

        });

    function setConfigVals(appConfigData) {
        config.loggingLevel = appConfigData.LoggingLevel;
        config.debugLogRetentionPeriod = appConfigData.LogRetentionPeriod;//in seconds
        config.debugLogCommunicationPeriod = appConfigData.LogCommunicationPeriod;//in seconds
        config.noOfDaysLicenceExpiryMessageShows = appConfigData.NoOfDaysLicenceExpiryMessageShows;
        config.isOnlineCheckPeriod = appConfigData.IsOnlineCheckPeriod;
        config.isOnlineTimeout = appConfigData.IsOnlineTimeout;
        config.isOnlineLowPriorityValidityPeriod = appConfigData.IsOnlineLowPriorityValidityPeriod;
        config.isOnlineMediumPriorityValidityPeriod = appConfigData.IsOnlineMediumPriorityValidityPeriod;
        config.isOnlineHighPriorityValidityPeriod = appConfigData.IsOnlineHighPriorityValidityPeriod;
        config.postRequestTimeout = appConfigData.PostRequestTimeout;
        config.getRequestTimeout = appConfigData.GetRequestTimeout;
        config.usageCommunicationPeriod = appConfigData.UsageCommunicationPeriod;//in seconds
        config.maxNewOffersCached = appConfigData.MaxNewOffersCached;
        config.maxSavedOffersCached = appConfigData.MaxSavedOffersCached;

        globals.appConfigApplied = true;
       
    }

    return dfd.promise();
}



/**
* Gets the app logos if they are stored locally or kicks off a job to retrieve them if they are not
* @method loadLocallyOrRetrieveAndStoreAppLogos
*/
function loadLocallyOrRetrieveAndStoreAppLogos(isCurrentContext) {
    var dfd = $.Deferred();
    const current = () => !isCurrentContext || isCurrentContext();
    
    if (localDL.isDBReady() === false) {
        setupIndexedDB
            .then(function (success) {
                doLoadLocallyOrRetrieveAndStoreAppLogos();
            })
            .fail(function (failure) {
                dfd.reject(failure);
            });
    } else {
        doLoadLocallyOrRetrieveAndStoreAppLogos();
    }

    function doLoadLocallyOrRetrieveAndStoreAppLogos() {
        if (!current()) return dfd.reject({ cancelled: true });
        var headerUrl = config.getAppHeaderURL();
        var defaultReportFooterUrl = config.defaultReportFooterURL();
        var defaultReportHeaderUrl = config.defaultReportHeaderURL();
        
        var appImageUrls = [];
        $.when(localDL.getImageIfInDB(headerUrl),
            localDL.getImageIfInDB(defaultReportFooterUrl),
            localDL.getImageIfInDB(defaultReportHeaderUrl))
            .done(function (r1, r2, r3) {
                if (!current()) return dfd.reject({ cancelled: true });
                var pack = localDL.PACK.Graphic;
                if (typeof r1 === 'string') {
                    appImageUrls.push(r1);
                } else {
                    config.appHeaderURL.value = r1[1];
                }

                if (typeof r2 === 'string') {
                    appImageUrls.push(r2);
                } else {
                    config.setDefaultReportFooterURL(r2[1]);
                }

                if (typeof r3 === 'string') {
                    appImageUrls.push(r3);
                } else {
                    config.setDefaultReportHeaderURL(r3[1]);
                }
                
                dfd.resolve();
                if (appImageUrls.length > 0) {
                    loadAllImages(appImageUrls, pack, function (allImagesDownloaded) {
                        if (current() && allImagesDownloaded === false) {
                            globals.appLogosDownloaded = false;
                        }
                    });
                }
            });
    }
    
    return dfd.promise();
}



/**
    * Wraps two method calls to combine the process of retrieving, converting and then storing images as dataUrls in the DB.
    * @method convertAndAddImageToDB
    * @param {String} url The url of the image to be retrieved and stored.
    * @param {String} pack The pack that the image is associated with.
    */
function convertAndAddImageToDB(url, pack) {
    var dfd = $.Deferred();

    var imageFormat = getImageFormat(url);
    convertImgToBase64(url, imageFormat)
        .then(function (dataUrl) {
            if (dataUrl) {
                localDL.putImageInDb(dataUrl, url, pack)
                .then(function () {
                    alreadyLoadedImages.push(url);
                    dfd.resolve();
                })
                .fail(function () {
                    dfd.reject();
                });
            } else {
                dfd.reject();
            }
            
        });

    return dfd.promise();
}

/**
    * Helper function for extracting the image file format from a given image url.
    * @method getImageFormat
    * @param {String} url The url of the image to figure out the format of.
    */
function getImageFormat(url) {
    var imageFormat, urlParts, fileExt;

    urlParts = url.split("/");
    fileExt = ((urlParts[urlParts.length - 1]).split("."))[1];
    switch (fileExt) {
        case "png":
            imageFormat = "image/png";
            break;
        case "jpg":
        case "jpeg":
            imageFormat = "image/jpeg";
            break;
        default:
            imageFormat = "image/png";
    }
    return imageFormat;
}

/**
    * Determines whether or not the app needs to diaplay a warning about the number of licence days the user has left, based on the Licence Lease Expiry Date stored in the DB.
    * @method checkLicenceDays
    * @return {Boolean} Returns true or false depending on whether licence warning should be displayed. If true it also returns the number of day remaining. 
    */
function checkLicenceDays() {
    // check stored value for when licence will expire to determine if the user needs to be notified
    var dfd = $.Deferred();

    localDL.getFromCache(OPERATION.LicenceLease, localDL.PROPERTY.LicenceLeaseExpiryDate)
        .then(function (storedData) {
            if (storedData) {
                var now = Date.now();
                var remaining = storedData - now;
                var days = Math.round(remaining / config.millisInDay);
                if (days >= 0 && days <= config.noOfDaysLicenceExpiryMessageShows) {
                    dfd.resolve(true, days);
                } else {
                    dfd.resolve(false);
                }   
            }
        });

    return dfd.promise();
}

/**
* Wrapper function for accessing localDL cache.
* @method retrieve
* @param {String} operation The operation being carried out.
* @param {String} keyOrProp The key of the data or name of the property to be retrieved.
*/
function retrieve(operation, keyOrProp) {
    
    switch (operation) {
        case OPERATION.ConfigDrawing:
            return retrieveImage(keyOrProp);
        default:
            return localDL.getFromCache(operation, keyOrProp).promise();
    }
    
}

/**
    * Wraps localDL getImageIfInDB without trying to download image if not found.
    * @method retrieveImage
    * @param {String} imageKeyOrUrl The key/url of the image to be retrieved.
    */
function retrieveImage(imageKeyOrUrl) {
    var dfd = $.Deferred();

    localDL.getImageIfInDBForConfigDrawing(imageKeyOrUrl)
        .then(function(urlOrKey, imageDataUrl){
            if(imageDataUrl === undefined) {
                dfd.resolve(null);
            }else {
                dfd.resolve(imageDataUrl);
            }
        });

    return dfd.promise();
}



/**
    * Handles version management for graphic, map, common, vehicle, company and onRoadCosts packs/distribution groups. The lastest version information for each of these packs is sent down everytime there is an 
    * authorise. The latest version value is checked against the locally stored value and if the version has increased one or more actions is carried out such as purging old data and downloading the new data.
    * @method handleVersions
    * @param {Object} latestVersions The latest version numbers for all of the packs handled by version control.
    */
function handleVersions(latestVersions, isCurrentContext) {
    var dfd = $.Deferred();
    const versionOwner = security.getIncarnation();
    const versionCurrent = () => security.incarnationMatches(versionOwner) && (!isCurrentContext || isCurrentContext());
    if (!versionCurrent()) return dfd.reject({ cancelled: true }).promise();
    const cacheOwner = isCurrentContext ? sessionCacheOwner(versionOwner, versionCurrent) : undefined;
    TScMessenger.writeTimerMessage('In handleVersions');
    retrieve(OPERATION.LatestVersions, localDL.PROPERTY.Versions)
        .then(function (storedData) {
            if (!versionCurrent()) return dfd.reject({ cancelled: true });
            var handleVersionsPromises = [];
            var slowTasks = [];
            var needToSaveVersions = false;
            var isFirstTime = true;
            var request;
            if (storedData) {
                TScMessenger.writeTimerMessage('In handleVersions, has stored data');
                isFirstTime = false;
                

                var latestVal = getVersionVal(latestVersions, "graphicVersion");
                var curStoredVal = getVersionVal(storedData, "graphicVersion");
                if (curStoredVal < latestVal) {
                    log("Graphic version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    forceGraphicsRefresh(localDL.PACK.Graphic, versionCurrent);
                    replaceReportImageUrls(undefined, versionCurrent).then(function () {
                        if (!versionCurrent()) return dfd.reject({ cancelled: true });
                        globals.user.updateUser({ reportGraphics: latestReportGraphicsFromLogin });
                    });
                }

                latestVal = getVersionVal(latestVersions, "mapVersion");
                curStoredVal = getVersionVal(storedData, "mapVersion");
                if (curStoredVal < latestVal) {
                    log("Map version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    localDL.clearStore(STORE.NewOffers, cacheOwner);
                    localDL.resetVehiclesOfferCachedFlag(cacheOwner);
                }
                
                latestVal = getVersionVal(latestVersions, "vehicleVersion");
                curStoredVal = getVersionVal(storedData, "vehicleVersion");
                if (curStoredVal < latestVal) {
                    log("Vehicle version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    
                    request = 'offers/newoffers?application=&vehicleType=&vehicleTypeCode=&axleLayout=&make=&range=&pageNumber=1&pageSize=30';
                    localDL.clearStore(STORE.Vehicles, cacheOwner);
                    localDL.clearStore(STORE.NewOffers, cacheOwner);
                    localDL.clearProperty(PROPERTY.VehiclePackData, cacheOwner);
                    if(globals.selectionListDfd === null) {
                        globals.selectionListDfd = $.Deferred();
                    }
                    doingBackgroundSelectionCaching.value = true;
                    doingBackgroundSelectionRetrieval.value = true;
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetSelectionList, true, undefined, versionCurrent).then(function (temp) {
                        if (!versionCurrent()) return dfd.reject({ cancelled: true });
                        selectionListCallback(temp);
                        doingBackgroundSelectionRetrieval.value = false;
                        if(globals.selectionListDfd !== null) {
                            globals.selectionListDfd.resolve({ selection: JSON.parse(JSON.stringify(temp))});
                            globals.selectionListDfd = null;
                        }
                    }));
                    
                }

                TScMessenger.writeDebugMessage('DataManager.js, handleVersions, checking Common Version');
                latestVal = getVersionVal(latestVersions, "commonVersion");
                curStoredVal = getVersionVal(storedData, "commonVersion");
                if (curStoredVal < latestVal) {
                    TScMessenger.writeDebugMessage('DataManager.js, handleVersions, Common Version changed, clearing local cache entries and retrieving fresh');
                    log("Common version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    
                    localDL.clearProperty(PROPERTY.CommonPackDetails, cacheOwner);
                    localDL.clearProperty(PROPERTY.LegislationDetails, cacheOwner);
                    localDL.clearStore(STORE.NewOffers, cacheOwner);
                    localDL.clearProperty(PROPERTY.LicenceCategories, cacheOwner);

                    request = 'commonpackdetails/get?customerDistributionGroupId=' + globals.user.getCdg() + "&databaseAttributeIds=" + config.databaseAttributesForTranslation;
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetCommonPackDetails, true, undefined, versionCurrent));
                    
                    if (globals.user.hasPermission(config.PERMISSIONS.COSTING.Code)) {

                        request = OPERATION.GetLicenceCategories.BaseUrl + '?companyId=' + globals.user.getCompanyId();
                        handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetLicenceCategories.Code, true, undefined, versionCurrent));

                    }
                }

                latestVal = getVersionVal(latestVersions, "onroadVersion");
                curStoredVal = getVersionVal(storedData, "onroadVersion");
                if (curStoredVal < latestVal) {
                    log("OnRoad version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    request = 'onroadcosts/get?customerDistributionGroupId=' + globals.user.getCdg();
                    localDL.clearProperty(PROPERTY.OnRoadCosts, cacheOwner);
                    localDL.clearStore(STORE.NewOffers, cacheOwner);
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetOnRoadCosts, true, undefined, versionCurrent));
                }

                latestVal = getVersionVal(latestVersions, "appConfigVersion");
                curStoredVal = getVersionVal(storedData, "appConfigVersion");
                if (curStoredVal < latestVal) {
                    log("AppConfig version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    request = OPERATION.GetAppConfig.BaseUrl;
                    localDL.clearProperty(PROPERTY.AppConfig, cacheOwner);
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetAppConfig.Code, true, undefined, versionCurrent));
                }

                latestVal = getVersionVal(latestVersions, "accessoryVersion");
                curStoredVal = getVersionVal(storedData, "accessoryVersion");
                if (curStoredVal < latestVal) {
                    log("Accessory version changed ", null, 'dataManager', config.LOG_MESSAGE_TYPE.INFO);
                    needToSaveVersions = true;
                    
                    localDL.clearStore(STORE.Bodies, cacheOwner);
                    localDL.clearStore(STORE.Accessories, cacheOwner);
                    localDL.clearStore(STORE.Trailers, cacheOwner);
                    localDL.clearStore(STORE.Payloads, cacheOwner);
                    localDL.clearProperty(PROPERTY.BodyMasses, cacheOwner);
                    doingBackgroundBodiesRetrieval.value = true;
                    doingBackgroundBodyStubsCaching.value = true;
                    
                    request = 'accessories/allbodystubs';
                    slowTasks.push(getNewTask(OPERATION.GetBodyStubs, request));

                    doingBackgroundAccessoriesRetrieval.value = true;
                    doingBackgroundAccessoriesStubsCaching.value = true;
                    
                    request = 'accessories/allaccessorystubs';
                    slowTasks.push(getNewTask(OPERATION.GetAccessoryStubs, request));

                    doingBackgroundTrailersRetrieval.value = true;
                    doingBackgroundTrailerStubsCaching.value = true;
                    request = 'accessories/alltrailerstubs';
                    slowTasks.push(getNewTask(OPERATION.GetTrailerStubs, request));

                    doingBackgroundPayloadsRetrieval.value = true;
                    doingBackgroundPayloadStubsCaching.value = true;
                    request = 'accessories/allpayloadstubs';
                    slowTasks.push(getNewTask(OPERATION.GetPayloadStubs, request));

                    request = OPERATION.GetBodyMasses.BaseUrl;
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetBodyMasses.Code, true, undefined, versionCurrent));
                    
                }
            } else {
                TScMessenger.writeTimerMessage('In handleVersions, no stored data');
                needToSaveVersions = true;
                if(globals.selectionListDfd === null) {
                    globals.selectionListDfd = $.Deferred();
                }
                doingBackgroundSelectionCaching.value = true;
                doingBackgroundSelectionRetrieval.value = true;
                request = 'offers/newoffers?application=&vehicleType=&vehicleTypeCode=&axleLayout=&make=&range=&pageNumber=1&pageSize=30';
                handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetSelectionList, true, undefined, versionCurrent).then(function (temp) {
                    if (!versionCurrent()) return dfd.reject({ cancelled: true });
                    selectionListCallback(temp);
                    doingBackgroundSelectionRetrieval.value = false;
                    if(globals.selectionListDfd !== null) {
                        globals.selectionListDfd.resolve({ selection: JSON.parse(JSON.stringify(temp))});
                        globals.selectionListDfd = null;
                    }
                }));
                
                request = 'commonpackdetails/get?customerDistributionGroupId=' + globals.user.getCdg() + "&databaseAttributeIds=" + config.databaseAttributesForTranslation;
                handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetCommonPackDetails, true, undefined, versionCurrent));
                
                request = 'onroadcosts/get?customerDistributionGroupId=' + globals.user.getCdg();
                handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetOnRoadCosts, true, undefined, versionCurrent));
                
                request = OPERATION.GetAppConfig.BaseUrl;
                handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetAppConfig.Code, true, undefined, versionCurrent));
                
                doingBackgroundBodiesRetrieval.value = true;
                doingBackgroundBodyStubsCaching.value = true;
                
                request = 'accessories/allbodystubs';
                slowTasks.push(getNewTask(OPERATION.GetBodyStubs, request));

                doingBackgroundAccessoriesRetrieval.value = true;
                doingBackgroundAccessoriesStubsCaching.value = true;
                
                request = 'accessories/allaccessorystubs';
                slowTasks.push(getNewTask(OPERATION.GetAccessoryStubs, request));
                
                doingBackgroundTrailersRetrieval.value = true;
                doingBackgroundTrailerStubsCaching.value = true;
                request = 'accessories/alltrailerstubs';
                slowTasks.push(getNewTask(OPERATION.GetTrailerStubs, request));

                doingBackgroundPayloadsRetrieval.value = true;
                doingBackgroundPayloadStubsCaching.value = true;
                request = 'accessories/allpayloadstubs';
                slowTasks.push(getNewTask(OPERATION.GetPayloadStubs, request));

                doingBackgroundSharedOfferStubsRetrieval.value = true;
                doingBackgroundSharedOfferStubsCaching.value = true;
                request = OPERATION.GetSharedOfferStubs.BaseUrl;
                slowTasks.push(getNewTask(OPERATION.GetSharedOfferStubs.Code, request));

                request = OPERATION.GetBodyMasses.BaseUrl;
                handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetBodyMasses.Code, true, undefined, versionCurrent));

                if (globals.user.hasPermission(config.PERMISSIONS.COSTING.Code)) {

                    request = OPERATION.GetLicenceCategories.BaseUrl + '?companyId=' + globals.user.getCompanyId();
                    handleVersionsPromises.push(handleDataRequest(null, request, OPERATION.GetLicenceCategories.Code, true, undefined, versionCurrent));
                }
            }

            if (needToSaveVersions === true) {
                handleVersionsPromises.push(localDL.addToCache(OPERATION.LatestVersions, localDL.PROPERTY.Versions, latestVersions, cacheOwner));

            }

            

            if (handleVersionsPromises.length > 0) {
                $.when.apply($, handleVersionsPromises)
                //performTasks(handleVersionsPromises)
                    .done(function () {
                        if (!versionCurrent()) return dfd.reject({ cancelled: true });
                        if (doingBackgroundOverviewRetrieval.value === true) {
                            doingBackgroundOverviewRetrieval.value = false;
                        }
                        if (isFirstTime === true) {
                            replaceReportImageUrls(undefined, versionCurrent).then(function () {
                                if (!versionCurrent()) return dfd.reject({ cancelled: true });
                                globals.user.updateUser({ reportGraphics: latestReportGraphicsFromLogin });
                            });
                        }
                        performTasks(slowTasks);
                        standardHandleVersionsTasksComplete.value = true;
                        dfd.resolve(needToSaveVersions);//passing need to save versions back to caller, true means there has been data/config updates
                    })
                    .fail(function (error) {
                        if (!versionCurrent()) return dfd.reject({ cancelled: true });
                        if(globals.selectionListDfd !== null) {
                            // globals.selectionListDfd.reject(error);???
                            globals.selectionListDfd = null;
                        }
                        dfd.reject(error);
                    });
            } else {
                standardHandleVersionsTasksComplete.value = true;
                slowHandleVersionsTasksComplete.value = true;
                dfd.resolve(needToSaveVersions);
            }
            
        }).fail(error => dfd.reject(error));

    return dfd.promise();

    function performTasks(tasks) {
        var taskDfd = $.Deferred();

        //var tasks = [];
        //addTask(dataManager.loadProperties);
        //addTask(dataManager.doDebugLogMaintenanceIfRequired);

        doTask(tasks);

        function doTask(taskArr) {
            if (!versionCurrent()) return taskDfd.reject({ cancelled: true });
            if (taskArr.length > 0) {
                var curTask = taskArr.shift();
                curTask.taskFunc().then(function (data) {
                    if (!versionCurrent()) return taskDfd.reject({ cancelled: true });
                    switch (curTask.op) {
                        case OPERATION.GetSelectionList:
                            selectionListCallback(data);
                            break;
                        case OPERATION.GetBodyStubs:
                            bodiesResult = data;
                            doingBackgroundBodiesRetrieval.value = false;
                            break;
                        case OPERATION.GetAccessoryStubs:
                            accessoriesResult = data;
                            doingBackgroundAccessoriesRetrieval.value = false;
                            break;
                        case OPERATION.GetTrailerStubs:
                            trailersResult = data;
                            doingBackgroundTrailersRetrieval.value = false;
                            break;
                        case OPERATION.GetPayloadStubs:
                            payloadsResult = data;
                            doingBackgroundPayloadsRetrieval.value = false;
                            break;
                        case OPERATION.GetSharedOfferStubs.Code:
                            sharedOfferStubsResult = data;
                            doingBackgroundSharedOfferStubsRetrieval.value = false;
                            sharedOfferSyncHandlerCallback(data);
                            break;
                    }
                    doTask(taskArr);
                }).fail(error => taskDfd.reject(error));
            } else {
                slowHandleVersionsTasksComplete.value = true;
                taskDfd.resolve();
            }

        }

        //function addTask(task) {
        //    tasks.push(function () { return task(); });
        //}

        return taskDfd.promise();
    }

    function getNewTask(op, request) {
        return { op: op, taskFunc: function () { return handleDataRequest(null, request, op, true, undefined, versionCurrent); } };
    }
}

function selectionListCallback(data) {
    selectionListResult = data;
}

function setOfflineValidUntilIfNeeded() {
    var dfd = $.Deferred();

    if (navigator.onLine === false) {
        setAppropriateValidUntil().then(function () {
            dfd.resolve();
        });
    } else {
        dfd.resolve();
    }

    return dfd.promise();
}

function setAppropriateValidUntil() {
    var dfd = $.Deferred();
    
    localDL.getFromCache(OPERATION.LicenceLease, localDL.PROPERTY.LicenceLeaseExpiryDate)
                    .then(function (storedData) {
                        if (storedData) {
                            var date = new Date(parseInt(storedData));
                            globals.user.setValidUntil(moment(date).format("M/D/YYYY h:mm:ss a"), "offline");
                        }
                        dfd.resolve();
                    }).fail(function () {
                        dfd.resolve();
                    });
    return dfd.promise();
}

/**
    * Helper function for handleVersions to retrieve a specific version value from the versions object.
    * @method getVersionVal
    * @param {Object} objectGroup The object containing the version values.
    * @param {String} keyToGet The specific version value to get.
    * @return {Number} Returns the requested version value or null if it can't find it. 
    */
function getVersionVal(objectGroup, keyToGet) {
    for (var key in objectGroup) {
        if (objectGroup[key]) {
            if (typeof objectGroup[key] === 'object') {
                if (objectGroup[key]["Key"] === keyToGet) {
                    return objectGroup[key]["Value"];
                }
                
            }
        }
    }
    return null;
}

/**
    * Wrapper function for caching items to localDL cache.
    * @method persist
    * @param {String} operation The operation being carried out.
    * @param {Object} data The data to be cached.
    * @param {String} propOrKey The key to cache the data under.
    */
function persist(operation, data, propOrKey, owner) {

    switch (operation) {
        case OPERATION.ConfigDrawing:
            return localDL.putConfigDrawingInDb(data, propOrKey, localDL.PACK.Vehicle);
        default:
            localDL.addToCache(operation, propOrKey, data, owner);
            break;
    }
    
}

/**
    * Refreshes all graphics for a given pack by first deleting all graphics and then kicing off a job to retrieve and store all the images again.
    * @method forceGraphicsRefresh
    * @param {String} pack The pack whose graphics are to be refreshed.
    */
function forceGraphicsRefresh(pack, isCurrentContext) {
    var urlsArr = [];
    localDL.getObjectStore(localDL.STORE.Images, "readonly")
        .then(function (objectStore) {
            if (isCurrentContext && !isCurrentContext()) return;
            var cursorRequest = objectStore.openCursor();
            cursorRequest.onsuccess = function (event) {
                if (isCurrentContext && !isCurrentContext()) return;
                if (event.target.result) {
                    if (event.target.result.value["pack"] === pack) {
                        urlsArr.push(event.target.result.value["imageurl"]);
                    }
                    event.target.result['continue']();
                } else {
                    if (urlsArr.length > 0) {
                        loadAllImages(urlsArr, pack);
                    }
                }
            };

            cursorRequest.onerror = function (event) {

            };

        });
}

/**
    * Wrapper method for removing items/records from localDL cache. Please note currently only supports removing the configuration drawing.
    * @method remove
    * @param {String} operation The operation being carried out.
    * @param {String} propOrKey The key or property name of the item to be removed.
    */
function remove(operation, propOrKey) {
    switch (operation) {
        case OPERATION.ConfigDrawing:
            return localDL.deleteRecord(localDL.STORE.Images, propOrKey);
        case OPERATION.ClearPartialNewOfferDetails:
            return localDL.deleteRecord(localDL.STORE.NewOffers, propOrKey);
        case OPERATION.RemoveLocalBody:
            return localDL.deleteRecord(localDL.STORE.Bodies, propOrKey);
        case OPERATION.RemoveLocalAccessory:
            return localDL.deleteRecord(localDL.STORE.Accessories, propOrKey);
        case OPERATION.RemoveLocalTrailer:
            return localDL.deleteRecord(localDL.STORE.Trailers, propOrKey);
        case OPERATION.RemoveLocalPayload:
            return localDL.deleteRecord(localDL.STORE.Payloads, propOrKey);
        default:
            break;
    }
}

function isOutsideAuthentication(op) {
    if (op === OPERATION.SignUp || op === OPERATION.RequestTrialExtension || 
        op === OPERATION.PostLoginDebug || op === OPERATION.CheckIsOnline.Code || 
        op === OPERATION.PostScreenSizeDebug || op === OPERATION.ValidateAccessToken) {
        return true;
    }
    return false;
}

/**
    * Method that manages retrieving data. 
    * @method handleDataRequest
    * @param {Function} local The localDL function call to be used to retrieve the data locally.
    * @param {String} request The request url to use to retrieve the data online.
    * @param {String} operation The operation bing carried out.
    * @param {Boolean} forceOnline A boolean flag indicating whether or not the operation should skip the local check and do online check only.
    * @param {Object} optionalData The optional payload data to be sent up in a POST.
    * @return {Object} Returns data requested from either local cache or online operation, or error if both operations fail. 
    */
function handleDataRequest(local, request, operation, forceOnline, optionalData, isCurrentContext) {
    var dfd = $.Deferred();
    let networkCounted = false, countedGeneration;
    function finishNetwork() {
        if (networkCounted && countedGeneration === networkRequestGeneration) networkRequestsUnderway = Math.max(0, networkRequestsUnderway - 1);
        networkCounted = false;
    }
    dfd.always(finishNetwork);
    if (libraryRecoveryRequired && !libraryTransitionActive && !isOutsideAuthentication(operation))
        return dfd.reject({ libraryRecoveryRequired: true }).promise();
    const releaseLibraryWork = security.registerCancellation?.(() => dfd.reject({ cancelled: true }));
    if (releaseLibraryWork) dfd.always(releaseLibraryWork);
    const requestOwner = security.getIncarnation();
    let responseProof = null;
    const requestCurrent = error => {
        const current = security.incarnationMatches(requestOwner) && (!responseProof || security.sameAuthority(responseProof)) && (!isCurrentContext || isCurrentContext(error));
        if (!current) dfd.reject({ cancelled: true });
        return current;
    };
    if (!requestCurrent()) return dfd.promise();
    const requestCacheOwner = isCurrentContext ? sessionCacheOwner(requestOwner, requestCurrent) : undefined;

    if ((typeof operation === 'string' && operation === config.OPERATION.SendIntercomEventData.Code && optionalData && optionalData.eventName && optionalData.eventName === config.INTERCOM_EVENT.LOGGED_OUT) || isOutsideAuthentication(operation) === true || !security.isRememberedSuppressed()) {
        //if (curOpTracker !== undefined) {
        //    curOpTracker.notifyRetrievalStarted(operation);
        //}
        if (forceOnline && forceOnline === true) {
            //call remote, if it fails then resolve error message
            
            tryRemote();
        } else {
            //call local/see if local available, if not call remote and if that fails resolve error message
            local()
                .then(handleSuccess)
                .fail(tryRemote);
        }
    } else {
        dfd.reject({errorMessage: 'unauthorised'});
    }
    
    function handleSuccess(data) {
        if (!requestCurrent()) return;
        if ((data === undefined) || (data === null)) {
            tryRemote();
        } else {
            switch (operation) {
                case OPERATION.GetNewOfferDetails:
                    if (data.Offer.Brochure) {
                        dfd.resolve(data, true);
                    } else {
                        dfd.resolve(data);
                    }
                    
                    break;
                case OPERATION.GetNewOfferSpecificationDetails:
                    if (data.Specification === undefined) {
                        tryRemote();
                    } else {
                        dfd.resolve(data, true);
                    }
                    break;
                case OPERATION.GetExistingOfferDetails:
                case OPERATION.GetSharedOfferDetails.Code:
                    if (data.Offer.Brochure) {
                        dfd.resolve(data, true);
                    } else {
                        dfd.resolve(data);
                    }
                    break;
                    
                case OPERATION.GetExistingOfferSpecificationDetails:
                    if (data.Specification === undefined) {
                        tryRemote();
                    } else {
                        dfd.resolve(data, true);
                    }
                    break;
                case OPERATION.GetSelectionList:
                case OPERATION.GetSharedOfferStubs.Code:
                    if (data?.Result?.ReturnCode === -1) {
                        tryRemote();
                    } else if (selectionResponseError(data, ['Offers'])) {
                        dfd.reject(selectionResponseError(data, ['Offers']));
                    } else {
                        dfd.resolve(data, true);
                    }
                    break;
                //case OPERATION.GetBodyStubs:
                //    if (data.Result.ReturnCode === -1 && globals.isOnline.value === true) {
                //        tryRemote();
                //    } else {
                //        dfd.resolve(data);
                //    }
                //    break;
                //case OPERATION.GetBody:
                //    if (data.cached === undefined || data.cached === 0) {
                //        if (globals.isOnline.value === true) {
                //            tryRemote();
                //        } else {
                //            dfd.reject({ errorMessage: 'Offline' });
                //        }
                        
                //    } else {
                //        dfd.resolve(data);
                //    }
                //    break;
                case OPERATION.GetBodyStubs:
                case OPERATION.GetAccessoryStubs:
                case OPERATION.GetTrailerStubs:
                case OPERATION.GetPayloadStubs:
                    if (data.Result.ReturnCode === -1 && globals.isOnline.value === true) {
                        tryRemote();
                    } else {
                        dfd.resolve(data);
                    }
                    break;
                case OPERATION.GetBody:
                case OPERATION.GetAccessory:
                case OPERATION.GetTrailer:
                case OPERATION.GetPayload:
                    if (data.cached === undefined || data.cached === 0) {
                        if (globals.isOnline.value === true) {
                            tryRemote();
                        } else {
                            dfd.reject({ errorMessage: 'Offline' });
                        }

                    } else {
                        dfd.resolve(data);
                    }
                    break;
                default:
                    dfd.resolve(data);
                    break;
            }
            
        }
        
    }

    function tryRemote() {
        if (!requestCurrent()) return;

        function updateCachedStatusOfLocalNewOfferObject(vehicleId) {
        if (!requestCurrent()) return;
                        
            localDL.getFromCache(OPERATION.GetNewOfferDetails, vehicleId)
            .then(function (offerData) {
        if (!requestCurrent()) return;
                if (offerData !== null) {
                    offerData.Complete = 2;
            //{Type: '', Id: 0, Value: 0}

                var tempOp;
                if (globals.user.hasPermission(config.PERMISSIONS.SPECIFICATION.Code)) {
                    tempOp = OPERATION.GetNewOfferSpecificationDetails;
                } else {
                    tempOp = OPERATION.GetNewOfferDetails;
                }
                localDL.addToCache(tempOp, vehicleId, offerData, requestCacheOwner);
                }
                
            });
        }
        function updateCachedStatusOfLocalExistingOfferObject(offerId) {
        if (!requestCurrent()) return;
            localDL.getFromCache(operation, offerId)
            .then(function (offerData) {
        if (!requestCurrent()) return;
                if (offerData !== null) {
                    offerData.Complete = 2;


                    var tempOp;
                    if (globals.user.hasPermission(config.PERMISSIONS.SPECIFICATION.Code)) {
                        tempOp = OPERATION.GetExistingOfferSpecificationDetails;
                    } else {
                        tempOp = operation;
                    }

                    localDL.addToCache(tempOp, offerId, offerData, requestCacheOwner);
                }
            });
        }

        countedGeneration = networkRequestGeneration;
        networkCounted = true;
        networkRequestsUnderway++;
        switch (operation) {
            case OPERATION.GetNewOfferDetails:
                allNewOfferImagesRetrieved.value = false;
                allNewSpecImagesRetrieved.value = false;
                newOfferDataCached.value = false;
                newSpecDataCached.value = false;

                newOfferDataAndImagesCachedSubscriptionRef = watch(allNewOfferAndSpecImagesAndDataCached, function (newValue) {
        if (!requestCurrent()) return;
                    if (newValue === true) {
                        newOfferDataAndImagesCachedSubscriptionRef();
                        newOfferDataAndImagesCachedSubscriptionRef = null;
                        var vehicleId = selectedVehicleId;
                        recentlyCached.value = { Type: 'NEW', Id: selectedVehicleId, Value: 2 };
                        allNewOfferImagesRetrieved.value = false;
                        allNewSpecImagesRetrieved.value = false;
                        newOfferDataCached.value = false;
                        newSpecDataCached.value = false;
                        if (doingBackgroundSelectionCaching.value === false) {
                            updateCachedStatusOfLocalNewOfferObject(vehicleId);
                        } else {
                            if (selectionListResult !== null) {
                                try {
                                    //selection.Offers
                                    selectionListResult.Offers.forEach(function (offer) {
        if (!requestCurrent()) return;
                                        if (offer.Id === vehicleId) {
                                            offer.OfferCached = 2;
                                        }
                                    });
                                }catch(selectionListUpdateEx) {
                                    TScMessenger.writeDebugMessage('DataManager.js, tryRemote, switch, OPERATION.GetNewOfferDetails, error updating cached on selection list in DM');
                                }
                            }
                            var doingBackgroundSelectionCachingSubscriptionRef = watch(doingBackgroundSelectionCaching, function (newValue) {
        if (!requestCurrent()) return;
                                if (newValue === false) {
                                    doingBackgroundSelectionCachingSubscriptionRef();
                                    doingBackgroundSelectionCachingSubscriptionRef = null;
                                    updateCachedStatusOfLocalNewOfferObject(vehicleId);
                                }
                            });
                        }

                        
                        

                    }
                });
                break;
            case OPERATION.GetExistingOfferDetails:
            case OPERATION.GetSharedOfferDetails.Code:
                allExistingOfferImagesRetrieved.value = false;
                allExistingSpecImagesRetrieved.value = false;
                existingOfferDataCached.value = false;
                existingSpecDataCached.value = false;
                allOriginalAccessoriesRetrievedObv.value = false;
                existingOfferDataAndImagesCachedSubscriptionRef = watch(allExistingOfferAndSpecImagesAndDataCached, function (newValue) {
        if (!requestCurrent()) return;
                    if (newValue === true) {
                        existingOfferDataAndImagesCachedSubscriptionRef();
                        existingOfferDataAndImagesCachedSubscriptionRef = null;
                        var offerId = selectedOfferId;
                        recentlyCached.value = { Type: 'SAVED', Id: selectedOfferId, Value: 2 };
                        allExistingOfferImagesRetrieved.value = false;
                        allExistingSpecImagesRetrieved.value = false;
                        existingOfferDataCached.value = false;
                        existingSpecDataCached.value = false;
                        allOriginalAccessoriesRetrievedObv.value = false;
                        if (doingBackgroundOfferCaching.value === false) {
                            //if (operation === OPERATION.GetExistingOfferDetails) {
                                updateCachedStatusOfLocalExistingOfferObject(offerId);
                            //}
                        } else {
                            if (operation !== OPERATION.GetExistingOfferDetails) {
                                if (sharedOfferStubsResult !== null) {
                                    try {
                                        sharedOfferStubsResult.Offers.forEach(function (offerStub) {
        if (!requestCurrent()) return;
                                            if (offerStub.Id === offerId) {
                                                offerStub.OfferCached = 2;
                                            }
                                        })
                                    }catch(sharedOfferStubsUpdateEx) {
                                        TScMessenger.writeDebugMessage('DataManager.js, tryRemote, switch, OPERATION.GetSharedOfferDetails, error updating cached on shared offers list in DM');
                                    }
                                }
                            } 
                            var doingBackgroundOfferCachingSubscriptionRef = watch(doingBackgroundOfferCaching, function (newValue) {
        if (!requestCurrent()) return;
                                if (newValue === false) {
                                    doingBackgroundOfferCachingSubscriptionRef();
                                    doingBackgroundOfferCachingSubscriptionRef = null;
                                    //if (operation === OPERATION.GetExistingOfferDetails) {
                                        updateCachedStatusOfLocalExistingOfferObject(offerId);
                                    //}
                                }
                            });
                        }

                        
                        

                    }
                });
                break;
        }
        remoteDL.handleRequest(security, request, operation, optionalData)
            .then(handleRemoteSuccess)
            .fail(handleRemoteFailure);

        
    }

    function handleRemoteSuccess(data) {
        finishNetwork();
        responseProof = security.captureProof();
        if (!requestCurrent()) return;
        try {
            var dontResolve = false;
            var forceCacheComplateWhenNoBrochure = false;
            //if (curOpTracker !== undefined) {
            //    curOpTracker.notifyRetrievalFinished(operation);
            //}
            var tempItems, tempItem;
            var i, j;
            switch (operation) {
                case OPERATION.GetNewOfferDetails:
                    if (data.Result.ReturnCode > 0) {
                        if (data.Offer && data.Offer.Brochure) {
                            
                            var newOfferUrlsToRetrieve = [];
                            // for (i = 0; i < data.Offer.Brochure.VehicleBrochureItemGroups.length; i++) {
                            //     tempItems = data.Offer.Brochure.VehicleBrochureItemGroups[i].VehicleBrochureItems;
                            //     for (j = 0; j < tempItems.length; j++) {
                            //         tempItem = tempItems[j];
                            //         if (tempItem["HTML"] !== undefined) {
                            //             newOfferUrlsToRetrieve.push(getImageSrc(tempItem["HTML"]));
                            //         }
                            //     }
                            // }
                            // for (i = 0; i < data.Offer.Brochure.VehicleBrochureItemGroups.length; i++) {
                            //     newOfferUrlsToRetrieve.push(data.Offer.Brochure.VehicleBrochureItemGroups[i].IconPictureURL);
                            // }
                            newOfferUrlsToRetrieve.push(data.Offer.VehiclePlusBodyImageURL);
                            loadAllImages(newOfferUrlsToRetrieve, localDL.PACK.Vehicle, function (allImagesDownLoaded) {
        if (!requestCurrent()) return;
                                allNewOfferImagesRetrieved.value = allImagesDownLoaded;
                            });
                        } else {
                            forceCacheComplateWhenNoBrochure = true;
                        }

                        localDL.addToCache(operation, data.Offer.VehicleId, data, requestCacheOwner)
                            .then(function () {
        if (!requestCurrent()) return;
                                newOfferDataCached.value = true;
                                if (forceCacheComplateWhenNoBrochure === true) {
                                    allNewOfferImagesRetrieved.value = true;
                                }
                            });
                        if (data.Offer.DataAvailability.ActiveSpecification !== true || (globals.user.hasPermission(config.PERMISSIONS.SPECIFICATION.Code) === false && !globals.user.allowSpecificationModule())) {
                            newSpecDataCached.value = true;
                            allNewSpecImagesRetrieved.value = true;
                        }
                    }
                    break;
                case OPERATION.GetNewOfferSpecificationDetails:
                    var newSpecUrlsToRetrieve = [];
                    pushVehicleImageUrlsOntoArray(data.Specification.BaseVehicle, newSpecUrlsToRetrieve);
                    for (i = 0; i < data.Specification.CompetitorVehicles.length; i++) {
                        pushVehicleImageUrlsOntoArray(data.Specification.CompetitorVehicles[i], newSpecUrlsToRetrieve);
                    }

                    loadAllImages(newSpecUrlsToRetrieve, localDL.PACK.Vehicle, function (allImagesDownLoaded) {
        if (!requestCurrent()) return;
                        allNewSpecImagesRetrieved.value = allImagesDownLoaded;
                    });
                    localDL.addToCache(operation, data.Advantages.VehicleId, data, requestCacheOwner)
                        .then(function () {
        if (!requestCurrent()) return;
                            newSpecDataCached.value = true;
                        });
                    break;
                case OPERATION.GetExistingOfferDetails:
                case OPERATION.GetSharedOfferDetails.Code:
                    if (data.Result.ReturnCode > 0) {
                        selectedOfferId = data.Changes.Id;
                        if (data.Offer && data.Offer.Brochure) {
                            var existingOfferUrlsToRetrieve = [];

                            // for (i = 0; i < data.Offer.Brochure.VehicleBrochureItemGroups.length; i++) {
                            //     tempItems = data.Offer.Brochure.VehicleBrochureItemGroups[i].VehicleBrochureItems;
                            //     for (j = 0; j < tempItems.length; j++) {
                            //         tempItem = tempItems[j];
                            //         if (tempItem["HTML"] !== undefined) {
                            //             var tempUrl = getImageSrc(tempItem["HTML"]);
                            //             existingOfferUrlsToRetrieve.push(tempUrl);
                            //         }
                            //     }
                            // }
                            // for (i = 0; i < data.Offer.Brochure.VehicleBrochureItemGroups.length; i++) {
                            //     var tmpBrochureIconUrl = data.Offer.Brochure.VehicleBrochureItemGroups[i].IconPictureURL;
                            //     existingOfferUrlsToRetrieve.push(tmpBrochureIconUrl);
                            // }
                            existingOfferUrlsToRetrieve.push(data.Offer.VehiclePlusBodyImageURL);
                            loadAllImages(existingOfferUrlsToRetrieve, localDL.PACK.Vehicle, function (allImagesDownLoaded) {
        if (!requestCurrent()) return;
                                allExistingOfferImagesRetrieved.value = allImagesDownLoaded;
                            });
                        } else {
                            forceCacheComplateWhenNoBrochure = true;
                        }

                        localDL.addToCache(OPERATION.GetExistingOfferDetails, data.Changes.Id, data, requestCacheOwner)
                            .then(function () {
        if (!requestCurrent()) return;
                                existingOfferDataCached.value = true;
                                if (forceCacheComplateWhenNoBrochure === true) {
                                    allExistingOfferImagesRetrieved.value = true;
                                }
                            });
                        if (data.Offer.DataAvailability.ActiveSpecification !== true || (globals.user.hasPermission(config.PERMISSIONS.SPECIFICATION.Code) === false && !globals.user.allowSpecificationModule())) {
                            existingSpecDataCached.value = true;
                            allExistingSpecImagesRetrieved.value = true;
                        }
                    }
                    break;
                case OPERATION.GetExistingOfferSpecificationDetails:
                    var existingSpecUrlsToRetrieve = [];
                    pushVehicleImageUrlsOntoArray(data.Specification.BaseVehicle, existingSpecUrlsToRetrieve);
                    for (i = 0; i < data.Specification.CompetitorVehicles.length; i++) {
                        pushVehicleImageUrlsOntoArray(data.Specification.CompetitorVehicles[i], existingSpecUrlsToRetrieve);
                    }

                    loadAllImages(existingSpecUrlsToRetrieve, localDL.PACK.Vehicle, function (allImagesDownLoaded) {
        if (!requestCurrent()) return;
                        allExistingSpecImagesRetrieved.value = allImagesDownLoaded;
                    });
                    break;
                case OPERATION.GetSelectionList:
                    if (selectionResponseError(data, ['Offers'])) {
                        doingBackgroundSelectionCaching.value = false;
                        dfd.reject(selectionResponseError(data, ['Offers']));
                        return dfd.promise();
                    }
                    //var vehiclePromises = [];
                    if (data.VehiclePack !== undefined && data.VehiclePack !== null) {
                        localDL.addToCache(OPERATION.GetVehiclePackData, PROPERTY.VehiclePackData, data.VehiclePack, requestCacheOwner);
                    }
                
                    requestedImages = [];
                    var vehicleImagesToRetrieve = [];
                    for (var vehicleCounter = 0; vehicleCounter < data.Offers.length; vehicleCounter++) {
                        var vehicle = data.Offers[vehicleCounter];
                        vehicle.OfferCached = shallowRef(0);
                        //#note, this code is commented out as we are no longer retrieving selection list images and so it is unnecessary
                        //pushSelectionVehicleImageUrlsOntoArray(vehicle, vehicleImagesToRetrieve);
                    }
                    globals.user.setNumberOfOffersFromServer(data.Offers.length);
                    dfd.resolve(data);
                    doingBackgroundSelectionCaching.value = true;
                    $.when($, queueLibraryBatch(operation, data.Offers, requestCurrent))
                        .done(function () {
        if (!requestCurrent()) return;
                            doingBackgroundSelectionCaching.value = false;
                        });
                    
                    //curOpTracker.registerImageRetrievalStarted(OPERATION.GetSelectionList);????
                    //#note, this code is commented out as we are no longer retrieving selection list images and so it is unnecessary
                    //loadAllImages(vehicleImagesToRetrieve, localDL.PACK.Vehicle, function (allImagesDownLoaded) {
                    //    //curOpTracker.registerAllImagesRetrieved(OPERATION.GetSelectionList);
                    //    //alert("All selection vehicle images downloaded");
                    //    //var breakPoint = 0;
                    //});
                    return dfd.promise();
                case OPERATION.GetCommonPackDetails:
                    localDL.addToCache(OPERATION.GetCommonPackDetails, PROPERTY.CommonPackDetails, data, requestCacheOwner);
                    break;
                case OPERATION.GetOnRoadCosts:
                
                    //curOpTracker.registerNumRecordsToCache(data.onRoadCosts.length, OPERATION.GetOnRoadCosts);
                    localDL.addToCache(OPERATION.GetOnRoadCosts, PROPERTY.OnRoadCosts, data, requestCacheOwner);
                    break;
                case OPERATION.GetAppConfig.Code:
                    localDL.addToCache(OPERATION.GetAppConfig.Code, PROPERTY.AppConfig, data, requestCacheOwner);
                    break;
                case OPERATION.GetLegislationDetails:
                    localDL.addToCache(OPERATION.GetLegislationDetails, PROPERTY.LegislationDetails, data, requestCacheOwner);
                    break;
                case OPERATION.GetUserSettings:
                    if (!isCurrentContext) localDL.addToCache(OPERATION.GetUserSettings, PROPERTY.UserSettings, data, requestCacheOwner);
                    break;
                case OPERATION.GetBodyStubs:
                    if (data.Bodies !== null && data.Bodies.length > 0) {
                        data.Bodies.forEach(function (bodyStub) {
        if (!requestCurrent()) return;
                            bodyStub.cached = 0;
                        });
                        globals.user.setNumberOfBodiesFromServer(data.Bodies.length);
                        dfd.resolve(data);
                        $.when($, queueLibraryBatch(operation, data.Bodies, requestCurrent))
                        .done(function () {
        if (!requestCurrent()) return;
                            doingBackgroundBodyStubsCaching.value = false;
                        });
                    } else {
                        dfd.resolve(data);
                        doingBackgroundBodyStubsCaching.value = false;
                    }

                    return dfd.promise();
                case OPERATION.GetBody:
                    if (!equipmentDetailAvailable(data, 'Body')) { settleEquipmentDetailFailure(dfd, data); return dfd.promise(); }
                    if (data.Result.ReturnCode === 1 && preventCaching === false) {
                        localDL.addToCache(OPERATION.GetBody, data.Body.Id + '_' + data.Body.AccessoryType + '_' + data.Body.Source, data.Body, requestCacheOwner);
                    }
                    break;
                case OPERATION.GetAccessoryStubs:
                    if (data.Accessories !== undefined && data.Accessories !== null && data.Accessories.length !== undefined && data.Accessories.length > 0) {
                        data.Accessories.forEach(function (accessoryStub) {
        if (!requestCurrent()) return;
                            accessoryStub.cached = 0;
                        });
                        globals.user.setNumberOfAccessoriesFromServer(data.Accessories.length);
                        dfd.resolve(data);
                        $.when($, queueLibraryBatch(operation, data.Accessories, requestCurrent))
                            .done(function () {
        if (!requestCurrent()) return;
                                doingBackgroundAccessoriesStubsCaching.value = false;
                            });
                    } else {
                        dfd.resolve(data);
                        doingBackgroundAccessoriesStubsCaching.value = false;
                    }
                    return dfd.promise();
                case OPERATION.GetAccessory:
                    // V1 wraps the polymorphic accessory detail in a JSON string.
                    // Decode only a positive result, before validating or caching it.
                    if (data?.Result?.ReturnCode > 0 && typeof data.Accessory === 'string') {
                        try { data.Accessory = JSON.parse(data.Accessory); }
                        catch { settleEquipmentDetailFailure(dfd, { Result: { ReturnCode: 1 } }); return dfd.promise(); }
                    }
                    if (!equipmentDetailAvailable(data, 'Accessory')) { settleEquipmentDetailFailure(dfd, data); return dfd.promise(); }
                    if (data.Result.ReturnCode === 1) {
                        if (preventCaching === false) {
                            localDL.addToCache(OPERATION.GetAccessory, data.Accessory.Id + '_' + data.Accessory.AccessoryType + '_' + data.Accessory.Source, data.Accessory, requestCacheOwner);
                        }
                    }
                    break;
                case OPERATION.GetTrailerStubs:
                    if (data.Trailers !== null && data.Trailers.length > 0) {
                        data.Trailers.forEach(function (trailerStub) {
        if (!requestCurrent()) return;
                            trailerStub.cached = 0;
                        });
                        globals.user.setNumberOfTrailersFromServer(data.Trailers.length);
                        dfd.resolve(data);
                        $.when($, queueLibraryBatch(operation, data.Trailers, requestCurrent))
                        .done(function () {
        if (!requestCurrent()) return;
                            doingBackgroundTrailerStubsCaching.value = false;
                        });
                    } else {
                        dfd.resolve(data);
                        doingBackgroundTrailerStubsCaching.value = false;
                    }

                    return dfd.promise();
                case OPERATION.GetTrailer:
                    if (!equipmentDetailAvailable(data, 'Trailer')) { settleEquipmentDetailFailure(dfd, data); return dfd.promise(); }
                    if (data.Result.ReturnCode === 1 && preventCaching === false) {
                        localDL.addToCache(OPERATION.GetTrailer, data.Trailer.Id + '_' + config.ACCESSORY_TYPES.TRAILER + '_' + data.Trailer.Source, data.Trailer, requestCacheOwner);
                    }
                    break;
                case OPERATION.GetPayloadStubs:
                    if (data.Payloads !== null && data.Payloads.length > 0) {
                        data.Payloads.forEach(function (payloadStub) {
        if (!requestCurrent()) return;
                            payloadStub.cached = 0;
                        });
                        globals.user.setNumberOfPayloadsFromServer(data.Payloads.length);
                        dfd.resolve(data);
                        $.when($, queueLibraryBatch(operation, data.Payloads, requestCurrent))
                        .done(function () {
        if (!requestCurrent()) return;
                            doingBackgroundPayloadStubsCaching.value = false;
                        });
                    } else {
                        dfd.resolve(data);
                        doingBackgroundPayloadStubsCaching.value = false;
                    }

                    return dfd.promise();
                case OPERATION.GetPayload:
                    if (!equipmentDetailAvailable(data, 'Payload')) { settleEquipmentDetailFailure(dfd, data); return dfd.promise(); }
                    if (data.Result.ReturnCode === 1 && preventCaching === false) {
                        data.Payload.AccessoryType = config.ACCESSORY_TYPES.PAYLOAD;
                        localDL.addToCache(OPERATION.GetPayload, data.Payload.Id + '_' + config.ACCESSORY_TYPES.PAYLOAD + '_' + data.Payload.Source, data.Payload, requestCacheOwner);
                    }
                    break;
                case OPERATION.GetSharedOfferStubs.Code:
                    if (selectionResponseError(data, ['Offers'])) {
                        doingBackgroundSharedOfferStubsCaching.value = false;
                        dfd.reject(selectionResponseError(data, ['Offers']));
                        return dfd.promise();
                    }
                    if (data.Offers.some(row => !savedSelectionOfferAvailable(row))) {
                        doingBackgroundSharedOfferStubsCaching.value = false;
                        dfd.reject({ errorMessage: 'Invalid selection response', malformedResponse: true });
                        return dfd.promise();
                    }
                    if (data.Offers !== null && data.Offers.length > 0) {
                        //localDL.clearStore(STORE.SharedOfferStubs)
                        //    .then(function () {
                        //        processingAndCacheSharedOfferStubs();
                        //    })
                        //    .fail(function () {
                        //        processingAndCacheSharedOfferStubs();
                        //    });
                        //function processingAndCacheSharedOfferStubs() {
                            data.Offers.forEach(function (sharedOfferStub) {
        if (!requestCurrent()) return;
                                sharedOfferStub.cached = 0;
                            });
                            dfd.resolve(data);
                            //$.when($, queueLibraryBatch(operation, data.Offers))
                            //.done(function () {
                                doingBackgroundSharedOfferStubsCaching.value = false;
                            //});
                        //}
                    } else {
                        dfd.resolve(data);
                        doingBackgroundSharedOfferStubsCaching.value = false;
                    }

                    return dfd.promise();
                case OPERATION.GetBodyMasses.Code:
                    localDL.addToCache(OPERATION.GetBodyMasses.Code, PROPERTY.BodyMasses, data, requestCacheOwner);
                    break;
                case OPERATION.GetLicenceCategories.Code:
                    localDL.addToCache(OPERATION.GetLicenceCategories.Code, PROPERTY.LicenceCategories, data, requestCacheOwner);
                    break;
                case OPERATION.GetDataRequest:
                case OPERATION.PostDataRequest:
                    break;
            }
            if (dontResolve === false) {
                dfd.resolve(data);
            } 
        } catch (handleremoteSuccessEx) {
            TScMessenger.writeErrorMessage("Failure during " + operation + " in dataManager.handleRemoteSuccess: JS Exception: " + handleremoteSuccessEx.message + " : " + handleremoteSuccessEx.name + " , Cloud Services Transaction Message: " + getCSTransactionResultMessage(data));
            dfd.reject({ errorMessage: "Failure during " + operation + " in dataManager.handleRemoteSuccess: JS Exception: " + 
                        handleremoteSuccessEx.message + " : " + handleremoteSuccessEx.name + " , Cloud Services Transaction Message: " + getCSTransactionResultMessage(data)});
        }
        function getCSTransactionResultMessage(csDataResponse) {
        if (!requestCurrent()) return;
            try {
                var message = '';
                if (csDataResponse.Message) {
                    message = csDataResponse.Message;
                } else if (csDataResponse.Result && csDataResponse.Result.Message) {
                    message = csDataResponse.Result.Message;
                }

                if (message === '') {
                    return 'Empty string returned from server'
                } else {
                    return message;
                }
            }catch(ex) {
                return 'Could not extract Cloud Services Transaction Result Message';
            }
        }
    }

    function convertAndStoreVehicleImages(vehicle, promiseArray) {
        if (!requestCurrent()) return;
        var pack = localDL.PACK.Vehicle;
        doUrlLoadPreCheckAndLoadIfNecessary(vehicle.Image, pack, promiseArray);
        doUrlLoadPreCheckAndLoadIfNecessary(vehicle.MakeLogoImage, pack, promiseArray);
    }

    /**
        * Helper method that pushes a given vehicles image urls onto a given array.
        * @method pushVehicleImageUrlsOntoArray
        * @param {Object} vehicle The vehicle containing the image urls that need to be pushed onto the array.
        * @param {Array} arr The array to push the image urls onto.
        */
    function pushVehicleImageUrlsOntoArray(vehicle, arr) {
        if (!requestCurrent()) return;
        arr.push(vehicle.ManufacturerLogo);
        arr.push(vehicle.ReportManufacturerLogo);
        arr.push(vehicle.ReportVehicleChassisImage);
        arr.push(vehicle.VehicleChassisImage);
    }

    /**
    * Helper method that pushes a given vehicles image urls onto a given array.
    * @method pushVehicleImageUrlsOntoArray
    * @param {Object} vehicle The vehicle containing the image urls that need to be pushed onto the array.
    * @param {Array} arr The array to push the image urls onto.
    */
    function pushSelectionVehicleImageUrlsOntoArray(vehicle, arr) {
        if (!requestCurrent()) return;
        arr.push(vehicle.Image);
        arr.push(vehicle.MakeLogoImage);
    }

    function handleRemoteFailure(error) {
        finishNetwork();
        if (!requestCurrent(error)) return;
        if (error.errorMessage === "Offline") {
            dfd.reject(error);
        } else {
            dfd.reject(error)
        }
    }

    return security.guardForSession(dfd.promise(), requestOwner, () => responseProof);
}

/**
    * Checks to see if a given image has already been downloaded and only kicks off a job to retrieve and store if it has not. If an array 
    * has been provided the job will be pushed onto that instead of being kicked off directly.
    * @method doUrlLoadPreCheckAndLoadIfNecessary
    * @param {String} url The image url to be checked and possibly downloaded/stored.
    * @param {String} pack The pack the image url is associated with.
    * @param {Array} optionalPromiseArray The array to push the image urls onto.
    */
function doUrlLoadPreCheckAndLoadIfNecessary(url, pack, optionalPromiseArray) {
    var thePack = pack || 'not_set';
    if ($.inArray(url, alreadyLoadedImages) === -1 && $.inArray(url, requestedImages) === -1 && url !== '' && url !== undefined) {
        requestedImages.push(url);
        //alreadyLoadedImages.push(url);
        if (optionalPromiseArray) {
            optionalPromiseArray.push(convertAndAddImageToDB(url, thePack));
        } else {
            convertAndAddImageToDB(url, thePack);
        }
        
    }
}

/**
    * Handles downloading and storing of a batch job of images giving the caller the opportunity to provide a callback function to be notified 
    * when the operation is complete and if it was successful. 
    * @method loadAllImages
    * @param {Array} imgUrlArr An array of one or more images to be downloaded/stored.
    * @param {String} pack The pack the image url(s) is/are associated with.
    * @param {Function} optionalCallback An optional callback function provided if the caller wants to be informed when the load operation 
    * has completed and whether it has been successful or not allowing the caller to take action at that point.
    */
function loadAllImages(imgUrlArr, pack, optionalCallback) {

    
    var numSucceeded = 0;
    var numFailed = 0;
    requestedImages = [];
    doImageLoad(imgUrlArr, numSucceeded, numFailed, optionalCallback);

    function doImageLoad(arr, numGood, numBad, callback) {

        if (arr.length > 0) {
            var millisecondsToWait = 100;
            setTimeout(function () {
                var curImgUrl = arr.shift();
                if ($.inArray(curImgUrl, alreadyLoadedImages) === -1 && $.inArray(curImgUrl, requestedImages) === -1 && curImgUrl !== "" && curImgUrl !== null) {
                    requestedImages.push(curImgUrl);
                    convertAndAddImageToDB(curImgUrl, pack)
                    .then(function () {
                        numGood += 1;
                        doImageLoad(arr, numGood, numBad, callback);
                    })
                    .fail(function () {
                        numBad += 1;
                        doImageLoad(arr, numGood, numBad, callback);
                    });
                } else {
                    doImageLoad(arr, numGood, numBad, callback);
                }
            }, millisecondsToWait);
            
        } else {
            if (callback) {
                if (numBad > 0) {
                    callback(false);
                } else {
                    callback(true);
                }
                
            }
        }
    }
}

/**
    * Method decides whether to simply pass back current value of globals.isOnline or force an immediate re-check based on the priority passed in by the caller.
    * @method checkOnlineState
    * @param {Number} priority A flag to tell the check how important it is that the information returned (i.e. whether the app is online or not) is up to date.
    */
function checkOnlineState(priority) {
    var dfd = $.Deferred();

    var validMillis, currentTime = Date.now();

    switch (priority) {
        case PRIORITY.LOW:
            doResolve(globals.isOnline.value);
            break;
        case PRIORITY.MEDIUM:
            validMillis = PRIORITY.MEDIUM;
            doMillisCheck(validMillis, currentTime);
            break;
        case PRIORITY.HIGH:
            validMillis = PRIORITY.HIGH;
            doMillisCheck(validMillis, currentTime);
            break;
        default:
            break;
    }

    function doMillisCheck(millis, curTime) {
        if (curTime > (isOnlineLastCheckTimeStamp + millis)) {
            if (networkRequestsUnderway === 0) {
                monitorOnlineState(true)
                .then(doResolve)
                .fail(doResolve);
            } else {
                doResolve(globals.isOnline.value);
            }
        } else {
            doResolve(globals.isOnline.value);
        }
    }

    function doResolve(onlineState) {
        if(onlineState === true) {
            dfd.resolve(onlineState);
        }else {
            dfd.reject(onlineState);
        }
    }

    return dfd.promise();
}

/**
    * Do online check to see if cloud services is available
    * @method checkIsOnline
    */
function checkIsOnline() {
    var dfd = $.Deferred();

    remoteDL.checkIsOnline(OPERATION.CheckIsOnline, security, true)
        .then(function (result) {
            dfd.resolve(true);
        })
        .fail(function () {
            dfd.resolve(false);
        });

    return dfd.promise();
}

/**
    * Method periodically checks if the app is online and updates the value of globals.isOnline. 
    * @method monitorOnlineState
    * @param {Boolean} force A flag to tell the method to check immediately.
    */
function monitorOnlineState(force) {
    //message, data, source, showToast, toastOptions
    //log("In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager, In MonitorOnlineState, dataManager", null, system.getModuleId(dataManager), config.LOG_MESSAGE_TYPE.INFO);

    //tempCounter++;
    //if (tempCounter === 10) {
    //    try {
    //        throw 1;
    //    } catch (exception) {
            
    //        log("In MonitorOnlineState, dataManager, testing error logging: " + tempCounter + ":...: " + exception.message || "no error message", exception.stack || "NOTHING", system.getModuleId(dataManager), false, null, config.LOG_MESSAGE_TYPE.ERROR);
    //        tempCounter = 0;
    //    }
        
    //}

    if (onlineCheckDfd === undefined) {
        onlineCheckDfd = $.Deferred();
    } else if (onlineCheckUnderway === false) {
        onlineCheckDfd = $.Deferred();
    }
    

    if (force) {
        if (onlineCheckUnderway === false) {
            doIsOnlineCheck();
        }
        
    } else {
        if (networkRequestsUnderway === 0) {
            if (onlineCheckUnderway === false) {
                if ((isOnlineLastCheckTimeStamp + 500) <= Date.now()) {
                    doIsOnlineCheck();
                } else {
                    onlineCheckDfd.resolve(globals.isOnline.value);
                }
            }
        } else {
            onlineCheckDfd.resolve(globals.isOnline.value);
        }
            
    }

    function doIsOnlineCheck() {
        onlineCheckUnderway = true;
        
        inner();
        
        function inner() {
            
            {
                remoteDL.checkIsOnlineHeadVersion()
                    .then(function () {
                        handleOnlineCheckResult(true);
                    })
                    .fail(function () {
                        handleOnlineCheckResult(false);
                    });
            }
        }
        
    }
    
    function handleOnlineCheckResult(res) {
        onlineCheckUnderway = false;
        isOnlineLastCheckTimeStamp = Date.now();
        globals.isOnline.value = res;

        onlineCheckDfd.resolve(res);
    }

    return onlineCheckDfd.promise();
}

function checkIsOnlineHeadVersion() {
    return remoteDL.checkIsOnlineHeadVersion();
}

function postDebugLogs() {

    var op;
    var payload;

    if(globals.user.getAuthenticationMethod() !== config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API) {
        checkForAppUpdate();
    }
    //syncSharedOffers();
    //syncTeamItems();

    if ((immediateDebugLogPostRequired === true || timeToPostDebugLogs() === true) && config.loggingLevel !== config.LOGGING_LEVEL.NONE && globals.isOnline.value === true) {
            
            
        localDL.getLogDumpForPost()
            .then(function (debugInfo) {
                op = OPERATION.PostDebugInfo;
                payload = debugInfo;
                if (securityTokenIsValid() === true) {
                    remoteDL.checkIsOnline(op, security, true, payload)
                        .then(function (result) {
                            handlePostDebugResult(op, result);
                        })
                        .fail(function () {
                            updateLastDebugLogUploadTimeStamp();
                        });
                } 
            })
            .fail(function () {
                updateLastDebugLogUploadTimeStamp();
            });
    }

    function handlePostDebugResult(op, result) {
        updateLastDebugLogUploadTimeStamp();
        if (result.ReturnCode === 1) {
            if (immediateDebugLogPostRequired === true) {
                immediateDebugLogPostRequired = false;
            }
            
            localDL.clearStore(STORE.DebugLog);
        }
    }

    function updateLastDebugLogUploadTimeStamp() {
        lastDebugLogUploadTimeStamp = Date.now();
        localDL.addToCache(OPERATION.LastDebugLogUpload, PROPERTY.LastDebugLogUpload, lastDebugLogUploadTimeStamp);
    }
}

function postUsage() {

    var op;
    var payload;

    if (timeToPostUsageLogs() === true && globals.isOnline.value === true) {
        
        localDL.getUsageInfoForPost()
            .then(function (usageInfo) {
                op = OPERATION.PostUsageInfo;
                payload = usageInfo;
                if (securityTokenIsValid() === true) {
                    TScMessenger.writeDebugMessage('DataManager.js, postUsage, posting to internals/usage');
                    remoteDL.checkIsOnline(op, security, true, payload)
                        .then(function (result) {
                            TScMessenger.writeDebugMessage('DataManager.js, postUsage, successful call to internals/usage...');
                            handlePostUsageResult(op, result)
                        })
                        .fail(function () {
                            updateLastUsageLogUploadTimeStamp();
                        });
                } 
            })
            .fail(function (error) {
                updateLastUsageLogUploadTimeStamp();
            });
    }

    function handlePostUsageResult(op, result) {
        updateLastUsageLogUploadTimeStamp();
        if (result.ReturnCode === 1) {
            
            localDL.clearStore(STORE.UsageLog);
        }
    }

    function updateLastUsageLogUploadTimeStamp() {
        lastUsageLogUploadTimeStamp = Date.now();
        localDL.addToCache(OPERATION.LastUsageLogUpload, PROPERTY.LastUsageLogUpload, lastUsageLogUploadTimeStamp);
    }
}

function timeToPostDebugLogs() {
    if (Date.now() > (lastDebugLogUploadTimeStamp + (config.debugLogCommunicationPeriod * 1000))) {
        return true;
    } else {
        return false;
    }
}

function addStaleLastUsageLogUploadTimeStamp() {
    let staleUsageLogUploadTimeStamp = Date.now() - (config.usageCommunicationPeriod * 1000);
    lastUsageLogUploadTimeStamp = staleUsageLogUploadTimeStamp;
    localDL.addToCache(OPERATION.LastUsageLogUpload, PROPERTY.LastUsageLogUpload, staleUsageLogUploadTimeStamp);
}

function doDebugLogMaintenanceIfRequired() {
    var dfd = $.Deferred();

    if ((Date.now() - (config.debugLogRetentionPeriod * 1000)) > lastDebugLogUploadTimeStamp) {
        var deleteOlderThanTimeStamp = Date.now() - (config.debugLogRetentionPeriod * 1000);
        localDL.clearOldDebugLogs(deleteOlderThanTimeStamp)
            .then(function () {
                dfd.resolve();
            });
    } else {
        dfd.resolve();
    }

    return dfd.promise();
}

function timeToPostUsageLogs() {
    //config.usageCommunicationPeriod, don't forget to multiply by 1000 to get millis
    if (Date.now() > (lastUsageLogUploadTimeStamp + (config.usageCommunicationPeriod * 1000))) {
        return true;
    } 
    return false;
}

function log(message, error, source, messageType, optionalIntercomMetaDataObject) {
    
    var messageAppend, stackTrace;

    var extractedErrorInfo = extractErrorAndStackInfo(error);
    messageAppend = extractedErrorInfo.messageAppend;
    stackTrace = extractedErrorInfo.stackTrace;
    
    var timeStamp = Date.now();
    var debugMessage = getDebugMessage();
    if (config.loggingLevel === config.LOGGING_LEVEL.ERRORS_ONLY) {
        if (messageType === config.LOG_MESSAGE_TYPE.ERROR) {
            //localDL.addToCache(OPERATION.LogDebugMessage, timeStamp, getDebugMessage());
            addMessageToCache();
        }
    } else if (config.loggingLevel === config.LOGGING_LEVEL.DETAILED) {
        //localDL.addToCache(OPERATION.LogDebugMessage, timeStamp, getDebugMessage());
        addMessageToCache();
    }
    if (messageType === config.LOG_MESSAGE_TYPE.ERROR && config.loggingLevel !== config.LOGGING_LEVEL.NONE) {
        immediateDebugLogPostRequired = true;
    }
    if (messageType === config.LOG_MESSAGE_TYPE.ERROR) {

        var intercomMetaDataObject = getMetadataObject(debugMessage);

        if (optionalIntercomMetaDataObject) {
            intercomMetaDataObject = extend(intercomMetaDataObject, optionalIntercomMetaDataObject);
        }

        sendInfoToIntercom(config.OPERATION.SendIntercomEventData, globals.getIntercomObject(config.INTERCOM_EVENT.ERROR_RAISED, intercomMetaDataObject));
        

        
    }
    function getMetadataObject(message) {
        var obj = {};
        obj[config.INTERCOM_METADATA_ITEM.ORIGIN] = message.Origin,
        obj[config.INTERCOM_METADATA_ITEM.DESCRIPTION] = message.Description
        return obj;
    }
    function extend(obj, src) {
        for (var key in src) {
            if (src[key]) obj[key] = src[key];
        }
        return obj;
    }
    function addMessageToCache() {
        
        localDL.addToCache(OPERATION.LogDebugMessage, timeStamp, debugMessage);
        

        


    }

    function getDebugMessage() {
        var debugMessage = {};
        debugMessage.StackTrace = stackTrace;
        debugMessage.Description = message + messageAppend;
        debugMessage.TimeStamp = timeStamp;
        debugMessage.Origin = source;
        debugMessage.MessageType = messageType;

        return debugMessage;
    }
    //logger.log(message, data, source, showToast, toastOptions, messageType);
}

function logAppUsage(usageType) {

    var logUsage = false;

    switch (usageType) {
        case config.USAGE_TYPE.ONLINE: 
        case config.USAGE_TYPE.OFFLINE:
            logUsage = true;
            break;
        case config.USAGE_TYPE.PERFORMANCE_OPENED:
        case config.USAGE_TYPE.COSTING_OPENED:
        case config.USAGE_TYPE.SPECIFICATION_OPENED:
        case config.USAGE_TYPE.SUMMARY_OPENED:
        case config.USAGE_TYPE.COMPANY_OVERVIEW_OPENED:
        case config.USAGE_TYPE.OFFERS_OPENED:
        case config.USAGE_TYPE.BROCHURE_OPENED:
        case config.USAGE_TYPE.TRAINING_OPENED:
        case config.USAGE_TYPE.CONFIGURATION_OPENED:
            resetUseLoggedTrackingBooleans();
            logUsage = true;
            break;
        case config.USAGE_TYPE.PERFORMANCE_USED:
            if (performanceUseLogged === false) {
                logUsage = true;
                performanceUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.COSTING_USED:
            if (costingUseLogged === false) {
                logUsage = true;
                costingUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.SPECIFICATION_USED:
            if (specificationUseLogged === false) {
                logUsage = true;
                specificationUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.SUMMARY_USED:
            if (summaryUseLogged === false) {
                logUsage = true;
                summaryUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.COMPANY_OVERVIEW_USED:
            if (companyOverviewUseLogged === false) {
                logUsage = true;
                companyOverviewUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.OFFERS_USED:
            if (offersUseLogged === false) {
                logUsage = true;
                offersUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.BROCHURE_USED:
            if (brochureUseLogged === false) {
                logUsage = true;
                brochureUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.TRAINING_USED:
            if (trainingUseLogged === false) {
                logUsage = true;
                trainingUseLogged = true;
            }
            break;
        case config.USAGE_TYPE.CONFIGURATION_USED:
            if (configurationUseLogged === false) {
                logUsage = true;
                configurationUseLogged = true;
            }
            break;
    }
    if (logUsage === true) {
        localDL.addToCache(OPERATION.LogAppUsage, Date.now(), { UsageType: usageType });
    }
    

    ////test data, should not be left here!!!
    //2628000000
    //localDL.addToCache(OPERATION.LogAppUsage, Date.now() - 2628000000, { UsageType: usageType });
    //localDL.addToCache(OPERATION.LogAppUsage, Date.now() - 2628000000+1, { UsageType: usageType });
    
    //localDL.addToCache(OPERATION.LogAppUsage, Date.now()+1, { UsageType: usageType });
    //localDL.addToCache(OPERATION.LogAppUsage, Date.now()+2, { UsageType: usageType });

    //localDL.addToCache(OPERATION.LogAppUsage, Date.now() + 2628000000, { UsageType: usageType });
    //localDL.addToCache(OPERATION.LogAppUsage, Date.now() + 2628000000 + 1, { UsageType: usageType });

}

function resetUseLoggedTrackingBooleans() {
    performanceUseLogged = false,
    costingUseLogged = false,
    specificationUseLogged = false,
    summaryUseLogged = false,
    companyOverviewUseLogged = false,
    offersUseLogged = false,
    brochureUseLogged = false,
    trainingUseLogged = false;
    configurationUseLogged = false;
}

function checkForAppUpdate() {

    localDL.getFromCache(OPERATION.LastUpdateCheck, PROPERTY.LastUpdateCheck)
        .then(function (lastUpdateCheckTimestamp) {
            var now = Date.now();
            if (lastUpdateCheckTimestamp === undefined || lastUpdateCheckTimestamp === null) {
                lastUpdateCheckTimestamp = 0;
            }
            if (Number(lastUpdateCheckTimestamp) + 300000 < now) {
                
                if (globals.isOnline.value) {
                    try {
                        if (navigator.serviceWorker) {
                            //serviceWorkerRegistration.update()
                            TScMessenger.writeDebugMessage('DataManager.js, checkForAppUpdate, checking for updates');
                            // globals.getRegisteredServiceWorkerContainer().update();
                            navigator.serviceWorker.getRegistration(globals.getServiceWorkerScope()).then((registration) => {
                                if (registration) {
                                    // if(registration.active) {
                                    //     registration.active.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                    // }
                                    // if(registration.waiting) {
                                    //     registration.waiting.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                    // }
                                    // if(registration.installing) {
                                    //     registration.installing.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                    // }
                                    // registration.addEventListener("updatefound", () => {
                                    //     if(registration.waiting) {
                                    //         registration.waiting.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                    //     }
                                    //     if(registration.installing) {
                                    //         registration.installing.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                    //     }
                                    //     console.log('update found detected, checking for updates...');
                                    // });
                                    globals.checkingForUpdates = true;
                                    registration.update().then(function (registrationFromUpdate){
                                        if(!registrationFromUpdate.waiting && !registrationFromUpdate.installing && registrationFromUpdate.active) {
                                            // registration.active.postMessage({ type: 'FINISHED_CHECKING_FOR_UPDATES'});
                                            globals.checkingForUpdates = false;
                                        }/*else {
                                            if(registrationFromUpdate.waiting) {
                                                registrationFromUpdate.waiting.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                            }
                                            if(registrationFromUpdate.installing) {
                                                registrationFromUpdate.installing.postMessage({ type: 'CHECKING_FOR_UPDATES'});
                                            }
                                        }*/
                                        TScMessenger.writeDebugMessage('DataManager.js, checkForAppUpdate, waiting: ' + registrationFromUpdate.waiting );
                                        TScMessenger.writeDebugMessage('DataManager.js, checkForAppUpdate, active: ' + registrationFromUpdate.active );
                                        TScMessenger.writeDebugMessage('DataManager.js, checkForAppUpdate, installing: ' + registrationFromUpdate.installing );
                                    });
                                }
                              });
                        } else if(window.applicationCache){
                            window.applicationCache.update();
                        }
                        
                    } catch (invalidStateErrorEx) {
                        TScMessenger.writeDebugMessage('DataManager.js, checkForAppUpdate, invalidStateErrorEx');
                    }
                    
                }
                
                localDL.addToCache(OPERATION.LastUpdateCheck, PROPERTY.LastUpdateCheck, now);
            }    
        });
}

function syncSharedOffers() {

    localDL.getFromCache(OPERATION.GetLastSharedOfferSyncTimestamp, PROPERTY.SharedOfferSyncTimestamp)
        .then(function (lastSharedOfferSyncTimestamp) {
            var now = Date.now();
            if (lastSharedOfferSyncTimestamp === undefined || lastSharedOfferSyncTimestamp === null) {
                localDL.addToCache(OPERATION.GetLastSharedOfferSyncTimestamp, PROPERTY.SharedOfferSyncTimestamp, now);
            } else {
                //if (Number(lastSharedOfferSyncTimestamp) + 120000 < now) {

                    if (globals.isOnline.value) {
                        try {
                            doingBackgroundSharedOfferStubsRetrieval.value = true;
                            doingBackgroundSharedOfferStubsCaching.value = true;

                            handleDataRequest(null, OPERATION.GetSharedOfferStubs.BaseUrl, OPERATION.GetSharedOfferStubs.Code, true)
                                .then(function (result) {
                                    if (result.Result.ReturnCode === 1) {
                                        sharedOfferStubsResult = result;
                                        doingBackgroundSharedOfferStubsRetrieval.value = false;

                                        sharedOfferSyncHandlerCallback(result);
                                    }
                                }).fail(function (error) {

                                });

                        } catch (sharedOfferSyncEx) {
                            TScMessenger.writeDebugMessage('DataManager.js, syncSharedOffers, sharedOfferSyncEx');
                        }

                    }

                    localDL.addToCache(OPERATION.GetLastSharedOfferSyncTimestamp, PROPERTY.SharedOfferSyncTimestamp, now);
                //}
            }
        });


    
}

function syncTeamItems() {

    localDL.getFromCache(OPERATION.GetLastTeamItemsSyncTimestamp, PROPERTY.TeamItemsSyncTimestamp)
        .then(function (lastTeamItemsSyncTimestamp) {
            var now = Date.now();
            if (lastTeamItemsSyncTimestamp === undefined || lastTeamItemsSyncTimestamp === null) {
                localDL.addToCache(OPERATION.GetLastTeamItemsSyncTimestamp, PROPERTY.TeamItemsSyncTimestamp, now);
            } else {
                //if (Number(lastTeamItemsSyncTimestamp) + 120000 < now) {

                    if (globals.isOnline.value) {
                        try {
                            //new Date(Number(lastTeamItemsSyncTimestamp)).toUTCString()
                            handleDataRequest(null, OPERATION.GetTeamSyncItems.BaseUrl + '?lastSyncTimeStamp=' + new Date(Number(lastTeamItemsSyncTimestamp)).toUTCString(), OPERATION.GetTeamSyncItems.Code, true)
                                .then(function (result) {
                                    if (result.Result.ReturnCode === 1) {
                                        if (result.NewOrUpdatedTeamItems.Bodies.length > 0) {

                                            result.NewOrUpdatedTeamItems.Bodies.forEach(function (bodyStub) {
                                                bodyStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetBodyStubs, result.NewOrUpdatedTeamItems.Bodies)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Cranes.length > 0) {

                                            result.NewOrUpdatedTeamItems.Cranes.forEach(function (craneStub) {
                                                craneStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Cranes)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Others.length > 0) {

                                            result.NewOrUpdatedTeamItems.Others.forEach(function (otherStub) {
                                                otherStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Others)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Fridges.length > 0) {

                                            result.NewOrUpdatedTeamItems.Fridges.forEach(function (fridgeStub) {
                                                fridgeStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Fridges)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Taillifts.length > 0) {

                                            result.NewOrUpdatedTeamItems.Taillifts.forEach(function (tailliftStub) {
                                                tailliftStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Taillifts)
                                        }
                                        if (result.NewOrUpdatedTeamItems.FifthWheels.length > 0) {

                                            result.NewOrUpdatedTeamItems.FifthWheels.forEach(function (fifthWheelStub) {
                                                fifthWheelStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.FifthWheels)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Hitches.length > 0) {

                                            result.NewOrUpdatedTeamItems.Hitches.forEach(function (hitchStub) {
                                                hitchStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Hitches)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Hooklifts.length > 0) {

                                            result.NewOrUpdatedTeamItems.Hooklifts.forEach(function (hookliftStub) {
                                                hookliftStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetAccessoryStubs, result.NewOrUpdatedTeamItems.Hooklifts)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Trailers.length > 0) {

                                            result.NewOrUpdatedTeamItems.Trailers.forEach(function (trailerStub) {
                                                trailerStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetTrailerStubs, result.NewOrUpdatedTeamItems.Trailers)
                                        }
                                        if (result.NewOrUpdatedTeamItems.Payloads.length > 0) {

                                            result.NewOrUpdatedTeamItems.Payloads.forEach(function (payloadStub) {
                                                payloadStub.cached = 0;
                                            });

                                            queueLibraryBatch(OPERATION.GetPayloadStubs, result.NewOrUpdatedTeamItems.Payloads)
                                        }

                                        teamSyncHandlerCallback(result);
                                    }
                                }).fail(function (error) {

                                });

                        } catch (teamItemsSyncEx) {
                            TScMessenger.writeDebugMessage('DataManager.js, syncSharedOffers, syncTeamItems, teamItemsSyncEx');
                        }

                    }

                    localDL.addToCache(OPERATION.GetLastTeamItemsSyncTimestamp, PROPERTY.TeamItemsSyncTimestamp, now);
                //}
            }
        });



}

/**
    * Wraps security.login and then carries out some operations on the returned data on the way past.
    * @method login
    * @param {String} username The username of the user to be authorised.
    * @param {String} password The password of the user to be authorised.
    * @param {String} machineCode The machine code of the device the user is being authorised from.
    * @param {Number} cdg The customer distribution group of the user attempting to authorise.
    * @param {Boolean} async A flag to indicate that the login call should be carried out asynchronously.
    * @return {Object} Returns data from successful login or error if login failed. 
    */
function showPendingChallenge(continuation, owner) {
    if (!security.incarnationMatches(owner) || (!continuation.pendingTransfer && !continuation.pendingSignup)) return;
    globals.hideSpinner();
    if (!continuation.independentSession) globals.setAlreadyAuthenticated('NO');
    if (continuation.pendingSignup) globals.showDialog({ type: 'VerifyEmailModal', pendingSignup: continuation.pendingSignup,
        operateSignup: continuation.operate, subscribeSignup: continuation.subscribe });
    else globals.showDialog({ type: 'VerifyTransferLicenceModal', pendingTransfer: continuation.pendingTransfer,
        emailAddress: continuation.emailAddress || '', operateTransfer: continuation.operate, subscribeTransfer: continuation.subscribe });
}
function signupSignInGuidance() {
    globals.hideSpinner();
}
function login(username, password, machineCode, cdg, async, isFromSignUp, authenticationMethod, bypassStandardSetup) {
    const dfd = $.Deferred();
    const request = security.login(username, password, machineCode, cdg, async, isFromSignUp, authenticationMethod);
    const owner = security.getIncarnation();
    globals.dmLoginDfd = dfd;
    request.progress(continuation => showPendingChallenge(continuation, owner));
    request.then(function (loginData) {
        if (!security.incarnationMatches(owner)) return dfd.reject({ cancelled: true });
        if (loginData?.signInRequired) { signupSignInGuidance(); return dfd.reject({ cancelled: true, signupCancelled: true, signupCompleted: true }); }
        if (!bypassStandardSetup) {
            globals.setAlreadyAuthenticated('YES');
            doDataActionsAfterLoginOrReAuth(loginData, false, dfd);
        } else dfd.resolve(loginData);
    }).fail(error => dfd.reject(error));
    return dfd.promise();
}

function logout(justLoggedOut, refreshedData, logoutFrom3rdPartyIdentityProvider) {
    var dfd = $.Deferred();
    TScMessenger.writeDebugMessage('DataManager.js, logout');
    globals.setAlreadyAuthenticated('NO');
    const localLogout = security.logout(logoutFrom3rdPartyIdentityProvider);
    const owner = security.getIncarnation();
    let completed = false;
    let cacheFinished = false, identityFinished = false;
    let cleanupAllowed = true;
    const cleanupGuard = { canProceed: () => cleanupAllowed && owner === security.getIncarnation() };
    const cancelCleanup = () => { cleanupAllowed = false; if (cleanupGuard.abort) cleanupGuard.abort(); };
    const unregisterCancellation = security.registerCancellation(cancelCleanup);
    const finish = () => {
        if (completed || !cacheFinished || !identityFinished) return;
        completed = true;
        unregisterCancellation();
        if (owner !== security.getIncarnation()) return dfd.reject({ cancelled: true });
        performOtherLogoutTasks();
    };
    setTimeout(() => { cancelCleanup(); cacheFinished = true; finish(); }, 4000);
    localLogout.always(() => { identityFinished = true; finish(); });
    try {
        localDL.clearBatchJobQueue().then(() => {
            if (!cleanupGuard.canProceed()) throw { cancelled: true };
            return localDL.clearAllCachedData(cleanupGuard);
        })
            .always(() => { cacheFinished = true; finish(); });
    } catch (error) { cacheFinished = true; finish(); }

    function performOtherLogoutTasks() {
        globals.setStorageKey(null);
        localStorage.setItem('localStorageState', 'PLAINTEXT');
        
        TScMessenger.writeDebugMessage('DataManager.js, logout, performOtherLogoutTasks');
        globals.timersAlreadySetup = false;
        globals.appConfigApplied = false;
        globals.clearTimers();
        globals.onlineOfflineEventListenersAlreadySetup.value = false;
        globals.user.clearVehicleSelectionSearchHistory();
        
        if(typeof globals.user.getAppRefreshUnderway() !== 'string') {
            // TScMessenger.writeDebugMessage('globals.dataModelReady.value = true in typeof globals.user.getAppRefreshUnderway() !== string>>DM.logout...');
            globals.dataModelReady.value = true;
        }
        if (refreshedData !== true) {
            globals.user.removeAppRefreshUnderway();
            // TScMessenger.writeDebugMessage('globals.dataModelReady.value = true in DM.logout>>refreshedData===true...');
            globals.dataModelReady.value = true;
            sendInfoToIntercom(config.OPERATION.SendIntercomEventData, globals.getIntercomObject(config.INTERCOM_EVENT.LOGGED_OUT));
        }
        globals.justLoggedOut = justLoggedOut !== undefined ? justLoggedOut : true;
        if (globals.justLoggedOut === true) {
            localStorage.removeItem('authMethod');
            globals.showModuleView();
        } else {
            globals.fadeOutAll();
        }
        dfd.resolve();
    }

    return dfd.promise();
}

function clearCachedData() {
    const owner = security.getIncarnation();
    const guard = { canProceed: () => security.incarnationMatches(owner) };
    const release = security.registerCancellation(() => guard.abort?.());
    return localDL.clearAllCachedData(guard).always(release);
}

/**
    * Wraps security.securityTokenIsValid and then carries out some operations on the returned data(if there is any) on the way past.
    * @method securityTokenIsValid
    * @return {Boolean} Returns true or false depending on whether the current security token is considered valid or if invalid, whether it could be renewd or not. 
    */
function securityTokenIsValid() { return security.securityTokenIsValid().IsValid; }

function securityTokenIsValidAsTextFlag(dontForceLicenceTransferMessage, callbackInfo) {
    if (ltvQualification) ltvQualification.contextEntries++;
    TScMessenger.writeDebugMessage('DataManager.js, securityTokenIsValidAsTextFlag');
    
    if (dontForceLicenceTransferMessage === undefined || dontForceLicenceTransferMessage === false) {
        globals.forceLicenceTransferMessage = true;
    }
    const ready = $.Deferred();
    let contextOwner = security.getIncarnation();
    if (security.securityTokenIsValid().IsValid && !security.hasPendingChallenge() && !callbackInfo?.requireContext) ready.resolve({ IsValid: true });
    else {
        const contextRequest = security.initialiseContext();
        contextRequest.progress(continuation => {
            showPendingChallenge(continuation, contextOwner);
        });
        contextOwner = security.getIncarnation();
        contextRequest.then(loginData => {
            if (loginData?.signInRequired) { signupSignInGuidance(); return ready.resolve({ IsValid: false, LoginData: { signInRequired: true } }); }
            return ready.resolve({
        IsValid: security.securityTokenIsValid().IsValid && loginData && loginData.contextEstablished && loginData.result &&
            (loginData.result.ReturnCode > 0 || loginData.result.ReturnCode === -4 || loginData.result.ReturnCode === -5),
        LoginData: loginData }); })
        .fail(error => ready.resolve({ IsValid: false, LoginData: { error } }));
    }
    return ready.then(function (result) {
    if (result.LoginData?.signInRequired) return 'SIGNUP_SIGN_IN_REQUIRED';
    if (result.LoginData?.error?.transferCancelled || result.LoginData?.error?.signupCancelled) return $.Deferred().reject(result.LoginData.error).promise();
    if (!security.incarnationMatches(contextOwner) || result.LoginData?.error?.cancelled) return "SESSION_CHANGED";
    globals.setAlreadyAuthenticated(result.IsValid ? 'YES' : 'NO');
    if (result.IsValid && result.LoginData) {
        let optionalDfd = undefined;
        TScMessenger.writeDebugMessage('DataManager.js, securityTokenIsValidAsTextFlag, has login data');
        
        var isBackgroundReAuth = true;
        // var isRefreshingDataFlag = globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.APP_UPDATE ||
        //                                 globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.USER_REFRESH ||
        //                             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.ERROR_UPGRADING_USER_ALREADY_SUBSCRIBED ||
        //                             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.ERROR_UPGRADING_USER_AFTER_SUCCESSFUL_SUBSCRIBE ||
        //                             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.FORCED_REFRESH_MISSING_DATA ||
        //                             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.CHANGING_LANGUAGE ||
        //                             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.FORCED_REFRESH_AFTER_BUY;
        var isRefreshingDataFlag = globals.user.getAppRefreshUnderway();

        if (isRefreshingDataFlag) {
            TScMessenger.writeDebugMessage('DataManager.js, securityTokenIsValidAsTextFlag, doing app refresh: ' + globals.user.getLastUrlHash());
            isBackgroundReAuth = false;
            globals.refreshDfd = $.Deferred();
            optionalDfd = globals.refreshDfd;
            if(globals.refreshDfdCallback) {
                globals.refreshDfd.promise().then(globals.refreshDfdCallback).fail(globals.refreshDfdFailCallback);
            }
        }
        if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
            dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.DOWNLOADING_APPLICATION_DATA.StepNum, isBackgroundReAuth);
        }
        doDataActionsAfterLoginOrReAuth(result.LoginData, isBackgroundReAuth, optionalDfd, callbackInfo);
    } else {
        TScMessenger.writeDebugMessage('DataManager.js, securityTokenIsValidAsTextFlag, no login data');
        if (result.IsValid === true) {
            setLocalDLIdentity();
            // globals.dataModelReady.value = true;
        }
        if(callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.SHARED_CALCULATION_DEEPLINK) {
            callbackInfo.callback();
        }
    }
    let returnFlag = '';
    if(result.IsValid) {
        returnFlag = config.AUTHENTICATION_STATE.AUTHENTICATED;
    } else {
        if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode < 0) {
            if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -99) {
                returnFlag = config.AUTHENTICATION_STATE.UNEXPECTED_SERVER_ERROR;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -98) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_MISSING_USERNAME;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -97) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_MISSING_AUTHENTICATION_METHOD;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -96) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_MISSING_PASSWORD;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -95) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_MISSING_SECURITY_TOKEN_API_USER;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -94) {
                returnFlag = config.AUTHENTICATION_STATE.AUTHENTICATION_FAILED_API_USER_INVALID_SECURITY_TOKEN;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -93) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_INVALID_APPLICATION_TYPE;
            } else if(result.LoginData && result.LoginData.result && result.LoginData.result.ReturnCode === -92) {
                returnFlag = config.AUTHENTICATION_STATE.BAD_REQUEST_MISSING_MACHINE_CODE;
            } else {
                returnFlag = config.AUTHENTICATION_STATE.AUTHENTICATION_FAILED;
            }
        }else if(result.LoginData && result.LoginData.error) {
            callbackInfo?.onAuthenticationError?.(result.LoginData.error);
            returnFlag = result.LoginData.error.retryable || result.LoginData.error.permissionDenied ? 'REMOTE_UNAVAILABLE' : config.AUTHENTICATION_STATE.NEED_TO_AUTHENTICATE;
        } else {
            returnFlag = config.AUTHENTICATION_STATE.NEED_TO_AUTHENTICATE;
        }
    }
    return returnFlag;
    });
}

function adjustLoggedInTrialUserAfterSuccessfulSubscribe(planType) {
    var dfd = $.Deferred();
    
    if (tempLoginDataRef !== null) {
        tempLoginDataRef.result.ReturnCode = 1;
        tempLoginDataRef.result.Message = "";
        tempLoginDataRef.trialDays = 0;
        switch (planType) {
            //case config.BUY_NOW_TYPE.MONTHLY_RECURRING.Code:
            //case config.BUY_NOW_TYPE.ANNUAL_RECURRING.Code:
            case config.BILLING_CYCLE_OPTIONS.MONTHLY:
            case config.BILLING_CYCLE_OPTIONS.ANNUALLY:
                tempLoginDataRef.fullDays = 60;
                break;
            default:
                tempLoginDataRef.fullDays = 14;
                break;
        }
        

        var validUntilOffline;
        
        var validUntilOfflineMillis = Date.now() + (tempLoginDataRef.fullDays * config.millisInDay);
        var date = new Date(validUntilOfflineMillis);
        validUntilOffline = moment(date).format("M/D/YYYY h:mm:ss a");
        

        globals.user.updateUser({
            validUntilOffline: validUntilOffline,
            userType: config.USER_TYPES.FULL_USER_OK,
            trialCalculationsRemaining: 0,
            firstLogin: false,
            trialDaysRemaining: tempLoginDataRef.trialDays
        });
        var isBackgroundReAuth = true;
        doDataActionsAfterLoginOrReAuth(tempLoginDataRef, isBackgroundReAuth, dfd);
        
    } else {
        dfd.reject();
    }

    return dfd.promise();
}

function doDataActionsAfterLoginOrReAuth(loginData, isBackgroundReAuth, optionalDfd, callbackInfo, isCurrentContext) {
    const owner = security.getIncarnation();
    let initialization;
    const bootstrapCurrent = () => security.incarnationMatches(owner) && (!isCurrentContext || isCurrentContext()) &&
        (!initialization || libraryInitialization === initialization);
    if (!bootstrapCurrent()) return optionalDfd?.reject({ cancelled: true });
    if (loginData.contextEstablished && loginData.result.ReturnCode > 0 && !loginData.librarySwitch) {
        // Ordinary authentication has already adopted the server-selected CDG.
        // Ignore old journals; retire only an obsolete immediate continuation.
        getLibraryTransition().cancelObsolete();
        if (getLibraryTransition().pending()) return optionalDfd?.reject({ cancelled: true });
        if (libraryRecoveryRequired) {
            initialization = {};
            libraryInitialization = initialization;
            libraryTransitionActive = true;
            libraryRecoveryRequired = true;
            globals.dataModelReady.value = false;
            localDL.clearBatchJobQueue();
            resetLibraryMemory();
        }
    }
    let transferLoadingToken;
    if ((loginData.transferCompleted || loginData.signupCompleted) && loginData.result.ReturnCode > 0) {
        transferLoadingToken = globals.beginTransferLoading(owner, security.registerCancellation);
        optionalDfd?.fail(() => globals.finishTransferLoading(transferLoadingToken));
    }
    const cacheOwner = sessionCacheOwner(owner, bootstrapCurrent);
    if (ltvQualification) ltvQualification.bootstrapEntries++;
    if (loginData.result.ReturnCode === -4 || loginData.result.ReturnCode === 2 || loginData.result.ReturnCode === -5) {
        tempLoginDataRef = loginData;
    }
    if (loginData.result.ReturnCode > 0) {
        setLocalDLIdentity();

        var fullDays = loginData.fullDays;
        var trialDays = loginData.trialDays;
        latestReportGraphicsFromLogin = loginData.reportGraphics;

        if (trialDays && trialDays > 0) {
            persist(OPERATION.LicenceLease, Date.now() + (trialDays * config.millisInDay), PROPERTY.LicenceLeaseExpiryDate, cacheOwner);
        } else if (fullDays) {
            persist(OPERATION.LicenceLease, Date.now() + (fullDays * config.millisInDay), PROPERTY.LicenceLeaseExpiryDate, cacheOwner);
        }

        if (loginData.result.ReturnCode === 3) {
            isBackgroundReAuth = false;
        }

        // if (isBackgroundReAuth === false || globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.APP_UPDATE || globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.USER_REFRESH ||
        //             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.ERROR_UPGRADING_USER_ALREADY_SUBSCRIBED ||
        //             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.ERROR_UPGRADING_USER_AFTER_SUCCESSFUL_SUBSCRIBE ||
        //             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.FORCED_REFRESH_MISSING_DATA ||
        //             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.CHANGING_LANGUAGE ||
        //             globals.user.getLastUrlHash() === config.APP_DATA_REFRESH_SCENARIO.FORCED_REFRESH_AFTER_BUY) {
        if (isBackgroundReAuth === false || globals.user.getAppRefreshUnderway()) {

            if (isBackgroundReAuth === true) {
                globals.refreshDfd = $.Deferred();
                optionalDfd = globals.refreshDfd;
                if(globals.refreshDfdCallback) {
                    globals.refreshDfd.promise().then(globals.refreshDfdCallback).fail(globals.refreshDfdFailCallback);
                }
            }

            TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, isBackgroundReAuth =' + isBackgroundReAuth);
            standardHandleVersionsTasksComplete.value = false;
            slowHandleVersionsTasksComplete.value = false;
        }

        localDL.addToCache(OPERATION.GetLastTeamItemsSyncTimestamp, PROPERTY.TeamItemsSyncTimestamp, Date.now(), cacheOwner);
        localDL.addToCache(OPERATION.GetLastSharedOfferSyncTimestamp, PROPERTY.SharedOfferSyncTimestamp, Date.now(), cacheOwner);

        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, loadingProperties');
        // if (optionalDfd) {
        //     optionalDfd.resolve(loginData);
        // }
        const cachedVersions = ltvCachedVersions?.owner === owner ? ltvCachedVersions.versions : null;
        ltvCachedVersions = null;
        const fixtureOwner = { getIsCancelled: () => !bootstrapCurrent(),
            trackTransaction: tx => security.registerCancellation(() => { try { tx.abort(); } catch { /* already ended */ } }) };
        const cacheReady = cachedVersions && ltvQualification ? localDL.addToCache(OPERATION.LatestVersions, localDL.PROPERTY.Versions, cachedVersions, fixtureOwner) : $.Deferred().resolve();
        cacheReady.then(() => { if (!bootstrapCurrent()) return $.Deferred().reject({ cancelled: true }).promise(); return loadProperties(bootstrapCurrent); }).then(function () {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
            TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, calling handleVersions');
            handleVersions(loginData.versions, bootstrapCurrent)
            .then(function (needToUpdate) {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, after handleVersions, needToUpdate=' + needToUpdate);
                if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
                    dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.DOWNLOADING_SETTINGS.StepNum, isBackgroundReAuth);
                }
                localDL.getFromCache(OPERATION.GetVehiclePackData, PROPERTY.VehiclePackData)
                    .then(function (vehiclePackData) {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                        if (vehiclePackData !== undefined && vehiclePackData !== null) {
                            config.loadingSummaryDisclaimer = vehiclePackData.LoadingSummaryDisclaimer;
                            config.specificationSummaryDisclaimer = vehiclePackData.SpecificationSummaryDisclaimer;
                            config.costingSummaryDisclaimer = vehiclePackData.CostingSummaryDisclaimer;
                            config.routingSummaryDisclaimer = vehiclePackData.RoutingSummaryDisclaimer;
                        }
                    });
                if (needToUpdate === true || loginData.librarySwitch === true || initialization) {
                    // getOnRoadCosts(globals.user.getCdg())
                    //         .then(function (onRoadCosts) {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                    //             globals.user.updateUser(onRoadCosts);
                                globals.user.clearSelectionArrays();
                                TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, getting Common pack details');
                                getCommonPackDetails(globals.user.getCdg(), bootstrapCurrent)
                                    .then(function (commonPackDetails) {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                                        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, updating User with Common pack details');
                                        globals.user.updateUser(commonPackDetails);
                                        //remember user settings are not cached
                                        
                                        getUserSettings(bootstrapCurrent)
                                            .then(function (userSettings) {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                                                globals.userSettingsDfd = null;
                                                TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, updating User settings');
                                                
                                                
                                                if (globals.appLogosDownloaded === false) {
                                                    TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, globals.appLogosDownloaded === false');
                                                    if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
                                                        dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.DOWNLOADING_IMAGES.StepNum, isBackgroundReAuth);
                                                    }
                                                    loadLocallyOrRetrieveAndStoreAppLogos(bootstrapCurrent)
                                                        .then(function () {
            if (!bootstrapCurrent()) { if (optionalDfd) optionalDfd.reject({ cancelled: true }); return; }
                                                            TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, after loadLocallyOrRetrieveAndStoreAppLogos');
                                                            if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
                                                                dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.CONFIGURING_APPLICATION.StepNum, isBackgroundReAuth);
                                                            }
                                                            if (isBackgroundReAuth === true) {
                                                                globals.successfulBackgroundReAuth.value = true;
                                                            }
                                                            if (optionalDfd) {
                                                                TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, loadLocallyOrRetrieveAndStoreAppLogos, resolving refreshDfd');
                                                                finishLibraryInitialization();
                                                                optionalDfd.resolve(loginData);
                                                            }else {
                                                                TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, loadLocallyOrRetrieveAndStoreAppLogos, setting globals.dataModelReady.value = true');
                                                                finishLibraryInitialization();
                                                                globals.dataModelReady.value = true;
                                                            }
                                                            if (loginData.result.ReturnCode === 3 && callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.LICENCE_TRANSFER) {
                                                                callbackInfo.callback();
                                                            } else if(callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.SHARED_CALCULATION_DEEPLINK) {
                                                                callbackInfo.callback();
                                                            }
                                                            // bus.emit('updateSelectionData');
                                                        }).fail(handleFailure);
                                                } else {
                                                    TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, globals.appLogosDownloaded === true');
                                                    if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
                                                        dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.CONFIGURING_APPLICATION.StepNum, isBackgroundReAuth);
                                                    }
                                                    if (isBackgroundReAuth === true) {
                                                        globals.successfulBackgroundReAuth.value = true;
                                                    }
                                                    if (optionalDfd) {
                                                        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, resolving refreshDfd');
                                                        finishLibraryInitialization();
                                                        optionalDfd.resolve(loginData);
                                                    }else {
                                                        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, setting globals.dataModelReady.value = true');
                                                        finishLibraryInitialization();
                                                        globals.dataModelReady.value = true;
                                                    }
                                                    if (loginData.result.ReturnCode === 3 && callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.LICENCE_TRANSFER) {
                                                        callbackInfo.callback();
                                                    } else if(callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.SHARED_CALCULATION_DEEPLINK) {
                                                        callbackInfo.callback();
                                                    }
                                                }
                                            }).fail(handleFailure);
                                    }).fail(handleFailure);
                                //});
                            // }).fail(handleFailure);


                } else {
                    if (dataOpListenerCallback !== undefined && dataOpListenerCallback !== undefined) {
                        dataOpListenerCallback(config.LOG_IN_REAUTH_STEPS.CONFIGURING_APPLICATION.StepNum, isBackgroundReAuth);
                    }
                    if (isBackgroundReAuth === true) {
                        globals.successfulBackgroundReAuth.value = true;
                    }
                    if (optionalDfd) {
                        finishLibraryInitialization();
                        optionalDfd.resolve(loginData);
                    }else {
                        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, setting globals.dataModelReady.value = true, needToUpdate = false path');
                        finishLibraryInitialization();
                        globals.dataModelReady.value = true;
                    }
                    if (loginData.result.ReturnCode === 3 && callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.LICENCE_TRANSFER) {
                        callbackInfo.callback();
                    } else if(callbackInfo && callbackInfo.context === config.AUTHENTICATION_CALLBACK_CONTEXT.SHARED_CALCULATION_DEEPLINK) {
                        callbackInfo.callback();
                    }
                }

            })
            .fail(handleFailure);
        }).fail(handleFailure);
        
        

    } else {
        if (isBackgroundReAuth === true) {
            globals.successfulBackgroundReAuth.value = false;
        }
        if (optionalDfd) {
            optionalDfd.resolve(loginData);
        }
    }
    function finishLibraryInitialization() {
        if (!initialization || !bootstrapCurrent()) return;
        libraryTransitionActive = false;
        libraryRecoveryRequired = false;
        librarySnapshot = null;
        // Keep this completed token until the next initialization so current
        // bootstrap/cache callbacks retain their original ownership predicate.
    }
    function handleFailure(error) {
        if (transferLoadingToken) globals.finishTransferLoading(transferLoadingToken);
        if (!bootstrapCurrent()) { optionalDfd?.reject({ cancelled: true }); return; }
        if (initialization) {
            libraryTransitionActive = false;
            libraryRecoveryRequired = true;
            globals.dataModelReady.value = false;
        }
        TScMessenger.writeDebugMessage('DataManager.js, doDataActionsAfterLoginOrReAuth, handleFailure');
        globals.userSettingsDfd = null;
        if (optionalDfd) {
            optionalDfd.reject(error);
        }
    }
}

function invalidateSecurityToken() {
    security.setSecurityToken('', '', '');
}

function signUp(firstName, lastName, emailAddress, password, country, authenticationMethod) {
    const fields = { firstName, lastName, emailAddress, password, applicationType: config.applicationIdentityType,
        country, token: config.externalAPIKey, authenticationMethod };
    const request = security.startSignup('website/signup?' + Object.entries(fields)
        .map(([key, value]) => encodeURIComponent(key) + '=' + encodeURIComponent(value ?? '')).join('&'));
    const owner = security.getIncarnation();
    request.progress(continuation => showPendingChallenge(continuation, owner));
    request.done(result => { if (result?.signInRequired && security.incarnationMatches(owner)) signupSignInGuidance(); });
    return request;
}

function validateAccessToken(launchTicket) {
    var dfd = $.Deferred();

    

    var request = OPERATION.ValidateAccessToken.BaseUrl;

    remoteDL.handleRequest(security, request, OPERATION.ValidateAccessToken, { launchTicket })
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendEmail(emailInfo) {
    var dfd = $.Deferred();

    

    var request = OPERATION.SendEmail.BaseUrl;

    handleDataRequest(null, request, OPERATION.SendEmail.Code, true, emailInfo)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function requestTrialExtension(company, phoneNumber) {
    
    var dfd = $.Deferred();

    var request = 'website/ExtendTrial?firstName=' + globals.user.getFirstName() +
        '&lastName=' + globals.user.getLastName() +
        '&emailAddress=' + globals.user.getEmailAddress() +
        '&company=' + company +
        '&phone=' + phoneNumber +
        '&country=' + globals.user.getCountryName() +
        '&token=' + config.externalAPIKey;

    handleDataRequest(null, request, OPERATION.RequestTrialExtension, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
 * Notify support that a user is looking for information about the Enterprise pricing from the
 * @param {string} company - Company name entered by the user
 * @param {string} phoneNumber - Contact telephone number entered by the user
 * @returns 
 */
function requestEnterprisePricing(company, phoneNumber) {
    
    var dfd = $.Deferred();

    var request = 'website/EnterprisePricingRequest?firstName=' + globals.user.getFirstName() +
        '&lastName=' + globals.user.getLastName() +
        '&emailAddress=' + globals.user.getEmailAddress() +
        '&company=' + company +
        '&phone=' + phoneNumber +
        '&country=' + globals.user.getCountryName() +
        '&token=' + config.externalAPIKey;

    handleDataRequest(null, request, OPERATION.RequestEnterprisePricing, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
    * 
    * @param {string} company
    * @param {string} phoneNumber
    * @returns {Promise}
    */
function requestSalesTool(company, phoneNumber) {

    var dfd = $.Deferred();

    var request = 'website/requestsalestool?firstName=' + globals.user.getFirstName() +
        '&lastName=' + globals.user.getLastName() +
        '&emailAddress=' + globals.user.getEmailAddress() +
        '&company=' + company +
        '&phone=' + phoneNumber +
        '&country=' + globals.user.getCountryName() +
        '&token=' + config.externalAPIKey;

    handleDataRequest(null, request, OPERATION.RequestSalesTool, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here
            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

/**
  * Sends an email to support letting them know that a user would like to upgrade the edition of the app
  * @param {string} company - Name of the company entered by the user
  * @param {string} phoneNumber - Phone number entered by the user
  * @param {string} featureRequired
  * @param {string} editionRequired - Editions required for the feature in question
  * @returns {Promise}
  */
function requestUpgrade(company, phoneNumber, featureRequired, editionRequired) {

    var dfd = $.Deferred();

    var request = 'website/requestupgrade?firstName=' + globals.user.getFirstName() +
        '&lastName=' + globals.user.getLastName() +
        '&emailAddress=' + globals.user.getEmailAddress() +
        '&company=' + company +
        '&phone=' + phoneNumber +
        '&country=' + globals.user.getCountryName() +
        '&featureRequired=' + featureRequired +
        '&editionRequired=' + editionRequired +
        '&currentEdition=' + globals.user.getEdition() +
        '&token=' + config.externalAPIKey;

    handleDataRequest(null, request, OPERATION.RequestUpgrade, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here
            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function validateNTEAUser(nteaMembershipNumber, username, password) {
    var dfd = $.Deferred();

    var data = {
        MembershipNumber: nteaMembershipNumber,
        UserName: username,
        Password: password
    };
    var request = 'integrations/validatenteauser';

    //var request = 'integrations/validatenteauser?username=' + username + '&password=' + password;

    handleDataRequest(null, request, OPERATION.ValidateNTEAUser, true, data)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function registerBuyNowClick() {

    var dfd = $.Deferred();

    var request = 'zoho/RegisterBuyNowClick?firstName=' + globals.user.getFirstName() +
        '&lastName=' + globals.user.getLastName() +
        '&emailAddress=' + globals.user.getEmailAddress() +
        '&applicationType=' + config.applicationIdentityType +
        '&token=' + config.externalAPIKey;

    handleDataRequest(null, request, OPERATION.RegisterBuyNowClick, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function buyNow(url) {
    return $.Deferred().reject({ unsupported: true, message: 'Dormant checkout is unsupported.' }).promise();
}

function getPortalURL(chargebeeCustomerId) {
    var dfd = $.Deferred();

    var request = 'chargebee/portalurl?chargebeeCustomerId=' + chargebeeCustomerId;

    handleDataRequest(null, request, OPERATION.GetPortalURL, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function getUpdatePaymentMethodURL() {
    ///chargebee/checkouturl
    var dfd = $.Deferred();

    var request = 'chargebee/updatepaymentmethodurl';

    handleDataRequest(null, request, OPERATION.GetUpdatePaymentMethodURL, true)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendLoginDebugInfo(origin, message, transactionMessage, error, user) {
    var dfd = $.Deferred();

    var messageAppend, stackTrace;

    var extractedErrorInfo = extractErrorAndStackInfo(error);
    messageAppend = extractedErrorInfo.messageAppend;
    stackTrace = extractedErrorInfo.stackTrace;
    
    var currentdate = new Date();
    var messageLoggedDate =  currentdate.getDate() + "/"
                    + (currentdate.getMonth() + 1) + "/"
                    + currentdate.getFullYear() + " @ "
                    + currentdate.getHours() + ":"
                    + currentdate.getMinutes() + ":"
                    + currentdate.getSeconds();


    //var messageLoggedDate = Date.now();
        

    var data = {
        DebugMessage: { MessageLoggedDate: messageLoggedDate, Origin: origin, Description: message + messageAppend, StackTrace: stackTrace, User: user || '', CloudServicesTransactionMessage: transactionMessage },
        Token: config.externalAPIKey
    }
    var request = 'debug/debug';

    handleDataRequest(null, request, OPERATION.PostLoginDebug, true, data)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function sendScreenSizeInfo(screenDetails, messageType) {
    var dfd = $.Deferred();

    //var messageAppend, stackTrace;

    //var extractedErrorInfo = extractErrorAndStackInfo(error);
    //messageAppend = extractedErrorInfo.messageAppend;
    //stackTrace = extractedErrorInfo.stackTrace;

    //var currentdate = new Date();
    //var messageLoggedDate = currentdate.getDate() + "/"
    //    + (currentdate.getMonth() + 1) + "/"
    //    + currentdate.getFullYear() + " @ "
    //    + currentdate.getHours() + ":"
    //    + currentdate.getMinutes() + ":"
    //    + currentdate.getSeconds();

    var screenDetailsObject = {
        DevicePixelRatio: screenDetails.dimensions.devicePixelRatio,
        OriginalScreenHeight: screenDetails.dimensions.originalScreenHeight,
        OriginalScreenWidth: screenDetails.dimensions.originalScreenWidth,
        ActualScreenHeight: screenDetails.dimensions.actualScreenHeight,
        ActualScreenWidth: screenDetails.dimensions.actualScreenWidth,
        OperatingSystem: screenDetails.operatingSystem,
        Browser: screenDetails.browser
    };

    var data = {
        ScreenDetails: screenDetailsObject,
        MessageType: messageType,
        Token: config.externalAPIKey
    }
    var request = 'debug/screensizeemail';

    handleDataRequest(null, request, OPERATION.PostScreenSizeDebug, true, data)
        .then(function (data) {
            //any custom pre-processing of data should be handled here                    

            dfd.resolve(data);
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });

    return dfd.promise();
}

function extractErrorAndStackInfo(error) {
    var extractedErrorInfo = {
        stackTrace: 'NA',
        messageAppend: ''
    }

    if (error !== undefined && error !== null) {
        try {
            if (error.message !== undefined) {
                extractedErrorInfo.messageAppend = ", Error Message: " + error.message;
            } else if (error.errorMessage !== undefined) {
                extractedErrorInfo.messageAppend = ", Error Message: " + error.errorMessage;
            } else {
                extractedErrorInfo.messageAppend = ", Error Message: " + error;
            }
            if (error.failedOp !== undefined) {
                extractedErrorInfo.messageAppend = extractedErrorInfo.messageAppend + ', Failed Operation: ' + error.failedOp;
            }
            if (error.opTimeout !== undefined) {
                extractedErrorInfo.messageAppend = extractedErrorInfo.messageAppend + ', Operation Timeout: ' + error.opTimeout;
            }
            extractedErrorInfo.stackTrace = error.stack || "NOTHING";


        } catch (exception) {
            extractedErrorInfo.messageAppend = exception.message || "no error message";
            extractedErrorInfo.stackTrace = exception.stack || "NOTHING";
        }
    }
    return extractedErrorInfo;
}

function dismissAppOptionItem(appOptionItemCode) {
    var dfd = $.Deferred();
    

    var timeStamp = Date.now();
    
    
    if (globals.isOnline.value === true) {
        saveOptionItemStatusOnline(appOptionItemCode);
    } else {
        doOfflineSave(appOptionItemCode);
    }

    function saveOptionItemStatusOnline(appOptionItemCode) {
        var request = OPERATION.PostAppOptionItem.BaseUrl + '?appOptionItemCode=' + appOptionItemCode + '&statusCode=' + config.APP_OPTION_ITEM_STATUS.DISMISSED;

        handleDataRequest(null, request, OPERATION.PostAppOptionItem.Code, true)
            .then(function (data) {
                //any custom pre-processing of data should be handled here                    
                dfd.resolve(data);
            })
            .fail(function (errorMessage) {
                doOfflineSave(appOptionItemCode);
                dfd.reject(errorMessage);
            });
        
    }

    function doOfflineSave(appOptionItemCode) {
        
        // app options are stored in the user in local storage and also have no interaction with other objects so nothing to do here except 
        // queue the request for syncing later and then resolve as if request had been successful

        var params = { appOptionItemCode: appOptionItemCode, statusCode: config.APP_OPTION_ITEM_STATUS.DISMISSED };
        queueSyncJob(OPERATION.PostAppOptionItem, timeStamp, params);
        
        dfd.resolve({ ReturnCode: 1, Message: "" });
    }

    return dfd.promise();
}

function registerDataOperationListener(listenerCallback, operation) {
    dataOpListenerCallback = listenerCallback;
}
//#region do not delete, this code is related to providing more information/feedback during login
//function registerDataOperationListener(listenerCallback, operation) {
//    dataOpListenerCallback = listenerCallback;
//    var isStarted = false;
//    numRecordsToCache = 0;
//    numRecordsCached = 0;
//    localDL.registerCacheListener(recordCachedCallback);
//    //var OpTracker = function () {
//    //    var numRecordsToCache = 0;
//    //    var numRecordsCached = 0;
//    //    //var ops = [
//    //    //    {
//    //    //        OpName: OPERATION.GetSelectionList,
//    //    //        Retrieval: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        },
//    //    //        Caching: {
//    //    //            Total: -1,
//    //    //            Done: 0
//    //    //        },
//    //    //        Images: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        }   
//    //    //    }, 
//    //    //    {
//    //    //        OpName: OPERATION.GetOnRoadCosts,
//    //    //        Retrieval: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        },
//    //    //        Caching: {
//    //    //            Total: -1,
//    //    //            Done: 0
//    //    //        },
//    //    //        Images: null
//    //    //    },
//    //    //    {
//    //    //        OpName: OPERATION.GetCommonPackDetails,
//    //    //        Retrieval: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        },
//    //    //        Caching: {
//    //    //            Total: -1,
//    //    //            Done: 0
//    //    //        },
//    //    //        Images: null
//    //    //    },
//    //    //    {
//    //    //        OpName: OPERATION.GetOverviewContent,
//    //    //        Retrieval: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        },
//    //    //        Caching: {
//    //    //            Total: -1,
//    //    //            Done: 0
//    //    //        },
//    //    //        Images: null
//    //    //    },
//    //    //    {
//    //    //        OpName: OPERATION.GetAppConfig,
//    //    //        Retrieval: {
//    //    //            Started: 'no',
//    //    //            Finished: 'no'
//    //    //        },
//    //    //        Caching: {
//    //    //            Total: -1,
//    //    //            Done: 0
//    //    //        },
//    //    //        Images: null
//    //    //    }
//    //    //];
//    //    function recordCachedCallback(op) {
//    //        //ops.forEach(function (opMonitor) {
//    //        //    if (opMonitor.OpName === op) {
//    //                //if (opMonitor.Caching.Total !== -1 && opMonitor.Caching.Total !== 0) {
//    //                //    opMonitor.Caching.Done = opMonitor.Caching.Done + 1;
//    //                //    dataOpListenerCallback('UPDATE', calculatePercentage());
//    //                //}
//    //        //        return;
//    //        //    }
//    //        //});
//    //        if (op === OPERATION.GetSelectionList) {
//    //            if (numRecordsCached === 0) {
//    //                dataOpListenerCallback('STARTED', 0);
//    //            }
//    //            numRecordsCached++;
//    //            doStatusCheck();
//    //        }
            
//    //    }
//    //    localDL.registerCacheListener(recordCachedCallback);
//    //    function calculatePercentage() {
                
//    //        if (numRecordsCached === numRecordsToCache) {
//    //            return 100;
//    //        }
//    //        return Math.floor((numRecordsCached / numRecordsToCache) * 100);
//    //    }
//    //    //function calculatePercentage() {
//    //    //    var percentage = 0;
//    //    //    ops.forEach(function (opMonitor) {
//    //    //        if (opMonitor.Retrieval.Started === 'yes') {
//    //    //            percentage += 5;
//    //    //        }
//    //    //        if (opMonitor.Retrieval.Finished === 'yes') {
//    //    //            percentage += 10;
//    //    //        }
//    //    //        if (opMonitor.Images != null && opMonitor.Images.Started === 'yes') {
//    //    //            percentage += 5;
//    //    //        }
//    //    //        if (opMonitor.Images != null && opMonitor.Images.Finished === 'yes') {
//    //    //            percentage += 15;
//    //    //        }
//    //    //        if (opMonitor.Caching.Total !== -1 && opMonitor.Caching.Total !== 0) {
//    //    //            if (opMonitor.Caching.Done % 100 === 0) {
//    //    //                percentage += opMonitor.Caching.Done / 100;
//    //    //            }
//    //    //            //percentage += Math.ceil(((opMonitor.Caching.Done / opMonitor.Caching.Total) * 100) * 8);
//    //    //        }
//    //    //    });
//    //    //    return percentage;
//    //    //}
        
//    //    //this.notifyRetrievalStarted = function(op) {
//    //    //    ops.forEach(function (opMonitor) { 
//    //    //        if (opMonitor.OpName === op) {
//    //    //            opMonitor.Retrieval.Started = 'yes';
//    //    //            if (isStarted === false) {
//    //    //                isStarted = true;
//    //    //                dataOpListenerCallback('STARTED', 10);
//    //    //            }
//    //    //            doStatusCheck();
//    //    //            return;
//    //    //        }
//    //    //    });
//    //    //}

//    //    //this.notifyRetrievalFinished = function (op) {
//    //    //    ops.forEach(function (opMonitor) {
//    //    //        if (opMonitor.OpName === op) {
//    //    //            opMonitor.Retrieval.Finished = 'yes';
//    //    //            doStatusCheck();
//    //    //            return;
//    //    //        }
//    //    //    });
//    //    //}
        
//    //    //this.registerImageRetrievalStarted = function(op) {
//    //    //    ops.forEach(function (opMonitor) {
//    //    //        if (opMonitor.OpName === op) {
//    //    //            opMonitor.Images.Started = 'yes';
//    //    //            doStatusCheck();
//    //    //            return;
//    //    //        }
//    //    //    });
//    //    //}

//    //    //this.registerAllImagesRetrieved = function(op) {
//    //    //    ops.forEach(function (opMonitor) {
//    //    //        if (opMonitor.OpName === op) {
//    //    //            opMonitor.Images.Finished = 'yes';
//    //    //            doStatusCheck();
//    //    //            return;
//    //    //        }
//    //    //    });
//    //    //}

//    //    this.registerNumRecordsToCache = function (numRecsTocache, op) {
//    //        //ops.forEach(function (opMonitor) {
//    //        //    if (opMonitor.OpName === op) {
//    //        //        opMonitor.Caching.Total = numRecordsTocache;
//    //        //        return;
//    //        //    }
//    //        //});
//    //        numRecordsToCache = numRecsTocache;
//    //    }
        
//    //    //function doStatusCheck() {
//    //    //    var curPercent = calculatePercentage();
//    //    //    if (curPercent < 100) {
//    //    //        dataOpListenerCallback('UPDATE', curPercent);
//    //    //    } else {
//    //    //        dataOpListenerCallback('FINISHED', curPercent);
//    //    //    }
//    //    //}
//    //    function doStatusCheck() {
//    //        var curPercent = calculatePercentage();
//    //        if (curPercent < 100) {
//    //            dataOpListenerCallback('UPDATE', curPercent);
//    //        } else {
//    //            dataOpListenerCallback('FINISHED', curPercent);
//    //        }
//    //    }
//    //    //return OpTracker;
//    //};
//    //curOpTracker = new OpTracker();
//}

//function recordCachedCallback(op) {
//    //ops.forEach(function (opMonitor) {
//    //    if (opMonitor.OpName === op) {
//    //if (opMonitor.Caching.Total !== -1 && opMonitor.Caching.Total !== 0) {
//    //    opMonitor.Caching.Done = opMonitor.Caching.Done + 1;
//    //    dataOpListenerCallback('UPDATE', calculatePercentage());
//    //}
//    //        return;
//    //    }
//    //});
//    if (op === OPERATION.GetSelectionList) {
//        if (numRecordsCached === 0) {
//            dataOpListenerCallback('STARTED', 0);
//        }
//        numRecordsCached++;
//        doStatusCheck();
//    }

//}

//function calculatePercentage() {

//    if (numRecordsCached === numRecordsToCache) {
//        return 100;
//    }
//    return Math.floor((numRecordsCached / numRecordsToCache) * 100);
//}

//function doStatusCheck() {
//    var curPercent = calculatePercentage();
//    if (curPercent < 100) {
//        dataOpListenerCallback('UPDATE', curPercent);
//    } else {
//        dataOpListenerCallback('FINISHED', curPercent);
//    }
//}
//#endregion do not delete, this code is related to providing more information/feedback during login

function setLocalDLIdentity() {
    if(globals.user.getAuthenticationMethod() === config.AUTHENTICATION_METHOD.TRUCKSCIENCE_API) {
        localDL.setIdentity(globals.user.getCdg(), globals.user.getUserName());
    }else {
        localDL.setIdentity(globals.user.getCdg(), globals.user.getEmailAddress());
    }
    
}

function runDataIntegrityTests() {
    var dfd = $.Deferred();

    var availableLegislations = globals.user.getAvailableLegislations();
    if (availableLegislations === undefined || availableLegislations === null || availableLegislations.length === 0) {
        dfd.reject(config.DATA_INTEGRITY_CHECK_FAILURES.LEGISLATIONS_NOT_AVAILABLE);
    } else {
        dfd.resolve();
    }

    return dfd.promise();
}

function replaceReportImageUrls(passedInRepGraphics, isCurrentContext) {
    var dfd = $.Deferred();
    if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true }).promise();
    var reportReplacePromises = [];
    var reportImagesToRetrieve = [];
    var graphicsToUse = passedInRepGraphics || latestReportGraphicsFromLogin;

    reportImagesToRetrieve.push(graphicsToUse.reportHeaderURL);
    reportImagesToRetrieve.push(graphicsToUse.reportFooterURL);
    reportImagesToRetrieve.push(graphicsToUse.landscapeReportHeaderLeftURL);

    loadAllImages(reportImagesToRetrieve, localDL.PACK.Graphic, function (allImagesDownLoaded) {
        if (isCurrentContext && !isCurrentContext()) return dfd.reject({ cancelled: true });
        if (allImagesDownLoaded && allImagesDownLoaded === true) {
            reportReplacePromises.push(replaceImageUrl(graphicsToUse, "reportHeaderURL", localDL.PACK.Graphic, isCurrentContext));
            reportReplacePromises.push(replaceImageUrl(graphicsToUse, "reportFooterURL", localDL.PACK.Graphic, isCurrentContext));
            reportReplacePromises.push(replaceImageUrl(graphicsToUse, "landscapeReportHeaderLeftURL", localDL.PACK.Graphic, isCurrentContext));
            $.when.apply($, reportReplacePromises)
                .done(function () {
                    dfd.resolve();
                }).fail(error => dfd.reject(error));
        }
    });
    
    //reportReplacePromises.push(replaceImageUrl(graphicsToUse, "reportHeaderURL", localDL.PACK.Graphic));
    //reportReplacePromises.push(replaceImageUrl(graphicsToUse, "reportFooterURL", localDL.PACK.Graphic));
    
    //$.when.apply($, reportReplacePromises)
    //    .done(function () {
    //        dfd.resolve();
    //    });

    return dfd.promise();
}

function sortSelectionList(data) {
    //var sortedOnGVW = data.Offers.sort(function (a, b) {
    //    return a.GVW - b.GVW;
    //});
    //var sortedOnGVWThenPayloed = sortedOnGVW.sort(function (a, b) {
    //    if (a.GVW === b.GVW) {
    //        return a.Payload - b.Payload
    //    } else {
    //        return 0;
    //    }
    //});
    //return sortedOnGVWThenPayloed;
    doSort(data.Offers);

    function doSort(array) {

        var swapped = false;

        for (var i = 0; i < array.length; i++) {
            if (i <= array.length - 2) {
                var a = array[i];
                var b = array[i + 1];
                if ((a.GVW - b.GVW) > 0) {
                    array[i] = b;
                    array[i + 1] = a;
                    swapped = true;
                } else if (a.GVW === b.GVW) {
                    if ((a.Payload - b.Payload) > 0) {
                        array[i] = b;
                        array[i + 1] = a;
                        swapped = true;
                    }

                }
            }
        }
        if (swapped === true) {
            doSort(array);
        }
    }
    
}
//#endregion

function downloadVehicleCsv(vehicleId, measurementSystemId) {
    var dfd = $.Deferred();
        
    var request = 'downloads/get?operationType=CSV-VEHICLES&objectIdentifier=' + vehicleId + '&measurementSystemId=' + measurementSystemId;
    handleDataRequest(null, request, OPERATION.DownloadVehicleCsv, true)
        .then(function (result) {
            dfd.resolve({
                data: result,
                ResultCode: 1
            });
        })
        .fail(function (errorMessage) {
            dfd.reject(errorMessage);
        });
    

    return dfd.promise();
}

function getSignalRConnectionInfo() {

    var dfd = $.Deferred();

    var options = {
        // url: 'https://truckscience-test-v2.azurewebsites.net/api/negotiate', //url: process.env.VUE_APP_AzureFunctionsServer + '/api/negotiate',
        url: process.env.VUE_APP_AzureFunctionsServer.replace(/\/+$/, '') + '/api/negotiate',
        beforeSend: setQueryHeader,
        type: operationTypeGet
    }

    // make ajax call
    security.protectedAjax(options)
        .then(requestSucceeded)
        .fail(requestFailed);

    // handle the ajax callback
    function requestSucceeded(data, textStatus, jqXHR) {

        
        dfd.resolve({ data: data });
    }

    function requestFailed(xhr, ajaxOptions, thrownError) {
        dfd.reject(xhr);
    }

    return dfd.promise();

    //return axios.get(config.azureFunctionsServer + '/api/negotiate', { headers: { 'requested-user': globals.user.getUserName() } })
    //    .then(function (response) {
    //        return response.data;
    //    }).catch(console.error);

    function setQueryHeader(xhr) {
        xhr.setRequestHeader('requested-user', globals.user.getUserName());
        xhr.setRequestHeader('SecurityToken', security.getSecurityToken());
    }
}
